import React, { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Wrap, WrapItem, Flex, VStack, HStack,
  Table, Thead, Tr, Th, Tbody, Td,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormLabel, FormErrorMessage,
  Button, Input, InputGroup, InputRightElement, Select, Textarea, Text, IconButton, ButtonGroup,
  List, ListItem,
  useDisclosure,
  FormControl,
  TableContainer,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Checkbox,
  CheckboxGroup,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Tag,
  TagLabel,
  TagCloseButton,
  Tooltip,
} from "@chakra-ui/react";
import { FaPlusCircle, FaTimes } from "react-icons/fa";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FiEdit3 } from "react-icons/fi";

import CurrencyList from "./CurrencyList";
import HelpIcon from "./HelpIcon";
import dayjs from "dayjs"; // 日時操作ライブラリ

import { useToast } from "@chakra-ui/react";
import { useAppSelector } from '../../store/hooks';

import { WebAPIContext } from '../../contexts/WebAPIContext';

import SelectorButton from "./SelectorButton"
import { countLinkWidth } from "./SelectorLink"
import AssignTabModal, { getReviewDebugConfirmMap, getValidFlowDivisionPairs } from "../Approve/AssignTabModal";
import AssignByCategoryModal, { CategoryAssignee, CategoryConfig, CategoryEmployee } from "../Approve/AssignByCategoryModal";
import ViewRestrictionModal from "./ViewRestrictionModal";
import TopicsModal from "../Opportunity/TopicsModal";
import {
  Opportunity, ProjectWithRdsData, PartNo, Errors, Employee,
  ProjectContextType, PagePermissions, PastProjectData,
  Currency, LatestCurrency, CurrencyProspect, OrganizationWithHierarchyType,
  CompanyPlantType, CompanyPlantSnapshotType, CompanyPlantSnapshotListType, InterestRateMaster,
  ProductCategoryType,
} from "../../contexts/types";

import {
  PurposeTypeCodeToNameMap, getPurposeTypeNameByCode, PurposeType,
  ApplicationFormType,
  ProfitLossDisplayType, isSimpleApplicationForm,
  ApproveStatus,
  getCustomerIndustryTypeNameByCode,
  EquipmentCategory, getEquipmentCategoryNameByCode, getEquipmentCategoryNamesByCodes,
  EquipmentUsageType, getEquipmentUsageTypeNameByCode,
  Messages,
  WorkflowId,
  DivisionConfirmKb, getDivisionConfirmName,
  SubWindowInvestmentScreeningDetailName,
  ValidDateRangeStart,
  ValidDateRangeEnd,
  PlannedBudgetType, getPlannedBudgetTypeNameByCode,
} from "../../contexts/constants";

import { compareString, hasFilterDateRange, hasFilterString, isValidDateRange, isValidDateFormat, parseCompanyPlantSnapshot, formatDateByLocale, joinEscapedList, splitEscapedList, resolveDisplayPartNo } from "../../utils/utils";
import FilterTextBox from "../../components/FilterTextBox";
import FilterDateRange from "../../components/FilterDateRange";
import FilterDropdown from "../../components/FilterDropdown";
import { useManualAPI } from "../../components/api/ManualApi";
import { CustomNumberInput } from "./CustomNumberInput";

import { getConfirmor, updateConfirm } from "../../utils/workflow";
import i18n from '../../utils/i18n';
import '../common.css';

// コンテキスト
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";

/** 言語設定 */
import { supportedLngs } from '../../utils/i18n';
import { getMultilingualDisplayName, getBilingualDisplayName, getDivisionHierarchyDisplay, getHeadquartersNameFromHierarchy, getDivisionNameFromHierarchy } from '../../utils/i18nDisplay';
// キーの抽出
export type LanguageCode = keyof typeof supportedLngs;
// 型の作成
export type BilingualName = {
  [key in LanguageCode]?: string;
}
/** 使用言語のオブジェクト */
const langs = Object.keys(supportedLngs) as LanguageCode[];
/** 使用言語の配列 */
const langFields = langs.join(' ');

/** クロスチェック担当者のカテゴリ設定（翻訳ファイルの非同期読み込みに対応するため関数化） */
const getCrossCheckCategories = (): Record<string, CategoryConfig> => ({
  manufacturing: { id: "manufacturing", label: i18n.t('features.InvestmentScreening.BasicInfo.category.manufacturing'), maxAssignees: 0, descendantOrganizationClassIds: ["MF00001"] },
  engineering: { id: "engineering", label: i18n.t('features.InvestmentScreening.BasicInfo.category.engineering'), maxAssignees: 0, descendantOrganizationClassIds: ["EN00001"] },
  it: { id: "it", label: i18n.t('features.InvestmentScreening.BasicInfo.category.it'), maxAssignees: 0, descendantOrganizationClassIds: ["IT00001"] },
});

/** 資材担当役員のカテゴリ設定 */
const getMaterialOfficerCategories = (): CategoryConfig[] => [
  { id: "materialOfficer", label: i18n.t('features.InvestmentScreening.BasicInfo.category.materialOfficer'), maxAssignees: 0, descendantOrganizationClassIds: ["PL00001"] },
];

/** 承認者のカテゴリ設定 */
const getApproverCategories = (): CategoryConfig[] => [
  { id: "productDirector", label: i18n.t('features.InvestmentScreening.BasicInfo.category.productDirector'), maxAssignees: 1, descendantOrganizationClassIds: ["PD00001"] },
  { id: "divisionManager", label: i18n.t('features.InvestmentScreening.BasicInfo.category.divisionManager'), maxAssignees: 1, descendantOrganizationClassIds: ["DM00001"] },
  { id: "salesHeadquarters", label: i18n.t('features.InvestmentScreening.BasicInfo.category.salesHeadquarters'), maxAssignees: 1, descendantOrganizationClassIds: ["SH00001"] },
  { id: "businessHeadquarters", label: i18n.t('features.InvestmentScreening.BasicInfo.category.businessHeadquarters'), maxAssignees: 1, descendantOrganizationClassIds: ["BH00001"] },
];

/**
 * Google Sheets の URL からスプレッドシートIDを抽出する
 */
const extractSpreadsheetId = (url: string): string | null => {
  const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
};

/**
 * 基本情報パラメータ
 */
export interface BasicInfoHandle {
  getSnapshot: () => Record<string, unknown>;
  applySnapshot: (data: Record<string, unknown>) => void;
}

interface BasicInfoProps {
  /** プロジェクトID */
  projectId: string;
  /** プロジェクトID更新用関数 */
  onProjectIdChange: (newProjectId: string) => void;
  /** 権限情報更新処理 */
  refreshUserPermissions: () => void;
  /** 振り返り準備フラグ（true：準備完了、false：準備中） */
  isReadyReview: boolean;
}

/**
 * 顧客情報
 */
interface CustomerType {
  /** 顧客ID */
  customerId: string,
  /** 顧客名 */
  customerName: string,
  /** 顧客正式社名 */
  customerFullName: string | null,
};

/**
 * MMI製品分類
 */
interface MMIProductCategoryType {
  /** MMI製品分類ID */
  MMProductCategoryId: string,
  /** MMI製品分類名 */
  MMProductCategoryName: string
};
/**
 * サーバ送信用確認者情報
 */
interface FlowPersonData {
  employeeId: string;
  flowId: string;
  divisionId: string;
}
/**
 * 表示用確認者情報
 */
interface Confirmor {
  employeeId: string;
  employeeName?: string;
  divisionId: string;
  confirmKbn: string;
}

/**
 * メール送信状態
 */
interface MailSentStatus {
  /** 確認区分ID */
  divisionId: string;
  /** 送信日時（nullなら未送信） */
  sentAt: string | null;
  /** 送信者の従業員ID */
  sentByEmployeeId?: string | null;
  /** 送信者名 */
  sentByEmployeeName?: string | null;
}

/**
 * 過去Projectのフィルター情報
 */
interface FilterPastProjectItemsType {
  projectName?: string,
  customerName?: string,
  customerProductName?: string,
  purpose?: string,
  projectStartDateStart?: Date | null,
  projectStartDateEnd?: Date | null,
  approveDayStart?: Date | null,
  approveDayEnd?: Date | null,
  partNo?: string,
}

/**
 * 新規投資申請画面(基本情報)
 * @returns
 */
const BasicInfo = forwardRef<BasicInfoHandle, BasicInfoProps>(({
  projectId,
  onProjectIdChange,
  refreshUserPermissions,
  isReadyReview,
}, ref) => {

  const { t } = useTranslation();
  /** トースト表示用 */
  const toast = useToast();

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);

  /** 画面タイトル */
  const TITLE = t('features.InvestmentScreening.BasicInfo.title');

  /** 会社/工場の最大選択数 */
  const MAX_COMPANY_PLANT_COUNT = 5;

  /** 必須エラーメッセージ生成 */
  const requiredErrorMessage = (fieldName: string) => t('common.message.requiredField', { fieldName });

  /** 年月日 - フォーマットエラーメッセージ生成 */
  const dateFormatErrorMessage = (fieldName: string) => t('features.InvestmentScreening.BasicInfo.validation.isValidDateFormatMessage', { fieldName });
  /** 年月 - フォーマットエラーメッセージ生成 */
  const monthFormatErrorMessage = (fieldName: string) => t('features.InvestmentScreening.BasicInfo.validation.isValidMonthFormatMessage', { fieldName });

  /** 年月日 - 有効範囲エラーメッセージ生成 */
  const dateRangeErrorMessage = (fieldName: string) => t('features.InvestmentScreening.BasicInfo.validation.inputDateRangeMessage', { fieldName, startDate: formatDateByLocale(ValidDateRangeStart), endDate: formatDateByLocale(ValidDateRangeEnd) });
  /** 年月 - 有効範囲エラーメッセージ生成 */
  const monthRangeErrorMessage = (fieldName: string) => t('features.InvestmentScreening.BasicInfo.validation.inputMonthRangeMessage', { fieldName, startMonth: dayjs(ValidDateRangeStart).format(t('common.dateFormat.dayjsYearMonth')), endMonth: dayjs(ValidDateRangeEnd).format(t('common.dateFormat.dayjsYearMonth')) });

  /** 開始日以降エラーメッセージ生成 */
  const afterStartDateErrorMessage = (fieldName: string) => t('features.InvestmentScreening.BasicInfo.validation.eolAfterStartDate', { fieldName });

  /** 現在の言語が日本語かどうか（件数リンクのオーバーレイ表示可否の判定に使用） */
  const isJapaneseLocale = !i18n.language || i18n.language.startsWith("ja");

  /** 入力項目名幅 */
  const labelWidth = 160;

  /** 入力項目値 */
  const valueWidth = 475;

  /**
   * 左右2列の各列幅（label + value + ボタン(ml=16px + minWidth100px)）
   * 列自体の幅を明示的に固定することで、言語（文言長）に関わらず
   * 折り返し（2列⇔1列）の切替タイミングを日本語・英語で完全に一致させる。
   */
  const wrapColumnWidth = labelWidth + valueWidth + 16 + 100;

  /** ラップ幅（左右2列 + Wrapのspacing分。言語で変わらない固定値） */
  const wrapWidth = wrapColumnWidth * 2 + 16;

  /** 確認項目名幅 */
  const labelComfirmWidth = 175;

  /**
   * 非表示：過去Project選択 ⇒ 本対応時にこのフラグを削除してください。
   *
   * ■ 過去Project選択の用途（目的別）
   *   - 増産投資: 既存製品の生産能力を増強する際に、元の投資案件を紐づける
   *   - 更新投資: 既存設備を更新する際に、元の投資案件を紐づける
   *   - 合理化投資: 既存工程を合理化する際に、元の投資案件を紐づける
   *   - 新製品製造: 新製品を試しに投資申請して軌道にのってから、再度追加で投資申請を出すパターン
   *               （同じ製品・パーツNoで、当初の試作投資 → 追加の量産投資）
   */
  const hiddenPastProject = false;

  /** 入力項目ラベルスタイル */
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    display: "block",
    marginBottom: 0
  }

  /** 件数リンクスタイル */
  const countLinkStyle = {
    textAlign: "right",
    paddingRight: "8px",
    display: "block",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: 'hidden',
    width: `${countLinkWidth}px`,
    textDecoration: "underline",
    color: "blue.500"
  }

  /** 最終確認ラベルスタイル */
  const finalConfirmLabelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    width: `${labelComfirmWidth}px`,
  };

  /** 最終確認値スタイル */
  const finalConfirmValueStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }

  /** 過去Projectセルスタイル */
  const pastProjectCellStyle = {
    padding: "10px"
  }

  /** ログインユーザー：従業員ID */
  const loginUserEmployeeId = useAppSelector((state) => state.employee.employeeId);
  /** ログインユーザー：従業員名 */
  const loginUserEmployeeName = useAppSelector((state) => state.employee.employeeName);
  /** ログインユーザー：事業部ID */
  const loginUserDivisionId = useAppSelector((state) => state.employee.divisionId);
  /** ログインユーザー：事業部名 */
  const loginUserDivisionName = useAppSelector((state) => state.employee.divisionName);
  /** ログインユーザー：全所属組織情報 */
  const loginUserAffiliations = useAppSelector((state) => state.employee.affiliations);
  /** ログインユーザー：本社経営管理 */
  const loginUserIsHeadquartersManagement = useAppSelector((state) => state.employee.isHeadquartersManagement);

  /** プロジェクト情報（デフォルト値） */
  const defaultProjectBasicInfo = {
    projectId: "",
    projectName: "",
    revisedCustomerProductNameByRevision: "",
    revisedOpportunityNameByRevision: "",
    mmProductNameFreeWrite: "",
    specificationAndPerformance: "",
    purpose: PurposeType.NewProduct.code,
    purposeOfInvestment: "",
    applicationFormType: ApplicationFormType.Detail.code,
    profitLossDisplayType: ProfitLossDisplayType.Show.code,
    carModel: "",
    customerIndustry: "",
    equipmentCategory: "",
    plannedBudget: PlannedBudgetType.WithinBudget.code,
    equipmentUsageType: EquipmentUsageType.Dedicated.code,
    partNo: [""],
    approvalStatus: ApproveStatus.Pending.code,
    updateDate: "",
    applicationDay: "",

    // 以降、RDSと名称データ
    customerId: "",
    customerName: "",
    customerFullName: "",
    opportunityId: "",
    opportunityName: "",
    customerProductId: "",
    customerProductName: "",
    mmProductCategoryId: "",
    mmProductCategoryName: "",
    endUserId: "",
    endUserName: "",
    registrantEmployeeId: "",
    registrantEmployeeName: "",
    divisionId: "",
    divisionName: "",
    confirmEmployees: [],
    crossCheckRequired: false,
    crossCheckDepartments: "",
    crossCheckAssignees: "",
    materialOfficerRequired: false,
    updateDateRDS: ""
  };

  /** エラー情報（デフォルト値） */
  const defaultErrors = {
    projectName: {
      isError: false,
      errorMessage: ""
    },
    customerId: {
      isError: false,
      errorMessage: ""
    },
    customerFullName: {
      isError: false,
      errorMessage: ""
    },
    customerProductId: {
      isError: false,
      errorMessage: ""
    },
    opportunityId: {
      isError: false,
      errorMessage: ""
    },
    endUserId: {
      isError: false,
      errorMessage: ""
    },
    carModel: {
      isError: false,
      errorMessage: ""
    },
    customerIndustry: {
      isError: false,
      errorMessage: ""
    },
    mmProductCategoryId: {
      isError: false,
      errorMessage: ""
    },
    mmProductNameFreeWrite: {
      isError: false,
      errorMessage: ""
    },
    partNo: {
      isError: false,
      errorMessage: ""
    },
    companyPlantId: {
      isError: false,
      errorMessage: ""
    },
    divisionId: {
      isError: false,
      errorMessage: ""
    },
    registrantDivisionId: {
      isError: false,
      errorMessage: ""
    },
    applicationMonth: {
      isError: false,
      errorMessage: ""
    },
    projectStartDate: {
      isError: false,
      errorMessage: ""
    },
    projectEndDate: {
      isError: false,
      errorMessage: ""
    },
    eolDate: {
      isError: false,
      errorMessage: ""
    },
    equipmentCategory: {
      isError: false,
      errorMessage: ""
    },
    equipmentUsageType: {
      isError: false,
      errorMessage: ""
    },
    increasedProductionStartDate: {
      isError: false,
      errorMessage: ""
    },
    updateAndOtherStartDate: {
      isError: false,
      errorMessage: ""
    },
    rationalizationStartDate: {
      isError: false,
      errorMessage: ""
    },
    newProductStartDate: {
      isError: false,
      errorMessage: ""
    },
    specificationAndPerformance: {
      isError: false,
      errorMessage: ""
    },
    purpose: {
      isError: false,
      errorMessage: ""
    },
    purposeOfInvestment: {
      isError: false,
      errorMessage: ""
    },
    pastProjectId: {
      isError: false,
      errorMessage: ""
    },
    applicationNumber: {
      isError: false,
      errorMessage: ""
    },
    equipmentPlanNumber: {
      isError: false,
      errorMessage: ""
    },
    plannedBudget: {
      isError: false,
      errorMessage: ""
    },
    budgetAmount: {
      isError: false,
      errorMessage: ""
    },
  };

  /**
   * 空の過去Project用フィルターを返す。
   * @returns 空の過去Project用フィルター
   */
  const emptyFilterPastProjectItems = () => {
    return {
      projectName: "",
      customerName: "",
      customerProductName: "",
      purpose: "",
      projectStartDateStart: null,
      projectStartDateEnd: null,
      approveDayStart: null,
      approveDayEnd: null,
      partNo: "",
    };
  }

  /** 確認者一覧表示順 */
  const CONFIRMOR_SORT_ORDER = ["company", "companyHead", "sales", "procurement"];

  /** プロジェクト基本情報 */
  const [projectBasicInfo, setProjectBasicInfo] = useState<ProjectWithRdsData>(JSON.parse(JSON.stringify(defaultProjectBasicInfo)));

  /** 編集前-目的 */
  const [beforePurpose, setBeforePurpose] = useState<string | null>(null);
  // DB保存済みの目的・過去PJ紐づけ値（最終確認画面の設備入れ替わり警告の比較用。未保存・新規時は null）
  const [savedEquipmentSource, setSavedEquipmentSource] = useState<{
    purpose: string | null;
    pastProjectId: string | null;
    pastReviewDate: string | null;
  } | null>(null);

  /** 過去プロジェクトフィルターテキスト */
  const [filterPastProjectItems, setFilterPastProjectItems] = useState<FilterPastProjectItemsType>(emptyFilterPastProjectItems());

  /** 過去プロジェクト情報 */
  const [pastProjectData, setPastProjectData] = useState<PastProjectData[] | null>(null);

  /** 過去プロジェクト情報(フィルター) */
  const filterPastProjectData = (pastProjectData || []).filter(p => {
    const partNoMatch = hasFilterString(p.partNo, filterPastProjectItems.partNo) ||
      (p.partNoDetails || []).some(d => hasFilterString(d.displayPartNo, filterPastProjectItems.partNo));
    return (
      hasFilterString(p.projectName, filterPastProjectItems.projectName) &&
      hasFilterString(p.customerName, filterPastProjectItems.customerName) &&
      hasFilterString(p.customerProductName, filterPastProjectItems.customerProductName) &&
      hasFilterString(p.purpose, filterPastProjectItems.purpose) &&
      hasFilterDateRange(p.projectStartDate, filterPastProjectItems.projectStartDateStart, filterPastProjectItems.projectStartDateEnd) &&
      hasFilterDateRange(p.approveDay, filterPastProjectItems.approveDayStart, filterPastProjectItems.approveDayEnd) &&
      partNoMatch
    )
  });

  /** 過去プロジェクト一覧選択（一時選択） */
  const [selectedPastProject, setSelectedPastProject] = useState<PastProjectData | null>(null);

  /** 過去プロジェクトから追加するPartNo（チェックボックス選択） */
  const [selectedPastPartNos, setSelectedPastPartNos] = useState<string[]>([]);

  /** 過去プロジェクトから追加済みのPartNo（編集不可） */
  const [lockedPartNos, setLockedPartNos] = useState<string[]>([]);

  /** 「変更予定」トグルがONになっている行のpartNo一覧（No.435対応） */
  const [displayPartNoToggleKeys, setDisplayPartNoToggleKeys] = useState<string[]>([]);

  /** DB登録済みのpartNo一覧（トグル活性判定用スナップショット、No.435対応） */
  const [registeredPartNos, setRegisteredPartNos] = useState<string[]>([]);

  /** DB登録済みのpartNo一覧（最終確認モーダルでの変更・削除検知用スナップショット。行削除では更新しない） */
  const [savedPartNoSnapshot, setSavedPartNoSnapshot] = useState<string[]>([]);

  /** 過去プロジェクト検索用PartNo（前方一致） */
  const [searchPartNoPrefix, setSearchPartNoPrefix] = useState<string>("");

  /** パーツ番号 */
  const [projectPartNo, setProjectPartNo] = useState<PartNo[]>([]);

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>(JSON.parse(JSON.stringify(defaultErrors)));

  /** 保存禁止(例外発生時など) */
  const [hasfetchException, setHasfetchException] = useState(false);

  /** 従業員データ */
  const [employeeData, setEmployeeData] = useState<Employee | null>();

  /** 選択 顧客情報（単一） */
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerType | null>(null);
  /** 選択 案件情報（単一） */
  const [selectedOpportunity, setSelectedOpportunity] = useState<any | null>(null);
  /** 選択 顧客製品情報（単一） */
  const [selectedCustomerProduct, setSelectedCustomerProduct] = useState<any | null>(null);
  /** 顧客製品IDがユーザー操作で変更されたかどうか */
  const isCustomerProductIdChangedByUser = useRef(false);
  // End User名: 複数入力対応（カンマ区切り保存）
  const [endUserList, setEndUserList] = useState<string[]>([""]);
  // 設備計画番号: 複数入力対応（配列保存）
  const [equipmentPlanNumberList, setEquipmentPlanNumberList] = useState<string[]>([""]);
  /** 選択 MMI製品分類情報（単一） */
  const [selectedMMIProductCategory, setSelectedMMIProductCategory] = useState<MMIProductCategoryType | null>(null);

  /** モーダル一時選択 事業部パス（全階層：事業本部→部門→事業部→ビジネス小計→ビジネス→製品） */
  const [tempSelectedDivisionPath, setTempSelectedDivisionPath] = useState<OrganizationWithHierarchyType[]>([]);

  /** 選択 事業部情報（単一） */
  const [selectedDivision, setSelectedDivision] = useState<OrganizationWithHierarchyType | null>(null);

  /** 選択 申請部署ID（AI制限判定に使用、複数所属時にユーザーが選択） */
  const [selectedRegistrantDivisionId, setSelectedRegistrantDivisionId] = useState<string>(loginUserDivisionId);

  /** モーダル一時選択 事業部情報（単一） - 最終選択された組織 */
  const [tempSelectedDivision, setTempSelectedDivision] = useState<OrganizationWithHierarchyType | null>(null);

  /** 顧客情報（End Userも同じものを参照） */
  const [customers, setCustomers] = useState<CustomerType[]>([]);
  /** 案件情報（複数） ⇒ 顧客情報取得時に取得 */
  const [selectedCustomerOpportunities, setSelectedCustomerOpportunities] = useState<any[]>([]);
  /** 案件情報（フィルタ済み） */
  const [filteredCustomerOpportunities, setFilteredCustomerOpportunities] = useState<any[]>([]);
  /** 顧客製品情報（複数） ⇒ 顧客情報取得時に取得 */
  const [selectedCustomerProducts, setSelectedCustomerProducts] = useState<any[]>([]);
  /** 顧客製品情報（複数） ⇒ 顧客情報取得時に取得 */
  const [filteredCustomerProducts, setFilteredCustomerProducts] = useState<any[]>([]);

  /** 製品カテゴリ情報（大分類）⇒ 顧客業界選択用 */
  const [productCategories, setProductCategories] = useState<ProductCategoryType[]>([]);
  /** MMI製品分類情報（複数） ⇒ モーダル表示時に取得 */
  const [mmiProductCategories, setMMIProductCategories] = useState<[] | null>(null);
  /** 事業部情報（複数） ⇒ モーダル表示時に取得 */
  const [divisions, setDivisions] = useState<OrganizationWithHierarchyType[] | null>(null);

  /** 会社/工場情報（複数） ⇒ モーダル表示時に取得 */
  const [companyPlants, setCompanyPlants] = useState<CompanyPlantType[] | null>(null);
  /** モーダル一時選択 会社/工場情報（単一） - 最終選択された会社/工場 */
  const [tempSelectedCompanyPlant, setTempSelectedCompanyPlant] = useState<CompanyPlantType | null>(null);
  /** モーダル一時選択 会社/工場パス（階層：会社→工場） */
  const [tempSelectedCompanyPlantPath, setTempSelectedCompanyPlantPath] = useState<CompanyPlantType[]>([]);
  /** モーダル内で選択済みの会社/工場リスト（複数選択対応） */
  const [tempSelectedCompanyPlants, setTempSelectedCompanyPlants] = useState<CompanyPlantType[]>([]);
  /** ModalHeader Tag でフォーカス中のインデックス（null = 未フォーカス） */
  const [focusedTagIndex, setFocusedTagIndex] = useState<number | null>(null);
  /** 会社グループ Accordion の開閉インデックス（undefined = 全閉） */
  const [accordionIndex, setAccordionIndex] = useState<number | undefined>(undefined);
  /** ローカル通貨/税率選択モーダルの開閉 */
  const [isCurrencyTaxModalOpen, setIsCurrencyTaxModalOpen] = useState(false);
  /** ローカル通貨/税率選択モーダル内で選択中の通貨ID */
  const [selectedActiveCurrencyId, setSelectedActiveCurrencyId] = useState("");
  /** ローカル通貨/税率選択モーダル内で選択中の税率 */
  const [selectedActiveTaxRate, setSelectedActiveTaxRate] = useState<number | null>(null);
  /** ローカル通貨/税率/エリアコード選択モーダル内で選択中のエリアコード */
  const [selectedActiveAreaCode, setSelectedActiveAreaCode] = useState("");
  /** 金利マスタデータ */
  const [areaData, setAreaData] = useState<{ areaCode: string, areaName: BilingualName }[]>([]);

  /** 会社/工場選択モーダルのボディ参照（スクロール用） */
  const companyPlantModalBodyRef = useRef<HTMLDivElement>(null);

  /**
   * 先頭文字からグループキーを取得する関数（英語名用）
   * @param nameEn 会社名（英語）
   * @returns 会社グループキー
   */
  const getGroupKey = (nameEn: string | undefined): string => {
    if (!nameEn) return "#";
    const firstChar = nameEn.charAt(0).toUpperCase();
    // アルファベットの場合はそのまま返す
    if (/^[A-Z]$/.test(firstChar)) {
      return firstChar;
    }
    // 数字の場合は "#" にグループ化
    if (/^[0-9]$/.test(firstChar)) {
      return "#";
    }
    // その他の文字
    return "#";
  };

  /**
   * 会社グループ Accordion のインデックスを算出する。
   * モーダル内の getGroupKey / sortedGroups と同等のロジック。
   * @param topItem - 会社レベル（トップ）の CompanyPlantType
   * @param allCompanyPlants - 全会社/工場データ（DynamoDB 取得済み）
   * @returns Accordion の index（undefined = 該当なし・全閉）
   */
  const calcAccordionIndex = (
    topItem: CompanyPlantType,
    allCompanyPlants: CompanyPlantType[],
  ): number | undefined => {
    const groupOrder = [
      "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M",
      "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "#",
    ];

    // トップレベル会社一覧（削除済み除外）
    const topItems = allCompanyPlants.filter(
      (cp) => !cp.parentCompanyPlantId && !cp.deleted,
    );
    // 実際に存在するグループキーを groupOrder の順で生成
    const groupKeys = new Set(topItems.map((item) => getGroupKey(item.companyPlantNameEn)));
    const sortedGroups = groupOrder.filter((k) => groupKeys.has(k));
    const targetKey = getGroupKey(topItem.companyPlantNameEn);
    const index = sortedGroups.indexOf(targetKey);
    return index >= 0 ? index : undefined;
  };

  /**  */
  const [topicsInfo, setTopicsInfo] = useState<Opportunity | null>(null);

  /** 事業部選択モーダル制御管理用 */
  const { isOpen: isDivisionModalOpen, onOpen: onDivisionModalOpen, onClose: onDivisionModalClose } = useDisclosure();

  /** 顧客業界選択モーダル制御管理用 */
  const { isOpen: isCustomerIndustryModalOpen, onOpen: onCustomerIndustryModalOpen, onClose: onCustomerIndustryModalClose } = useDisclosure();
  /** モーダル一時選択 顧客業界パス（大分類→細分類の順） */
  const [tempSelectedIndustryPath, setTempSelectedIndustryPath] = useState<ProductCategoryType[]>([]);
  /** 顧客業界選択モーダル 各階層のフィルタ値 */
  const [industryFilterValues, setIndustryFilterValues] = useState<string[]>(["", "", "", ""]);

  /** 会社/工場選択モーダル制御管理用 */
  const { isOpen: isCompanyPlantModalOpen, onOpen: onCompanyPlantModalOpen, onClose: onCompanyPlantModalClose } = useDisclosure();

  /** 案件詳細モーダル制御管理用 */
  const { isOpen: isTopicsModalOpen, onOpen: onTopicsModalOpen, onClose: onTopicsModalClose } = useDisclosure();

  /** 案件選択モーダル制御管理用 */
  const { isOpen: isOpportunityModalOpen, onOpen: onOpportunityModalOpen, onClose: onOpportunityModalClose } = useDisclosure();
  /** 案件選択モーダル内のフィルタ値 */
  const [opportunityFilterValue, setOpportunityFilterValue] = useState("");
  /** 案件選択モーダル内の一時選択 */
  const [tempSelectedOpportunity, setTempSelectedOpportunity] = useState<Record<string, any> | null>(null);

  /** 顧客製品選択モーダル制御管理用 */
  const { isOpen: isCustomerProductModalOpen, onOpen: onCustomerProductModalOpen, onClose: onCustomerProductModalClose } = useDisclosure();
  /** 顧客製品選択モーダル内のフィルタ値 */
  const [customerProductFilterValue, setCustomerProductFilterValue] = useState("");
  /** 顧客製品選択モーダル内の一時選択 */
  const [tempSelectedCustomerProduct, setTempSelectedCustomerProduct] = useState<Record<string, any> | null>(null);

  /** 過去Project選択モーダル制御管理用 */
  const { isOpen: isOpenPastProject, onOpen: onOpenPastProject, onClose: onClosePastProject } = useDisclosure();

  /** 最終確認モーダル制御管理用 */
  const { isOpen: isFinalConfirmationModalOpen, onOpen: onFinalConfirmationModalOpen, onClose: onFinalConfirmationModalClose } = useDisclosure();

  /** 金利取得結果（記入者確認時に取得） */
  const [fetchedInterestRateInfo, setFetchedInterestRateInfo] = useState<{
    rate: number | null;
    fiscalPeriod: string;
    isLatestUsed: boolean;  // 最新金利を代用したか
    latestFiscalPeriod?: string;  // 代用した場合の実際の年度
  } | null>(null);

  /** 担当者設定モーダル制御管理用 */
  const { isOpen: isOpenAssignTabModal, onOpen: onOpenAssignTabModal, onClose: onCloseAssignTabModal } = useDisclosure();

  /** クロスチェック担当者設定モーダル制御 */
  const { isOpen: isOpenCrossCheckAssignModal, onOpen: onOpenCrossCheckAssignModal, onClose: onCloseCrossCheckAssignModal } = useDisclosure();

  /** クロスチェック担当者 */
  const [crossCheckAssignees, setCrossCheckAssignees] = useState<CategoryAssignee[]>([]);

  /** 資材担当役員担当者設定モーダル制御 */
  const { isOpen: isOpenMaterialOfficerAssignModal, onOpen: onOpenMaterialOfficerAssignModal, onClose: onCloseMaterialOfficerAssignModal } = useDisclosure();

  /** 資材担当役員担当者 */
  const [materialOfficerAssignees, setMaterialOfficerAssignees] = useState<CategoryAssignee[]>([]);

  /** 承認者設定モーダル制御 */
  const { isOpen: isOpenApproverAssignModal, onOpen: onOpenApproverAssignModal, onClose: onCloseApproverAssignModal } = useDisclosure();

  /** 画面閲覧制限モーダル制御 */
  const { isOpen: isOpenViewRestrictionModal, onOpen: onOpenViewRestrictionModal, onClose: onCloseViewRestrictionModal } = useDisclosure();
  /** 画面閲覧制限「制限する」の選択状態（DBには保存しない。取得時に設定の有無で初期化） */
  const [isViewRestrictionEnabled, setIsViewRestrictionEnabled] = useState<boolean>(false);

  /** 承認者一覧 */
  const [approverAssignees, setApproverAssignees] = useState<CategoryAssignee[]>([]);

  /** 権限用 画面パーミッション */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();

  /** 差戻しだった場合のトースト表示フラグ */
  const [isFirstRemandToast, setIsFirstRemandToast] = useState<boolean>(true);

  /** 確認担当者JSON */
  const [confirmor, setConfirmor] = useState<any>(null);

  /** 担当者設定モーダルで設定した確認者情報 */
  const [selectedConfirmorData, setSelectedConfirmorData] = useState<any>(null);

  /** 担当者設定モーダルで設定した確認者ID */
  const [selectedConfirmorIds, setSelectedConfirmorIds] = useState<string[]>([]);

  /** 担当者設定モーダルで編集不可確認者ID */
  const [disabledEmployeeIds, setDisabledEmployeeIds] = useState<string[]>([]);

  /** 差し戻し時の承認者/CC/資材担当の再確定フラグ */
  const [approverReconfirmed, setApproverReconfirmed] = useState<boolean>(false);
  const [crossCheckReconfirmed, setCrossCheckReconfirmed] = useState<boolean>(false);
  const [materialOfficerReconfirmed, setMaterialOfficerReconfirmed] = useState<boolean>(false);

  /** 画面表示用確認者 */
  const [dispConfirmor, setDispConfirmor] = useState<any>(null);

  /** DB保存済み確認者（未保存変更の検知用） */
  const [savedConfirmor, setSavedConfirmor] = useState<any>(null);

  /** メール送信状態（確認区分ごと） */
  const [mailSentStatus, setMailSentStatus] = useState<MailSentStatus[]>([]);

  /** メール送信中フラグ（確認区分ID） */
  const [sendingMailDivisionId, setSendingMailDivisionId] = useState<string | null>(null);

  /** メール送信確認モーダル用 */
  const { isOpen: isMailConfirmOpen, onOpen: onMailConfirmOpen, onClose: onMailConfirmClose } = useDisclosure();
  const [mailConfirmTarget, setMailConfirmTarget] = useState<{
    divisionId: string;
    divisionName: string;
    employeeIds: string[];
    employeeNames: string[];
  } | null>(null);

  // SpreadSheet参照モーダル制御用
  const {
    isOpen: isSpreadSheetModalOpen,
    onOpen: onSpreadSheetModalOpen,
    onClose: onSpreadSheetModalClose
  } = useDisclosure();

  const { getManualByName } = useManualAPI();
  const [spreadSheetManualUrl, setSpreadSheetManualUrl] = useState<string>("");
  /** SpreadSheet参照モーダルで表示するマニュアル名のi18nキー（クロスチェック担当者一覧 / 承認者一覧 など、ボタンごとに切り替える） */
  const [spreadSheetManualNameKey, setSpreadSheetManualNameKey] = useState<string>(
    'features.InvestmentScreening.BasicInfo.label.crossCheckManualName'
  );

  /** SpreadSheet参照モーダルを指定したマニュアル名で開く */
  const openSpreadSheetModal = (manualNameKey: string) => {
    setSpreadSheetManualNameKey(manualNameKey);
    onSpreadSheetModalOpen();
  };

  /** M_CURRENCY通貨情報*/
  const [currencyData, setCurrencyData] = useState<Currency[]>([])
  /** T_CEAS_CURRENCY通貨情報*/
  const [currencyDataById, setCurrencyDataById] = useState<Currency[]>([])
  /**最新の為替レート取得　更新監視用 */
  const [currencyUpdated, setCurrencyUpdated] = useState(false);
  /** 為替レート種別（actual: 実勢レート, plan: 計画レート） */
  const [rateType, setRateType] = useState<'actual' | 'plan' | null>('actual');
  /** 為替レート種別選択中（actual: 実勢レート, plan: 計画レート） */
  const [selectedRateType, setSelectedRateType] = useState<'actual' | 'plan' | null>(null);
  /** 為替レート取得で取得\選択ボタンが押されたかを管理するフラグ */
  const [isRateTypeConfirmClicked, setIsRateTypeConfirmClicked] = useState(false);

  /** 一時読込時のロック復元の呼び出し連番（古い非同期結果を捨てるために使用） */
  const restoreSeqRef = useRef(0);

  /**
   * 一時読込後に、過去プロジェクトのPartNoをサーバーから取得して編集不可に復元する。
   * 一時保存ファイルは手編集され得るため、ファイル内の値ではなく過去PJのPartNoを根拠にロックする。
   * 取得に失敗した場合は過去PJの紐づけを解除し、再選択を促す。
   * @param pastProjectId 紐づけ先の過去プロジェクトID
   * @param pastReviewDate 紐づけ時点の承認済み振り返り日（無ければ null）
   * @param loadedPartNos 一時保存ファイルから復元したPartNo一覧
   */
  const restoreLockedPartNos = async (
    pastProjectId: string,
    pastReviewDate: string | null,
    loadedPartNos: PartNo[]
  ): Promise<void> => {
    // 呼び出し連番を採番（完了時に最新でなければ結果を捨てる）
    restoreSeqRef.current += 1;
    const seq = restoreSeqRef.current;
    try {
      const response = await WebAPI.request({
        query: `
          query getPartNo($projectId: String, $hasOtherPart: Boolean, $reviewDate: String, $referencingProjectId: String) {
            getPartNo(projectId: $projectId, hasOtherPart: $hasOtherPart, reviewDate: $reviewDate, referencingProjectId: $referencingProjectId) {
              partNo
              displayPartNo
            }
          }`,
        variables: {
          projectId: pastProjectId,
          hasOtherPart: false,
          ...(pastReviewDate && { reviewDate: pastReviewDate }),
          referencingProjectId: projectId
        }
      });
      // 別の一時読込・復元が開始されていた場合は結果を捨てる
      if (seq !== restoreSeqRef.current) return;
      if (!response?.data?.getPartNo) {
        throw new Error("getPartNo response is empty");
      }

      // 過去PJのPartNo（trim済み・空除外）と、変更予定の対応表
      const pastList: { partNo: string; displayPartNo: string }[] = (response.data.getPartNo as PartNo[])
        .map((p: PartNo) => ({ partNo: p.partNo?.trim() || "", displayPartNo: p.displayPartNo || "" }))
        .filter((p) => p.partNo !== "");
      const pastPartNos: string[] = pastList.map((p) => p.partNo);

      // 読込後の一覧に無い過去PJのPartNoを補完対象にする（既存行のdisplayPartNoは上書きしない）
      const loadedKeys = loadedPartNos.map((p) => p.partNo?.trim() || "");
      const toAdd = pastList.filter((p) => !loadedKeys.includes(p.partNo));
      if (toAdd.length > 0) {
        // stateの最新値に対して関数形式で追加（重複は除外）
        setProjectPartNo((prev) => {
          const currentKeys = prev.map((p) => p.partNo?.trim() || "");
          const newRows = toAdd.filter((p) => !currentKeys.includes(p.partNo));
          if (newRows.length === 0) return prev;
          // 未入力の空行は除いて追加する（空行が余分に残らないようにする）
          const base = prev.filter((p) => (p.partNo?.trim() || "") !== "");
          return [...base, ...newRows];
        });
        // displayPartNoがある補完行はトグルON状態にする
        const newToggleKeys = toAdd.filter((p) => !!p.displayPartNo).map((p) => p.partNo);
        if (newToggleKeys.length > 0) {
          setDisplayPartNoToggleKeys((prev) => Array.from(new Set([...prev, ...newToggleKeys])));
        }
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BasicInfo.message.partNoAdded', { partNos: toAdd.map((p) => p.partNo).join(", ") }),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }

      // 過去PJの全PartNoをロック
      setLockedPartNos(pastPartNos);
    } catch (e) {
      console.error("Failed to restore locked PartNo after temporary load", e);
      if (seq !== restoreSeqRef.current) return;
      // 紐づけ解除（既存の解除ボタンと同じ5項目）
      changeProjectBasicInfo("pastProjectId", null);
      changeProjectBasicInfo("pastProjectName", null);
      changeProjectBasicInfo("pastReviewDate", null);
      setSelectedPastProject(null);
      setLockedPartNos([]);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.pastProjectReselect'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // 一時保存対象（16項目）: projectBasicInfo / projectPartNo / selectedCustomer / selectedOpportunity /
  //   selectedCustomerProduct / endUserList / selectedMMIProductCategory / selectedDivision /
  //   selectedCompanyPlant / crossCheckAssignees / materialOfficerAssignees / approverAssignees /
  //   dispConfirmor / selectedConfirmorData / selectedConfirmorIds / rateType
  //   ※ confirmor（確認ステータス）は保存しない（DB由来の別物）
  //   ※ projectBasicInfo の projectId / updateDate / updateDateRDS / approvalStatus は保存しない（PJ識別子・サーバー管理フィールド）
  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      const data: Record<string, any> = {};

      // 確認者：モーダルなしで直接データを整形して保存する処理
      const handleDirectSave = () => {
        const validPairs = getValidFlowDivisionPairs(projectBasicInfo.purpose, !!reviewDate);
        let flowPersonData: any[] = [];

        // 1. validPairs（flowId と divisionId のペア）をベースにデータを組み立て
        validPairs.forEach(pair => {
          // dispConfirmor から、このペアの divisionId に一致する「全員」を取得
          const matchedConfirmors = dispConfirmor.filter(
            (confirmor: any) => confirmor.divisionId === pair.divisionId
          );

          // 一致した社員ごとにレコードを作成して追加
          matchedConfirmors.forEach((confirmor: any) => {
            flowPersonData.push({
              flowId: pair.flowId,
              divisionId: pair.divisionId,
              employeeId: confirmor.employeeId,
              employeeName: confirmor.employeeName,
            });
          });
        });

        // 2. 重複排除（元のロジックと完全に同じ条件）
        // employeeId + flowId + divisionId の組み合わせで重複排除
        const seen = new Set<string>();
        flowPersonData = flowPersonData.filter(item => {
          const key = `${item.flowId}-${item.employeeId}-${item.divisionId}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        // 3. ソート（元のロジックと完全に同じ条件）
        flowPersonData = flowPersonData.sort((item1, item2) => {
          if (item1.flowId < item2.flowId) return -1;
          if (item1.flowId > item2.flowId) return 1;
          return 0;
        });

        // 1. Stateの更新（画面表示用など）
        setSelectedConfirmorData(flowPersonData);
        // 2. 作成したデータを保存用のオブジェクトのキーに格納する。
        data.selectedConfirmorData = flowPersonData;
      };


      const { projectId: _pid, updateDate: _ud, updateDateRDS: _udrds, approvalStatus: _as, ...restBasicInfo } = projectBasicInfo;
      data.projectBasicInfo = restBasicInfo;
      if (projectPartNo.length > 0) data.projectPartNo = projectPartNo;
      if (selectedCustomer !== null) data.selectedCustomer = selectedCustomer;
      if (selectedOpportunity !== null) data.selectedOpportunity = selectedOpportunity;
      if (selectedCustomerProduct !== null) data.selectedCustomerProduct = selectedCustomerProduct;
      if (endUserList.some(e => e !== "")) data.endUserList = endUserList;
      if (selectedMMIProductCategory !== null) data.selectedMMIProductCategory = selectedMMIProductCategory;
      if (selectedDivision !== null) data.selectedDivision = selectedDivision;
      data.selectedRegistrantDivisionId = selectedRegistrantDivisionId;

      if (equipmentPlanNumberList !== null) {
        // 設備計画番号
        data.projectBasicInfo.equipmentPlanNumber = equipmentPlanNumberList;
      }

      if (crossCheckAssignees.length > 0) data.crossCheckAssignees = crossCheckAssignees;
      if (materialOfficerAssignees.length > 0) data.materialOfficerAssignees = materialOfficerAssignees;
      if (approverAssignees.length > 0) data.approverAssignees = approverAssignees;
      if (dispConfirmor !== null) data.dispConfirmor = dispConfirmor;
      if (dispConfirmor !== null && !selectedConfirmorData || selectedConfirmorData?.length === 0) {
        // 空の時は表示データを整形して”selectedConfirmorData”を作成する。
        handleDirectSave();
      } else if (selectedConfirmorData !== null) {
        data.selectedConfirmorData = selectedConfirmorData;
      };
      if (selectedConfirmorIds.length > 0) data.selectedConfirmorIds = selectedConfirmorIds;
      if (rateType !== 'actual') data.rateType = rateType;
      return data;
    },
    applySnapshot: (data: Record<string, any>) => {
      if (data.projectBasicInfo !== undefined) {
        // projectId / updateDate / updateDateRDS / approvalStatus はサーバー管理フィールドのため現在値を維持する
        // （スナップショットには含まれていないが、古いスナップショットが含む場合も考慮して明示的に上書き）
        type SnapshotBasicInfo = Omit<ProjectWithRdsData, 'projectId' | 'updateDate' | 'updateDateRDS' | 'approvalStatus'>;
        setProjectBasicInfo({
          ...projectBasicInfo,
          ...(data.projectBasicInfo as SnapshotBasicInfo),
          projectId: projectBasicInfo.projectId,
          updateDate: projectBasicInfo.updateDate,
          updateDateRDS: projectBasicInfo.updateDateRDS,
          approvalStatus: projectBasicInfo.approvalStatus,
        });
      }
      if (data.projectPartNo !== undefined) {
        const restoredPartNo = data.projectPartNo as PartNo[];
        setProjectPartNo(restoredPartNo);
        // displayPartNoが設定済みの行はトグルON状態に復元する（No.435対応）
        const restoredToggleKeys = restoredPartNo
          .filter((p: PartNo) => !!p.displayPartNo)
          .map((p: PartNo) => p.partNo?.trim() || "");
        setDisplayPartNoToggleKeys(restoredToggleKeys);
      }
      // 過去Project紐づけPartNoの非活性（ロック）を復元する（スナップショットにはlockedPartNosを保存しない）
      if (data.projectBasicInfo !== undefined || data.projectPartNo !== undefined) {
        const snapshotBasicInfo = data.projectBasicInfo as Partial<ProjectWithRdsData> | undefined;
        // スナップショットに無い場合は現在の画面stateにフォールバックする
        const restoredPastProjectId = snapshotBasicInfo
          ? (snapshotBasicInfo.pastProjectId ?? null)
          : (projectBasicInfo.pastProjectId ?? null);
        const restoredPastReviewDate = snapshotBasicInfo
          ? (snapshotBasicInfo.pastReviewDate ?? null)
          : (projectBasicInfo.pastReviewDate ?? null);
        const restoredPartNos = (data.projectPartNo as PartNo[] | undefined) ?? projectPartNo;
        if (restoredPastProjectId) {
          void restoreLockedPartNos(restoredPastProjectId, restoredPastReviewDate, restoredPartNos);
        } else {
          // 紐づけなし（または改ざんで解除）の場合はロックを解除し、実行中の復元結果も無効化する
          restoreSeqRef.current += 1;
          setLockedPartNos([]);
        }
      }
      if (data.selectedCustomer !== undefined) {
        const customer = data.selectedCustomer as CustomerType | null;
        setSelectedCustomer(customer);
        if (customer?.customerId) {
          fetchCustomerProducts(customer.customerId);
          fetchOpportunities(customer.customerId);
        }
      }
      if (data.selectedOpportunity !== undefined) setSelectedOpportunity(data.selectedOpportunity);
      if (data.selectedCustomerProduct !== undefined) setSelectedCustomerProduct(data.selectedCustomerProduct);
      if (data.endUserList !== undefined) setEndUserList(data.endUserList as string[]);
      if (data.selectedMMIProductCategory !== undefined) setSelectedMMIProductCategory(data.selectedMMIProductCategory as MMIProductCategoryType | null);
      if (data.selectedDivision !== undefined) setSelectedDivision(data.selectedDivision as OrganizationWithHierarchyType | null);
      if (data.selectedRegistrantDivisionId !== undefined) setSelectedRegistrantDivisionId(data.selectedRegistrantDivisionId as string);
      if (data.crossCheckAssignees !== undefined) setCrossCheckAssignees(data.crossCheckAssignees as CategoryAssignee[]);
      if (data.materialOfficerAssignees !== undefined) setMaterialOfficerAssignees(data.materialOfficerAssignees as CategoryAssignee[]);
      if (data.approverAssignees !== undefined) setApproverAssignees(data.approverAssignees as CategoryAssignee[]);
      if (data.dispConfirmor !== undefined) setDispConfirmor(data.dispConfirmor);
      if (data.selectedConfirmorData !== undefined) setSelectedConfirmorData(data.selectedConfirmorData);
      if (data.selectedConfirmorIds !== undefined) setSelectedConfirmorIds(data.selectedConfirmorIds as string[]);
      if (data.rateType !== undefined) setRateType(data.rateType as 'actual' | 'plan');
      if (data.projectBasicInfo.equipmentPlanNumber !== undefined) {
        setEquipmentPlanNumberList(data.projectBasicInfo.equipmentPlanNumber as string[]);
      }
    },
  }));

  /** 為替レート種別選択モーダル用 */
  const { isOpen: isRateTypeModalOpen, onOpen: onRateTypeModalOpen, onClose: onRateTypeModalClose } = useDisclosure();
  /** 実勢レートプレビュー用 */
  const [previewActualRates, setPreviewActualRates] = useState<LatestCurrency[]>([]);
  /** 計画レートプレビュー用 */
  const [previewPlanRates, setPreviewPlanRates] = useState<CurrencyProspect[]>([]);
  /** レートプレビュー取得中フラグ */
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // -----------------------------
  // 権限情報
  // -----------------------------
  const { userPermissions, reviewDate } = useContext(ProjectContext) as ProjectContextType;

  /**
   * 初期表示時
   */
  useEffect(() => {
    fetchCustomers();
    getProductCategory();

    // 読み込み時にレート表示をできるように初期表示時で取得
    // プレビュー用にレートを取得
    setIsLoadingPreview(true);
    const fetchPreviewRates = async () => {
      try {
        // 会計年度を算出（4月始まり：1〜3月は前年度）
        const now = new Date();
        const currentYear = (now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear()).toString();
        const [actualData, planData] = await Promise.all([
          getLatestCurrencyFromSas(),
          getCurrencyProspectFromSas(currentYear)
        ]);
        setPreviewActualRates(actualData);
        setPreviewPlanRates(planData);
      } catch (error) {
        console.error('[handlePutCurrency] プレビュー用レート取得エラー:', error);
      } finally {
        setIsLoadingPreview(false);
      }
    }
    fetchPreviewRates();
  }, []);

  /**
   * 顧客製品ID変更時（ユーザー操作）に製品カテゴリの全階層IDを取得してcustomerIndustry/customerIndustryHierarchyにセット
   * 初回データロード時はスキップし、保存済みのcustomerIndustryを優先する
   */
  useEffect(() => {
    if (!projectBasicInfo.customerProductId) return;

    if (!isCustomerProductIdChangedByUser.current) {
      // 初回データロード時はスキップ
      return;
    }
    isCustomerProductIdChangedByUser.current = false;

    const fetchCategoryHierarchy = async () => {
      const response = await WebAPI.request({
        query: `
          query getCategoryHierarchyByCustomerProductId($customerProductId: String) {
            getCategoryHierarchyByCustomerProductId(customerProductId: $customerProductId)
          }`,
        variables: { customerProductId: projectBasicInfo.customerProductId }
      });

      const categoryIds: string[] | null = response?.data?.getCategoryHierarchyByCustomerProductId;
      if (categoryIds && categoryIds.length > 0) {
        // 大分類IDをcustomerIndustryにセット
        changeProjectBasicInfo("customerIndustry", categoryIds[0]);
        // 階層名を " / " 区切りで生成してcustomerIndustryHierarchyにセット
        const hierarchyNames = categoryIds
          .map(id => productCategories.find(c => c.productCategoryId === id)?.productCategoryName)
          .filter(Boolean)
          .join(" / ");
        changeProjectBasicInfo("customerIndustryHierarchy", hierarchyNames);
      }
    };

    fetchCategoryHierarchy();
  }, [projectBasicInfo.customerProductId]);

  /**
   * 権限情報
   */
  useEffect(() => {
    if (!userPermissions || (projectId && !projectBasicInfo.registrantDivisionId)) {
      return;
    }

    const pagePermissions = getPermissions(userPermissions, WorkflowId.BasicInfo, projectBasicInfo.purpose, loginUserEmployeeId, loginUserDivisionId, selectedRegistrantDivisionId, loginUserAffiliations);
    setPagePermissions(pagePermissions);
  }, [userPermissions, loginUserEmployeeId, loginUserDivisionId, projectBasicInfo, loginUserAffiliations, selectedRegistrantDivisionId]);

  useEffect(() => {
    if (pagePermissions?.currentFlowId === WorkflowId.Remand) {
      // 差戻しだった場合、トースト表示して担当者設定を促す。
      if (isFirstRemandToast) {
        setIsFirstRemandToast(false);
        const items = [t('features.InvestmentScreening.BasicInfo.category.confirmerSelection'), t('features.InvestmentScreening.BasicInfo.category.approverSelection')];
        if (projectBasicInfo.crossCheckRequired) items.push(t('features.InvestmentScreening.BasicInfo.category.crossCheckAssigneeSelection'));
        if (projectBasicInfo.materialOfficerRequired) items.push(t('features.InvestmentScreening.BasicInfo.category.materialOfficerSelection'));
        items.push(t('features.InvestmentScreening.BasicInfo.category.registrantConfirm'));
        toast({
          title: TITLE,
          description: (<>{t('features.InvestmentScreening.BasicInfo.label.remandReassignPrefix')}{items.map((item, i) => (
            <span key={i}>{i > 0 && "・"}{`「${item}」`}</span>
          ))}<br />{t('features.InvestmentScreening.BasicInfo.label.remandReassignSuffix')}</>),
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
      }
    }
  }, [pagePermissions, projectBasicInfo]);

  /**
   * プロジェクトID変更時にプロジェクト情報を取得する。
   */
  useEffect(() => {
    if (projectId) {
      if (reviewDate && isReadyReview === false) {
        // 振り返りモードで振り返り準備ができていない場合、処理しない
        return;
      }

      // 既存案件
      fetchProject();
    } else {
      // 新規案件登録

      // トピックス情報初期化
      setTopicsInfo(null);

      // 事業部
      setSelectedDivision({
        parentOrganizationId: null,
        parentOrganizationName: "",
        organizationId: loginUserDivisionId,
        organizationName: loginUserDivisionName,
      });

      // すべて初期化
      setProjectBasicInfo(JSON.parse(JSON.stringify(defaultProjectBasicInfo)));
      setSelectedRegistrantDivisionId(loginUserDivisionId);
      setBeforePurpose(null);
      setSavedEquipmentSource(null);
      const newPartNoInfo: PartNo = { projectId: projectId, partNo: "", displayPartNo: "" };
      setProjectPartNo([newPartNoInfo]);

      setSelectedCustomer(null);
      setSelectedOpportunity(null);
      setSelectedCustomerProduct(null);
      setEndUserList([""]); // End User名: 複数入力リスト初期化
      setEquipmentPlanNumberList([""]); // 設備計画番号: 複数入力リスト初期化
      setSelectedMMIProductCategory(null);
      setSelectedDivision(null);

      setSelectedCustomerProducts([]);
      setFilteredCustomerProducts([]);

      setEmployeeData({
        employeeId: loginUserEmployeeId,
        employeeName: loginUserEmployeeName,
        divisionId: loginUserDivisionId,
        divisionName: loginUserDivisionName,
      });
    }

  }, [projectId]);

  /**
   * 確認者ユーザーID取得
   */
  useEffect(() => {
    const fetchConfirmor = async () => {
      try {
        // 確認担当者JSONを取得
        const confirmorData = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmorData);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }
    };
    if (projectId) {
      fetchConfirmor();
    }
  }, [projectId, reviewDate]);

  /**
   * メール送信履歴取得
   * 振り返りモード時は reviewDate を引数に渡し、振り返り用テーブルから取得する
   */
  useEffect(() => {
    const fetchMailSendHistory = async () => {
      try {
        const result = await WebAPI.request({
          query: `
            query GetMailSendHistory($projectId: String!, $reviewDate: String) {
              getMailSendHistory(projectId: $projectId, reviewDate: $reviewDate) {
                divisionId
                sentAt
                sentByEmployeeId
                sentByEmployeeName
              }
            }
          `,
          variables: {
            projectId,
            ...(reviewDate && { reviewDate }),
          }
        });
        console.log("result", result)
        const history = result?.data?.getMailSendHistory || [];
        setMailSentStatus(history.map((h: any) => ({
          divisionId: h.divisionId,
          sentAt: h.sentAt,
          sentByEmployeeId: h.sentByEmployeeId,
          sentByEmployeeName: h.sentByEmployeeName,
        })));
      } catch (error) {
        console.error("メール送信履歴取得エラー:", error);
      }
    };

    if (projectId) {
      fetchMailSendHistory();
    }
  }, [projectId, reviewDate]);

  /**
   * 事業部選択表示時 - 既存選択から階層を復元
   */
  useEffect(() => {
    if (isDivisionModalOpen) {
      if (!divisions) {
        fetchDivisions();
      } else if (selectedDivision && selectedDivision.organizationId) {
        // divisionsから完全なオブジェクト（parentOrganizationId含む）を取得
        const organization = divisions.find((org) => org.organizationId === selectedDivision.organizationId);

        if (organization) {
          // 既存選択から階層パスを復元
          const buildHierarchyPath = (targetOrg: OrganizationWithHierarchyType) => {
            const path: OrganizationWithHierarchyType[] = [];
            let current: OrganizationWithHierarchyType | undefined = targetOrg;

            // 親をたどってパスを構築（最上位から順に並べる）
            // organization00000（ミネベアミツミ）が最上位のため、parentOrganizationId === "organization00000" で終了
            while (current) {
              path.unshift(current);
              if (current.parentOrganizationId === "organization00000") {
                break; // 本部に到達したら終了
              }
              current = divisions.find((org) => org.organizationId === current?.parentOrganizationId);
            }

            return path;
          };

          const hierarchyPath = buildHierarchyPath(organization);

          // hierarchyPath = [事業本部, 部門, 事業部, ビジネス小計, ビジネス, 製品]
          setTempSelectedDivisionPath(hierarchyPath);
          setTempSelectedDivision(organization);
        } else {
          // マスタに存在しない場合はリセット
          setTempSelectedDivisionPath([]);
          setTempSelectedDivision(null);
        }
      } else {
        // 選択なしの場合はリセット
        setTempSelectedDivisionPath([]);
        setTempSelectedDivision(null);
      }
    }
  }, [isDivisionModalOpen]);

  /**
  * 通貨情報取得
  */
  useEffect(() => {
    const getCurrencyData = async () => {
      if (projectId && rateType === null) return;

      let getCurrencyData: Currency[];
      if (!projectId || rateType === 'actual') {
        // 実勢レート取得（営業支援API経由）
        const latestData = await getLatestCurrencyFromSas();
        // Currency型に変換
        getCurrencyData = latestData.map((item, index) => ({
          currencyId: item.currencyId,
          currencyName: item.currencyName,
          exchangeRate: item.exchangeRate,
          order: index + 1,
        }));
      } else {
        // 計画レート取得（営業支援API経由）
        // 会計年度を算出（4月始まり：1〜3月は前年度）
        const now = new Date();
        const currentYear = (now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear()).toString();
        const prospectData = await getCurrencyProspectFromSas(currentYear);
        // Currency型に変換
        getCurrencyData = prospectData.map((item, index) => ({
          currencyId: item.currencyId,
          currencyName: item.currencyName,
          exchangeRate: item.exchangeRate,
          order: index + 1,
        }));
      }
      setCurrencyData([...getCurrencyData]);
      if (projectId) {
        // T_CEAS_CURRENCYを取得
        const getCurrencyDataById = await getCurrencyById(projectId);
        setCurrencyDataById([...getCurrencyDataById]);
      };
    };
    getCurrencyData();
  }, [projectId, currencyUpdated, rateType])

  /**
  * SpreadSheetモーダルが開いた時に、マニュアルデータを取得して表示用URLを生成する
  */
  useEffect(() => {
    // モーダルを閉じた・アンマウントされた場合に、fetch完了前でもローディング状態を確実に解除するためのフラグ
    let cancelled = false;

    const fetchManualUrl = async () => {
      if (!isSpreadSheetModalOpen) {
        return;
      }
      // 前回開いた時のURLが一瞬表示されないよう、開いた直後にリセットする
      setSpreadSheetManualUrl("");
      WebAPI.setLoading(true);
      try {
        const manualNameKey = t(spreadSheetManualNameKey);
        const targetManual = await getManualByName(manualNameKey);
        if (cancelled) return;

        if (targetManual) {
          if (targetManual.manualLink) {
            const spreadsheetId = extractSpreadsheetId(targetManual.manualLink);
            if (spreadsheetId) {
              // 本社経営管理権限の有無で edit と preview を切り替える
              const mode = loginUserIsHeadquartersManagement ? 'edit' : 'preview';
              setSpreadSheetManualUrl(`https://docs.google.com/spreadsheets/d/${spreadsheetId}/${mode}`);
            } else {
              setSpreadSheetManualUrl(targetManual.manualLink);
            }
          } else {
            // リンクが空文字などの不正なデータだった場合はローディングを解除
            WebAPI.setLoading(false);
          }
        } else {
          // 対象データが存在しない場合
          setSpreadSheetManualUrl("");
          WebAPI.setLoading(false);
          toast({
            title: t('common.label.error'),
            description: t('features.InvestmentScreening.BasicInfo.message.spreadSheetManualError', { manualName: manualNameKey }),
            status: 'error',
            duration: 8000,
            isClosable: true,
          });
        }
      } catch (error) {
        if (cancelled) return;
        console.error("マニュアルURLの取得に失敗しました", error);
        setSpreadSheetManualUrl("");
        WebAPI.setLoading(false);
        toast({
          title: t('common.label.error'),
          description: t('features.InvestmentScreening.BasicInfo.message.spreadSheetManualError', { manualName: t(spreadSheetManualNameKey) }),
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
      }
    };
    fetchManualUrl();

    return () => {
      cancelled = true;
      // モーダルが開いている状態からこのeffectが再実行/破棄される場合
      // （閉じられた場合を含む）、fetch未完了でもローディングを解除しておく
      if (isSpreadSheetModalOpen) {
        WebAPI.setLoading(false);
      }
    };
  }, [isSpreadSheetModalOpen, spreadSheetManualNameKey, loginUserIsHeadquartersManagement]);

  /**
   * 為替レート取得で取得/選択ボタンを押したときの処理
   */
  useEffect(() => {

    // 取得ボタンを押したとき、かつ既存プロジェクトの場合のみ実行
    if (isRateTypeConfirmClicked && projectId) {
      updateCurrencyAsync();

      // 実行後はフラグを false に戻す（連続実行の防止）
      setIsRateTypeConfirmClicked(false);
    }
  }, [projectId, isRateTypeConfirmClicked, rateType]);

  /**
   * プロジェクトIDを新規採番して返す。
   * @param customerId 顧客ID
   * @param mmiproductCategoryId MMI製品分類ID
   * @returns 採番したプロジェクトID
   */
  // 顧客名: 自由入力化に伴い、プロジェクトIDから顧客ID部分を除外
  // const generateProjectId = (customerId: string | undefined, mmiproductCategoryId: string | undefined) => {
  //   const timestamp = dayjs().format("YYYYMMDDHHmmss");
  //   return `${customerId}-${mmiproductCategoryId}-${timestamp}`;
  // };
  const generateProjectId = (mmiproductCategoryId: string | undefined) => {
    const timestamp = dayjs().format("YYYYMMDDHHmmss");
    return `${mmiproductCategoryId}-${timestamp}`;
  };

  /**
   * 指定した入力項目の値でプロジェクト情報を変更する。
   * @param event 入力項目
   */
  const handleChangeProjectBasicInfo = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    changeProjectBasicInfo(name, value);
  };

  /**
   * 指定した値でプロジェクト情報を変更する。
   * @param name 変更先プロパティ名
   * @param value 入力項目
   */
  const changeProjectBasicInfo = (name: string, value: any) => {
    setProjectBasicInfo((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  /**
   * 指定した値でパーツNoを変更する。
   * パーツNo自体を変更した場合、変更前の値に紐づく変更予定（displayPartNo）の
   * トグル状態と入力値は無効になるためクリアする（No.435対応）。
   * @param index 変更インデックス
   * @param newPartNo 入力項目
   */
  const changeProjectPartNo = (index: number, newPartNo: string) => {
    const oldPartNoKey = projectPartNo[index]?.partNo?.trim() || "";
    if (oldPartNoKey && displayPartNoToggleKeys.includes(oldPartNoKey)) {
      setDisplayPartNoToggleKeys(prev => prev.filter(key => key !== oldPartNoKey));
    }
    setProjectPartNo((prevState) =>
      prevState.map((partNoInfo, idx) =>
        idx === index ? { ...partNoInfo, partNo: newPartNo, displayPartNo: "" } : partNoInfo
      )
    );
  };

  /**
   * 指定した値で変更後パーツNo（displayPartNo）を変更する。
   * @param index 変更インデックス
   * @param newDisplayPartNo 入力項目
   */
  const changeProjectDisplayPartNo = (index: number, newDisplayPartNo: string) => {
    setProjectPartNo((prevState) =>
      prevState.map((partNoInfo, idx) =>
        idx === index ? { ...partNoInfo, displayPartNo: newDisplayPartNo } : partNoInfo
      )
    );
  };

  /**
  * 指定した値でフィルター情報を変更する。
  * @param propertyName 変更先プロパティ名
  * @param value 入力項目
  */
  const changeFilterPastProjectItems = (propertyName: string, value: any) => {
    setFilterPastProjectItems((prevState: any) => ({
      ...prevState,
      [propertyName]: value,
    }));
  }

  /**
   * プロジェクト情報のパーツNo入力欄を追加する。
   */
  const removePartNo = () => {
    // 末尾のPartNoを取得
    const lastPartNo = projectPartNo[projectPartNo.length - 1]?.partNo?.trim() || "";
    // 末尾から削除
    const newProjectPartNo = projectPartNo?.filter((_, idx) => idx < projectPartNo.length - 1);
    setProjectPartNo(newProjectPartNo);
    // 削除したPartNoが他の行に存在しない場合のみ、lockedPartNosから削除
    const remainingPartNos = newProjectPartNo.map(p => p.partNo?.trim() || "");
    if (lastPartNo && lockedPartNos.includes(lastPartNo) && !remainingPartNos.includes(lastPartNo)) {
      setLockedPartNos(lockedPartNos.filter(p => p !== lastPartNo));
    }
    // 削除したPartNoが他の行に存在しない場合のみ、displayPartNoToggleKeysから削除（No.435対応）
    if (lastPartNo && displayPartNoToggleKeys.includes(lastPartNo) && !remainingPartNos.includes(lastPartNo)) {
      setDisplayPartNoToggleKeys(displayPartNoToggleKeys.filter(p => p !== lastPartNo));
    }
    // 削除したPartNoが他の行に存在しない場合のみ、registeredPartNosから削除（No.435対応）
    if (lastPartNo && registeredPartNos.includes(lastPartNo) && !remainingPartNos.includes(lastPartNo)) {
      setRegisteredPartNos(registeredPartNos.filter(p => p !== lastPartNo));
    }
  }

  /**
   * プロジェクト情報の指定されたインデックスのパーツNo入力欄を削除する。
   */
  const removePartNoByIndex = (indexToRemove: number) => {
    if (projectPartNo.length <= 1) return;
    const partNoToRemove = projectPartNo[indexToRemove]?.partNo?.trim() || "";
    // 指定インデックスを削除
    const newProjectPartNo = projectPartNo?.filter((_, idx) => idx !== indexToRemove);
    setProjectPartNo(newProjectPartNo);
    // 削除したPartNoが他の行に存在しない場合のみ、lockedPartNosから削除
    const remainingPartNos = newProjectPartNo.map(p => p.partNo?.trim() || "");
    if (partNoToRemove && lockedPartNos.includes(partNoToRemove) && !remainingPartNos.includes(partNoToRemove)) {
      setLockedPartNos(lockedPartNos.filter(p => p !== partNoToRemove));
    }
    // 削除したPartNoが他の行に存在しない場合のみ、displayPartNoToggleKeysから削除（No.435対応）
    if (partNoToRemove && displayPartNoToggleKeys.includes(partNoToRemove) && !remainingPartNos.includes(partNoToRemove)) {
      setDisplayPartNoToggleKeys(displayPartNoToggleKeys.filter(p => p !== partNoToRemove));
    }
    // 削除したPartNoが他の行に存在しない場合のみ、registeredPartNosから削除（No.435対応）
    if (partNoToRemove && registeredPartNos.includes(partNoToRemove) && !remainingPartNos.includes(partNoToRemove)) {
      setRegisteredPartNos(registeredPartNos.filter(p => p !== partNoToRemove));
    }
  }

  /**
   * 指定した行の変更予定トグル状態を切り替える（No.435対応）。
   * OFF→ON: displayPartNo入力モードに切り替える（既存値があればそのまま維持）。
   * ON→OFF: displayPartNoの入力値を破棄し、partNo入力モードに戻す。
   * @param index 対象行のインデックス
   * @param partNoKey 状態管理キーとして使うtrim済みpartNoの値
   */
  const toggleDisplayPartNoMode = (index: number, partNoKey: string): void => {
    const isCurrentlyOn = displayPartNoToggleKeys.includes(partNoKey);
    if (isCurrentlyOn) {
      // OFFに戻す場合はdisplayPartNoの入力値を破棄する
      changeProjectDisplayPartNo(index, "");
      setDisplayPartNoToggleKeys(prev => prev.filter(key => key !== partNoKey));
    } else {
      setDisplayPartNoToggleKeys(prev => [...prev, partNoKey]);
    }
  };

  /**
   * 入力値が、自分自身の行を除く他の行のpartNo・displayPartNoのいずれかと重複していないか判定する（No.435対応）。
   * 空文字は「未入力」を意味し複数行に存在しうるため、比較対象から除外する。
   * @param value 入力しようとしている値
   * @param currentIndex 編集中の行のインデックス（自分自身は比較対象から除外する）
   * @returns 重複していればtrue
   */
  const isDuplicatePartNoValue = (value: string, currentIndex: number): boolean => {
    if (value === "") return false;
    return projectPartNo.some((p, idx) => {
      if (idx === currentIndex) return false;
      return p.partNo === value || (!!p.displayPartNo && p.displayPartNo === value);
    });
  };

  /**
   * プロジェクト情報のパーツNo入力欄を追加する。
   */
  const addPartNo = () => {
    const newPartNo: PartNo = { partNo: "" };
    // 末尾に追加
    setProjectPartNo((prevState) => [...prevState, newPartNo]);
  }

  /**
   * End User名の入力欄を指定インデックスから削除する。
   */
  const removeEndUserByIndex = (indexToRemove: number) => {
    setEndUserList((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  /**
   * End User名の入力欄を追加する。
   */
  const addEndUser = () => {
    setEndUserList((prev) => [...prev, ""]);
  };

  /**
   * End User名の入力値を変更する。
   */
  const changeEndUser = (index: number, value: string) => {
    setEndUserList((prev) =>
      prev.map((item, idx) => (idx === index ? value : item))
    );
  };

  /**
   * 設備計画番号の入力欄を指定インデックスから削除する。
   */
  const removeEquipmentPlanNumberByIndex = (indexToRemove: number) => {
    setEquipmentPlanNumberList((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  /**
   * 設備計画番号の入力欄を追加する。
   */
  const addEquipmentPlanNumber = () => {
    setEquipmentPlanNumberList((prev) => [...prev, ""]);
  };

  /**
   * 設備計画番号の入力値を変更する。
   */
  const changeEquipmentPlanNumber = (index: number, value: string) => {
    setEquipmentPlanNumberList((prev) =>
      prev.map((item, idx) => (idx === index ? value : item))
    );
  };

  /**
   * プロジェクト情報を取得する。
   */
  const fetchProject = async () => {
    setHasfetchException(false);

    // プロジェクト情報取得（dynamodb、RDS）
    const response = await WebAPI.request({
      query: `
          query fetchProject($projectId: String, $useRDSData: Boolean, $hasOtherPart: Boolean, $reviewDate: String) {
            getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
              projectId
              projectName
              revisedCustomerProductNameByRevision
              revisedOpportunityNameByRevision
              mmProductNameFreeWrite
              specificationAndPerformance
              purpose
              purposeOfInvestment
              applicationFormType
              profitLossDisplayType
              carModel
              customerIndustry
              customerIndustryHierarchy
              applicationMonth
              projectStartDate
              projectEndDate
              eolDate
              equipmentCategory
              equipmentUsageType
              increasedProductionStartDate
              updateAndOtherStartDate
              rationalizationStartDate
              newProductStartDate
              pastProjectId
              pastProjectName
              pastReviewDate
              approvalStatus
              updateDate
              applicationDay
              customerId
              customerName
              customerFullName
              opportunityId
              opportunityName
              applicationCategory
              customerProductId
              customerProductName
              mmProductCategoryId
              mmProductCategoryName
              endUserId
              endUserName
              registrantEmployeeId
              registrantEmployeeName
              registrantDivisionId
              registrantDivisionName
              divisionId
              divisionName
              companyPlantId
              companyPlantSnapshot
              baseInterestRate
              applicationNumber
              equipmentPlanNumber
              plannedBudget
              budgetAmount
              divisionHierarchy
              divisionHierarchyEn
              rateType
              confirmEmployees {
                employeeId
                divisionId
                employeeName
              }
              updateDateRDS
              approverAssignees
              crossCheckRequired
              crossCheckDepartments
              crossCheckAssignees
              materialOfficerRequired
              materialOfficerAssignees
              screenViewRestriction
            }
            getPartNo(projectId: $projectId, hasOtherPart: $hasOtherPart, reviewDate: $reviewDate) {
              partNo
              displayPartNo
            }
          }`,
      variables: {
        projectId: projectId,
        useRDSData: true,
        hasOtherPart: false,
        ...(reviewDate && { reviewDate })
      }
    });

    if (response && response.data && response.data.getProjectById) {

      if (!response.data.getProjectById.projectId) {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BasicInfo.message.projectFetchFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getProjectById. errors = ${JSON.stringify(response?.errors)}`);
        setHasfetchException(true);
        return;
      }

      // 顧客業界: 自由入力化に伴い、既存のデフォルト設定をコメントアウト
      // if (!response.data.getProjectById.customerIndustry) {
      //   // changeProjectBasicInfo("customerIndustry", CustomerIndustry.InVehicle.code);
      //   response.data.getProjectById.customerIndustry = CustomerIndustry.InVehicle.code;
      // }
      // 顧客業界: コード値をそのまま使用（Selectの選択肢はproductCategoryIdで管理）

      // 設備用途区分: 未設定の場合は「専用設備」をデフォルト設定
      if (!response.data.getProjectById.equipmentUsageType) {
        response.data.getProjectById.equipmentUsageType = EquipmentUsageType.Dedicated.code;
      }

      // 正常に取得できた場合
      setProjectBasicInfo(response.data.getProjectById);

      const projectInfo = response.data.getProjectById;

      // 画面閲覧制限: 設定があれば「制限する」で表示
      setIsViewRestrictionEnabled(projectInfo.screenViewRestriction != null);

      // カテゴリ割当データの復元（旧形式 departmentCode/departmentName にも対応）
      const parseCategoryAssignees = (json: string | undefined): CategoryAssignee[] => {
        if (!json) return [];
        try {
          const parsed = JSON.parse(json);
          if (!Array.isArray(parsed)) return [];
          return parsed.map((item: any) => ({
            categoryId: item.categoryId ?? item.departmentCode ?? "",
            categoryName: item.categoryName ?? item.departmentName ?? "",
            employees: item.employees ?? [],
          }));
        } catch (e) {
          console.error("Failed to parse category assignees:", e);
          return [];
        }
      };

      setCrossCheckAssignees(parseCategoryAssignees(projectInfo.crossCheckAssignees));
      setMaterialOfficerAssignees(parseCategoryAssignees(projectInfo.materialOfficerAssignees));
      setApproverAssignees(parseCategoryAssignees(projectInfo.approverAssignees));

      // 登録済み目的を保持
      setBeforePurpose(projectInfo.purpose);
      // 取得時点の DB 保存値を保持（設備入れ替わり警告の比較用）
      setSavedEquipmentSource({
        purpose: projectInfo.purpose ?? null,
        pastProjectId: projectInfo.pastProjectId ?? null,
        pastReviewDate: projectInfo.pastReviewDate ?? null,
      });

      // 為替レート種別（保存されていない場合はデフォルトで実勢レート）
      if (projectInfo.rateType) {
        setRateType(projectInfo.rateType as 'actual' | 'plan');
      }

      if (projectInfo.confirmEmployees) {
        const confirmEmployees = projectInfo.confirmEmployees as Array<{ employeeId: string; divisionId: string; employeeName: string }>;
        const confirmEmployeeIds = confirmEmployees.map(emp => emp.employeeId);
        // 確認者従業員ID
        setSelectedConfirmorIds(confirmEmployeeIds);
        // DB保存済み担当者も解除可能にする（確認済みの場合、確認履歴はワークフローから取得して表示される）
        setDisabledEmployeeIds([]);

        setDispConfirmor(confirmEmployees);
        // DB保存済み確認者を保持（未保存変更の検知用）
        setSavedConfirmor(confirmEmployees);
      }

      // RDSデータの選択値設定
      // 顧客名: IDデータの場合はマスタ連携復元、テキストデータの場合は"顧客名,正式社名"形式(エスケープ済み)を分割して復元
      {
        // customerName が存在し customerId と異なる場合はマスタ解決成功（IDデータ）
        if (projectInfo.customerName && projectInfo.customerName !== projectInfo.customerId) {
          changeProjectBasicInfo("customerName", projectInfo.customerName || "");
          changeProjectBasicInfo("customerFullName", projectInfo.customerFullName || "");

          setSelectedCustomer({
            customerId: projectInfo.customerId,
            customerName: projectInfo.customerName,
            customerFullName: projectInfo.customerFullName,
          } as CustomerType);

          // 案件・顧客製品情報を取得
          await fetchOpportunities(projectInfo.customerId);
          await fetchCustomerProducts(projectInfo.customerId);
        } else {
          // 自由入力テキスト: "顧客名,正式社名" 形式（値中のカンマはエスケープ済み）を分割して復元
          const parts = splitEscapedList(projectInfo.customerId);
          changeProjectBasicInfo("customerName", parts[0] || "");
          changeProjectBasicInfo("customerFullName", parts[1] || "");
        }
      }

      // 顧客製品名: IDデータの場合はマスタ連携復元、テキストデータの場合はそのまま表示
      {
        const name = projectInfo.customerProductName || projectInfo.customerProductId || "";
        if (projectInfo.revisedCustomerProductNameByRevision?.trim()) {
          changeProjectBasicInfo("customerProductName", projectInfo.revisedCustomerProductNameByRevision);
        } else {
          changeProjectBasicInfo("customerProductName", name);
        }
        // customerProductName が存在し customerProductId と異なる場合はマスタ解決成功
        if (projectInfo.customerProductName && projectInfo.customerProductName !== projectInfo.customerProductId) {
          setSelectedCustomerProduct({
            customerProductId: projectInfo.customerProductId,
            customerProductName: projectInfo.customerProductName,
          });
        }
      }

      // 案件名: IDデータの場合はマスタ連携復元、テキストデータの場合はそのまま表示
      {
        const name = projectInfo.opportunityName || projectInfo.opportunityId || "";
        if (projectInfo.revisedOpportunityNameByRevision?.trim()) {
          changeProjectBasicInfo("opportunityName", projectInfo.revisedOpportunityNameByRevision);
        } else {
          changeProjectBasicInfo("opportunityName", name);
        }
        // opportunityName が存在し opportunityId と異なる場合はマスタ解決成功（IDデータ）
        if (projectInfo.opportunityName && projectInfo.opportunityName !== projectInfo.opportunityId) {
          setSelectedOpportunity({
            opportunityId: projectInfo.opportunityId,
            opportunityName: projectInfo.opportunityName,
          });
        }
      }

      // トピックス情報初期化
      setTopicsInfo(null);

      // End User名の復元（カンマ区切り対応、値中のカンマはエスケープ解除して復元）
      {
        const list = splitEscapedList(projectInfo.endUserName);
        setEndUserList(list.length > 0 ? list : [""]);
      }

      // 設備計画番号の復元（配列）
      {
        const list = Array.isArray(projectInfo.equipmentPlanNumber) ? projectInfo.equipmentPlanNumber : [];
        setEquipmentPlanNumberList(list.length > 0 ? list : [""]);
      }

      // MMI製品分類
      setSelectedMMIProductCategory({
        MMProductCategoryId: projectInfo.mmProductCategoryId,
        MMProductCategoryName: projectInfo.mmProductCategoryName,
      });

      // 事業部
      setSelectedDivision({
        parentOrganizationId: null,
        parentOrganizationName: "",
        organizationId: projectInfo.divisionId,
        organizationName: projectInfo.divisionName,
      });

      // 申請部署（保存済み値を復元、null/undefined の場合のみログインユーザーの主所属で補完）
      setSelectedRegistrantDivisionId(projectInfo.registrantDivisionId ?? loginUserDivisionId);

      // 記入者名
      setEmployeeData({
        divisionId: "",
        divisionName: "",
        employeeId: projectInfo.registrantEmployeeId,
        employeeName: projectInfo.registrantEmployeeName,
      });

    } else {
      // データ取得失敗の場合
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.projectFetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      console.error(`Failed to call API getProjectById. errors = ${JSON.stringify(response?.errors)}`);
      setHasfetchException(true);
      return;
    }
    if (response && response.data && response.data.getPartNo) {
      // データ取得成功の場合
      if (response.data.getPartNo.length < 1) {
        // パーツNoが空っぽなら、新規入力用に１つ追加
        const newPartNoInfo: PartNo[] = [{ partNo: "" }];
        response.data.getPartNo = newPartNoInfo;
      }

      setProjectPartNo(response.data.getPartNo);

      // DB登録済みPartNoのスナップショットを設定（トグル活性判定用、No.435対応）
      const loadedPartNos: string[] = response.data.getPartNo
        .map((p: PartNo) => p.partNo?.trim() || "")
        .filter((p: string) => p !== "");
      setRegisteredPartNos(loadedPartNos);
      setSavedPartNoSnapshot(loadedPartNos);

      // displayPartNoが設定済みの行は、初期表示時点で自動的にトグルON状態にする（No.435対応）
      const initialToggleKeys: string[] = response.data.getPartNo
        .filter((p: PartNo) => !!p.displayPartNo)
        .map((p: PartNo) => p.partNo?.trim() || "");
      setDisplayPartNoToggleKeys(initialToggleKeys);

      // 過去Projectと紐づいている場合、そのPartNoを編集不可に設定
      const projectInfo = response.data.getProjectById;
      if (projectInfo?.pastProjectId) {
        try {
          // 紐づけ時点の承認済み振り返り日（保存済みの pastReviewDate、申請のみの場合は null）
          const pastReviewDate = projectInfo.pastReviewDate ?? null;

          const pastPartNoResponse = await WebAPI.request({
            query: `
              query getPartNo($projectId: String, $hasOtherPart: Boolean, $reviewDate: String, $referencingProjectId: String) {
                getPartNo(projectId: $projectId, hasOtherPart: $hasOtherPart, reviewDate: $reviewDate, referencingProjectId: $referencingProjectId) {
                  partNo
                }
              }`,
            variables: {
              projectId: projectInfo.pastProjectId,
              hasOtherPart: false,
              ...(pastReviewDate && { reviewDate: pastReviewDate }),
              referencingProjectId: projectId
            }
          });
          if (pastPartNoResponse?.data?.getPartNo) {
            const pastPartNos: string[] = pastPartNoResponse.data.getPartNo
              .map((p: PartNo) => p.partNo?.trim() || "")
              .filter((p: string) => p !== "");
            const currentPartNos: string[] = response.data.getPartNo.map((p: PartNo) => p.partNo?.trim() || "");
            // 現在のPartNoと一致する過去ProjectのPartNoを編集不可に設定
            const lockedParts = pastPartNos.filter((p: string) => currentPartNos.includes(p));
            setLockedPartNos(lockedParts);
          } else {
            // API成功だがデータなしの場合、安全側に倒して全PartNoをロック
            const allPartNos: string[] = response.data.getPartNo
              .map((p: PartNo) => p.partNo?.trim() || "")
              .filter((p: string) => p !== "");
            setLockedPartNos(allPartNos);
          }
        } catch (e) {
          console.error("Failed to fetch past project PartNo for locking", e);
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.BasicInfo.message.pastProjectPartNoFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          // 安全側に倒して全PartNoをロック
          const allPartNos: string[] = response.data.getPartNo
            .map((p: PartNo) => p.partNo?.trim() || "")
            .filter((p: string) => p !== "");
          setLockedPartNos(allPartNos);
        }
      }

    } else {
      // データ取得失敗の場合
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.projectPartNoFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      console.error(`Failed to call API getPartNo. errors = ${JSON.stringify(response?.errors)}`);
      setHasfetchException(true);
    }
  }


  /**
   * 過去Project情報を取得する。（パーツNoに紐づくプロジェクト情報）
   * @returns 過去Project情報
   */
  const fetchPastProject = async () => {
    try {
      const payload = {
        query: `
          query {
            getProjectByPartNo(partNoList:${JSON.stringify(projectPartNo.map(p => p.partNo))}) {
              projectId
              projectName
              revisedCustomerProductNameByRevision
              purpose
              customerNationality
              projectStartDate
              partNo
              createDate
              approvalStatus
              applicationDay
              approveDay
              customerId
              customerProductId
              customerProductName
              mmProductCategoryId
            }
          }
        `
      };
      const response = await WebAPI.request(payload);

      if (response?.data?.getProjectByPartNo) {
        let pastProjectData: PastProjectData[] = response?.data?.getProjectByPartNo;

        pastProjectData = pastProjectData.filter((p, _) => {
          if (p.projectId === projectId) {
            // 自プロジェクトは対象外
            return false;
          }

          // 承認済み（approvalStatus === "90"）以外は対象外（課題No.98）
          if (p.approvalStatus !== ApproveStatus.ApprovalComplete.code) {
            return false;
          }

          // 顧客名
          // 顧客名: 自由入力データのフォールバック追加
          p.customerName = (customers?.find(c => c.customerId === p.customerId)?.customerName)
            || splitEscapedList(p.customerId)[0]  // フォールバック: カンマ区切り(エスケープ対応)の先頭を顧客名として使用
            || "";

          // 顧客製品名
          p.customerProductName = p.revisedCustomerProductNameByRevision || p.customerProductName;

          return true;
        });

        // ソート:承認日 降順
        pastProjectData = pastProjectData.sort((x, y) => compareString(x, y, "approveDay", "desc"));

        return pastProjectData;
      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BasicInfo.message.pastProjectFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getProjectByPartNo. errors = ${JSON.stringify(response?.errors)}`);
      }

      return null;
    } catch (e) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.pastProjectFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Exception to call API getProjectByPartNo. e = }`, e);
      return null;
    }
  }

  /**
   * PartNo前方一致で過去プロジェクト情報を検索する。
   * @param partNoPrefix PartNo前方一致検索文字列
   * @returns { projects: プロジェクト配列, hasMore: 100件超過フラグ }
   */
  const fetchPastProjectByPrefix = async (partNoPrefix: string): Promise<{ projects: PastProjectData[] | null, hasMore: boolean }> => {
    try {

      console.log(`fetchPastProjectByPrefix START(partNoPrefix = ${partNoPrefix})`);

      if (!partNoPrefix || partNoPrefix.trim() === "") {
        return { projects: [], hasMore: false };
      }

      const payload = {
        query: `
          query getProjectByPartNoPrefix($partNoPrefix: String) {
            getProjectByPartNoPrefix(partNoPrefix: $partNoPrefix) {
              projects {
                projectId
                projectName
                revisedCustomerProductNameByRevision
                purpose
                customerNationality
                projectStartDate
                partNo
                partNoDetails {
                  partNo
                  displayPartNo
                }
                createDate
                approvalStatus
                applicationDay
                approveDay
                customerId
                customerProductId
                customerProductName
                mmProductCategoryId
                reviewDate
              }
              hasMore
            }
          }
        `,
        variables: { partNoPrefix }
      };
      const response = await WebAPI.request(payload);

      if (response?.data?.getProjectByPartNoPrefix) {
        const result = response.data.getProjectByPartNoPrefix;
        let pastProjectData: PastProjectData[] = result.projects || [];
        const hasMore: boolean = result.hasMore || false;

        pastProjectData = pastProjectData.filter((p, _) => {
          if (p.projectId === projectId) {
            // 自プロジェクトは対象外
            return false;
          }

          // 承認済み（approvalStatus === "90"）以外は対象外（課題No.98）
          // if (p.approvalStatus !== ApproveStatus.ApprovalComplete.code) {
          //   return false;
          // }

          // 顧客名
          // 顧客名: 自由入力データのフォールバック追加
          p.customerName = (customers?.find(c => c.customerId === p.customerId)?.customerName)
            || splitEscapedList(p.customerId)[0]  // フォールバック: カンマ区切り(エスケープ対応)の先頭を顧客名として使用
            || "";

          // 顧客製品名
          p.customerProductName = p.revisedCustomerProductNameByRevision || p.customerProductName;

          return true;
        });

        // ソート:承認日 降順
        pastProjectData = pastProjectData.sort((x, y) => compareString(x, y, "approveDay", "desc"));

        return { projects: pastProjectData, hasMore };
      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BasicInfo.message.pastProjectFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getProjectByPartNoPrefix. errors = ${JSON.stringify(response?.errors)}`);
      }

      return { projects: null, hasMore: false };
    } catch (e) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.pastProjectFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Exception to call API getProjectByPartNoPrefix. e = `, e);
      return { projects: null, hasMore: false };
    }
  }

  /**
   * 顧客情報を取得する。
   */
  const fetchCustomers = async () => {
    const response = await fetchDynamoDB(t('features.InvestmentScreening.BasicInfo.label.customerInfo'), "getCustomer", ["customerId", "customerName", "customerFullName"]);
    setCustomers(response);
    if ((response?.length ?? 0) < 1) {
      setHasfetchException(true);
    }
  };

  /**
   * 顧客製品情報を取得する。
   * @param customerId 顧客ID
   */
  const fetchCustomerProducts = async (customerId: string) => {
    const response = await fetchDynamoDBWithVariables(t('features.InvestmentScreening.BasicInfo.label.customerProductInfo'), "getCustomerProductByCustomerId", "$customerId: String", "customerId: $customerId", ["customerProductId", "customerProductName", "endUserId", "endUserName"], { customerId });
    setSelectedCustomerProducts(response);
    setFilteredCustomerProducts(response);
    return (response?.length ?? 0) >= 1;
  };

  /**
   * 案件情報を取得する。
   * @param customerId 顧客ID
   */
  const fetchOpportunities = async (customerId: string) => {
    const response = await fetchDynamoDBWithVariables(t('features.InvestmentScreening.BasicInfo.label.opportunityInfo'), "getOpportunityByCustomerId", "$customerId: String", "customerId: $customerId",
      ["opportunityId", "opportunityName", "applicationCategory", "customerProductId", "MMProductCategoryId", "MMProductCategoryName",
        "MMISourceId", "MMISourceName", "MMISourceCurrencyId", "endUserId", "endUserName"], { customerId });

    setSelectedCustomerOpportunities(response);
    setFilteredCustomerOpportunities(response);
    return (response?.length ?? 0) >= 1;
  };

  /**
   * 製品カテゴリ情報（全階層）を取得する。顧客業界の選択肢として使用。
   */
  const getProductCategory = async () => {
    const response: ProductCategoryType[] = await fetchDynamoDB(t('features.InvestmentScreening.BasicInfo.label.productCategoryInfo'), "getProductCategory", ["productCategoryId", "productCategoryName", "hierarchy", "upperLevelCategoryId"]);
    setProductCategories(response);
  };

  /**
   * MMI製品情報を取得する。
   */
  const fetchMMIProductCategories = async () => {
    const response = await fetchDynamoDB(t('features.InvestmentScreening.BasicInfo.label.mmiProductInfo'), "getMMProduct", ["MMProductCategoryId", "MMProductCategoryName"]);
    setMMIProductCategories(response);
  };

  /**
   * 会社/工場情報を取得する。
   * M_CEAS_COMPANY_PLANT から取得
   */
  const fetchCompanyPlants = async () => {
    // DynamoDB: M_CEAS_COMPANY_PLANT から会社/工場情報を取得
    const response: CompanyPlantType[] = await fetchDynamoDB(t('features.InvestmentScreening.BasicInfo.label.companyPlantInfo'), "getCompanyPlant", ["companyPlantId", "companyPlantName", "companyPlantNameEn", "parentCompanyPlantId", "companyCode", "currencyId", "locationCountry", "areaCode", "baseInterestRate", "taxRate", "deleted"]);
    setCompanyPlants(response);

    // 複数IDからtempSelectedCompanyPlantsを復元
    const ids = (projectBasicInfo?.companyPlantId ?? "").split(",").map(id => id.trim()).filter(Boolean);
    if (ids.length > 0) {
      const restored = ids.map(id => response.find(cp => cp.companyPlantId === id)).filter((cp): cp is CompanyPlantType => !!cp);
      setTempSelectedCompanyPlants(restored);
    }

    // 単一選択の後方互換：先頭IDを selectedCompanyPlant / tempSelectedCompanyPlantPath に反映
    const firstId = ids[0];
    if (firstId) {
      const companyPlant = response.find((cp) => cp.companyPlantId === firstId);
      if (companyPlant) {
        // 既存選択から階層パスを復元
        const buildHierarchyPath = (targetCp: CompanyPlantType) => {
          const path: CompanyPlantType[] = [];
          let current: CompanyPlantType | undefined = targetCp;
          while (current) {
            path.unshift(current);
            if (!current.parentCompanyPlantId) break;
            current = response.find((cp) => cp.companyPlantId === current?.parentCompanyPlantId);
          }
          return path;
        };

        const hierarchyPath = buildHierarchyPath(companyPlant);
        setTempSelectedCompanyPlantPath(hierarchyPath);
        setTempSelectedCompanyPlant(companyPlant);

        // モーダルオープン時の Accordion 初期展開インデックスを設定
        const topItem = hierarchyPath[0];
        if (topItem) {
          setAccordionIndex(calcAccordionIndex(topItem, response));
        }
      }
    }
  };

  /**
   * 事業部情報を取得する。
   * M_CEAS_ACCOUNTING_ORGANIZATION から適用年度を指定して取得
   */
  const fetchDivisions = async () => {
    // 申請月度をそのまま、適用年度として扱う
    const fiscalPeriod = projectBasicInfo.applicationMonth;

    const payload = {
      query: `
        query getAccountingOrganization($hasDeleted: Boolean, $fiscalPeriod: String) {
          getAccountingOrganization(hasDeleted: $hasDeleted, fiscalPeriod: $fiscalPeriod) {
            organizationId
            organizationName
            organizationNameEn
            parentOrganizationId
            parentOrganizationName
            fiscalPeriod
          }
        }
      `,
      variables: {
        hasDeleted: false,
        fiscalPeriod: fiscalPeriod || undefined,
      },
    };

    const result = await WebAPI.request(payload);
    const response: OrganizationWithHierarchyType[] = result?.data?.getAccountingOrganization ?? [];

    if (!result?.data?.getAccountingOrganization) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.divisionInfoFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API getAccountingOrganization. errors = ${JSON.stringify(result?.errors)}`);
    } else if (result?.data?.getAccountingOrganization.length === 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.divisionInfoNotRegistered'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }

    setDivisions(response);
    if (selectedDivision && selectedDivision.organizationId) {
      const organization = response.find((org) => org.organizationId === selectedDivision.organizationId);
      if (organization) {
        setSelectedDivision(organization);

        // 既存選択から階層パスを復元
        const buildHierarchyPath = (targetOrg: OrganizationWithHierarchyType) => {
          const path: OrganizationWithHierarchyType[] = [];
          let current: OrganizationWithHierarchyType | undefined = targetOrg;

          while (current) {
            path.unshift(current);
            if (current.parentOrganizationId === "organization00000") {
              break;
            }
            current = response.find((org) => org.organizationId === current?.parentOrganizationId);
          }

          return path;
        };

        const hierarchyPath = buildHierarchyPath(organization);

        // hierarchyPath = [事業本部, 部門, 事業部, ビジネス小計, ビジネス, 製品]
        setTempSelectedDivisionPath(hierarchyPath);
        setTempSelectedDivision(organization);
      }
    }
  };

  /**
   * トピックス情報を取得する。
   */
  const fetchTopicInfo = async () => {
    const columns: string[] = [

    ];
    const response = await fetchDynamoDB(t('features.InvestmentScreening.BasicInfo.label.topicsInfo'), "getOpportunityById", columns);
    setDivisions(response);
  };

  /**
   * 指定したプロジェクトIDの通貨情報を取得する。
   * @param projectId プロジェクトID
   * @returns 通貨情報
   */
  const getCurrencyById = async (projectId: string): Promise<Currency[]> => {
    const currencyData = await fetchDynamoDBWithVariables(
      t('features.InvestmentScreening.BasicInfo.label.currencyInfoById'),
      "getCurrencyById",
      "$projectId: String",
      "projectId: $projectId",
      ["currencyId", "currencyName", "exchangeRate", "order", "updateDate"],
      { projectId: projectId }
    );
    return currencyData;
  }

  /**
   * 営業支援から実勢レート（月次最新）を取得する。
   * @returns 実勢レート情報
   */
  const getLatestCurrencyFromSas = async (): Promise<LatestCurrency[]> => {
    const data = await fetchDynamoDB(t('features.InvestmentScreening.BasicInfo.label.actualRateFromSas'), "getLatestCurrencyFromSas", ["currencyId", "currencyName", "exchangeRate", "rateMonth"]);
    return data;
  }

  /**
   * 営業支援から計画レート（見込み）を取得する。
   * @param rateYear 対象年
   * @returns 計画レート情報
   */
  const getCurrencyProspectFromSas = async (rateYear: string): Promise<CurrencyProspect[]> => {
    const data = await fetchDynamoDBWithVariables(
      t('features.InvestmentScreening.BasicInfo.label.planRateFromSas'),
      "getCurrencyProspectFromSas",
      "$rateYear: String",
      "rateYear: $rateYear",
      ["currencyId", "currencyName", "exchangeRate", "rateYear", "version"],
      { rateYear: rateYear }
    );
    return data;
  }

  /**
   * 指定したAPIを実行してデータを取得する。
   * @param dataName データ論理名
   * @param apiName API名称
   * @param columns 取得カラム
   * @returns 取得データ
   */
  const fetchDynamoDB = async (dataName: string, apiName: string, columns: string[]) => {
    return await fetchDynamoDBColumnString(dataName, apiName, columns.join('\n'));
  }
  /**
   * 指定したAPIを実行してデータを取得する。
   * @param dataName データ論理名
   * @param apiName API名称
   * @param columns 取得カラム
   * @returns 取得データ
   */
  /**
   * 指定したAPIをvariablesパラメータ方式で実行してデータを取得する。
   * @param dataName データ論理名
   * @param apiName API名称（パラメータなし）
   * @param variablesDef 変数定義（例: "$projectId: String"）
   * @param args 引数（例: "projectId: $projectId"）
   * @param columns 取得カラム
   * @param variables 変数の値
   * @returns 取得データ
   */
  const fetchDynamoDBWithVariables = async (dataName: string, apiName: string, variablesDef: string, args: string, columns: string[], variables: Record<string, any>) => {
    const columnsStr = columns.join('\n');
    const payload = {
      query: `
           query ${apiName}(${variablesDef}) {
            ${apiName}(${args}) {
               ${columnsStr}
             }
           }`,
      variables: variables
    };
    const response = await WebAPI.request(payload);

    if (response && response.data && response.data.hasOwnProperty(apiName) && response.data[apiName]) {
      return response.data[apiName];
    } else {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.fetchFailedGeneric', { name: dataName }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API ${apiName}. errors = ${JSON.stringify(response?.errors)}`);
      return [];
    }
  };

  const fetchDynamoDBColumnString = async (dataName: string, apiName: string, columns: string) => {
    const payload = {
      query: `
           query {
            ${apiName} {
               ${columns}
             }
           }`
    };
    const response = await WebAPI.request(payload);

    apiName = apiName.replace(/\(.*\)$/, '');

    if (response && response.data && response.data.hasOwnProperty(apiName) && response.data[apiName]) {
      // 正常に取得できた場合
      return response.data[apiName];
    } else {
      // データ取得失敗の場合
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.fetchFailedGeneric', { name: dataName }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      console.error(`Failed to call API ${apiName}. errors = ${JSON.stringify(response?.errors)}`);
      return [];
    }
  };

  /**
   * 申請月度から適用年度を計算する。
   * 4月以降は当年度、3月以前は前年度を返す。
   * @param applicationMonth 申請月度（YYYY-MM形式）
   * @returns 適用年度（YYYY-MM形式、4月始まり）
   */
  const getFiscalPeriod = (applicationMonth: string): string => {
    if (!applicationMonth || applicationMonth.length < 7) {
      return "";
    }
    const [yearStr, monthStr] = applicationMonth.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    // 4月以降は当年度、3月以前は前年度
    const fiscalYear = month >= 4 ? year : year - 1;
    return `${fiscalYear}-04`;
  };

  /**
   * 金利マスタから短期金利を取得する。
   * @param areaCode 地域コード
   * @param fiscalPeriod 適用年度（YYYY-MM形式）
   * @returns 短期金利（取得失敗時はnull）
   */
  const fetchInterestRate = async (areaCode: string, fiscalPeriod: string): Promise<number | null> => {
    if (!areaCode || !fiscalPeriod) {
      return null;
    }

    const payload = {
      query: `
        query getInterestRateMasterById($areaCode: String!, $fiscalPeriod: String!) {
          getInterestRateMasterById(areaCode: $areaCode, fiscalPeriod: $fiscalPeriod) {
            areaCode
            fiscalPeriod
            shortTermRate
          }
        }
      `,
      variables: {
        areaCode: areaCode,
        fiscalPeriod: fiscalPeriod,
      },
    };

    try {
      const response = await WebAPI.request(payload);
      if (response?.data?.getInterestRateMasterById) {
        const interestRate = response.data.getInterestRateMasterById as InterestRateMaster;
        return interestRate.shortTermRate ?? null;
      }
      console.warn(`金利マスタが見つかりませんでした。areaCode=${areaCode}, fiscalPeriod=${fiscalPeriod}`);
      return null;
    } catch (error) {
      console.error(`金利マスタ取得エラー: ${error}`);
      return null;
    }
  };

  /**
   * 指定した地域コードの最新金利を取得する。
   * @param areaCode 地域コード
   * @returns { rate: 金利, fiscalPeriod: 年度 } または 金利マスタ または null
   */
  const fetchLatestInterestRate = async (areaCode?: string): Promise<{ rate: number; fiscalPeriod: string } | InterestRateMaster[] | null> => {

    const payload = {
      query: `
        query getInterestRateMaster($hasDeleted: Boolean) {
          getInterestRateMaster(hasDeleted: $hasDeleted) {
            areaCode
            areaName {${langFields}}
            fiscalPeriod
            shortTermRate
          }
        }
      `,
      variables: {
        hasDeleted: false,
      },
    };

    try {
      const response = await WebAPI.request(payload);
      if (response?.data?.getInterestRateMaster) {
        const allRates = response.data.getInterestRateMaster as InterestRateMaster[];

        if (!areaCode) {
          return allRates;
        }

        // 指定したareaCodeのデータをフィルター＆最新年度でソート
        const filteredRates = allRates
          .filter(r => r.areaCode === areaCode && r.shortTermRate !== null && r.shortTermRate !== undefined)
          .sort((a, b) => (b.fiscalPeriod || "").localeCompare(a.fiscalPeriod || ""));

        if (filteredRates.length > 0) {
          const latest = filteredRates[0];
          return {
            rate: latest.shortTermRate!,
            fiscalPeriod: latest.fiscalPeriod || "",
          };
        }
      }
      console.warn(`最新金利が見つかりませんでした。areaCode=${areaCode}`);
      return null;
    } catch (error) {
      console.error(`最新金利取得エラー: ${error}`);
      return null;
    }
  };

  /**
   * 顧客選択確定処理（SelectorButtonから選択時）
   * 顧客ID連携 + 案件・顧客製品情報の取得を行う
   * @param selectedItem 選択項目
   */
  const handleConfirmSelection = async (selectedItem: Record<string, any> | null | undefined) => {
    if (!selectedItem) return;

    // 顧客選択確定
    setSelectedCustomer(selectedItem as CustomerType);

    // テキスト入力欄にもセット
    changeProjectBasicInfo("customerName", selectedItem.customerName || "");
    changeProjectBasicInfo("customerFullName", selectedItem.customerFullName || "");

    // プロジェクト情報（RDS）の顧客IDに反映
    changeProjectBasicInfo("customerId", selectedItem.customerId);

    // 顧客製品リセット
    setSelectedCustomerProduct(null);
    changeProjectBasicInfo("customerProductId", null);
    changeProjectBasicInfo("customerProductName", "");
    setFilteredCustomerProducts([]);

    // 案件リセット
    setSelectedOpportunity(null);
    changeProjectBasicInfo("opportunityId", null);
    changeProjectBasicInfo("opportunityName", "");
    setFilteredCustomerOpportunities([]);
    setTopicsInfo(null);

    // 案件情報取得
    await fetchOpportunities(selectedItem.customerId);

    // 顧客製品情報取得
    await fetchCustomerProducts(selectedItem.customerId);
  };

  /**
   * 顧客名手動入力時の処理
   * テキスト入力時は選択状態をクリアしてテキスト保存モードに切り替え
   * @param selectedItem 選択項目
   */
  const handleSelectCustomer = (selectedItem: Record<string, any> | null | undefined) => {
    if (!selectedItem) return;
    changeProjectBasicInfo("customerName", selectedItem.customerName || "");
    changeProjectBasicInfo("customerFullName", selectedItem.customerFullName || "");
  };

  /**
   * 案件選択確定処理（SelectorLinkから選択時）
   * @param selectedItem 選択項目
   */
  const handleSelectOpportunity = (selectedItem: Record<string, any> | null | undefined) => {
    // 案件選択確定
    setSelectedOpportunity(selectedItem);
    // プロジェクト情報（RDS）の案件IDに反映
    changeProjectBasicInfo("opportunityId", selectedItem?.opportunityId);
    // 案件名テキスト欄にもセット
    changeProjectBasicInfo("opportunityName", selectedItem?.opportunityName || "");
    // 取得済みの顧客製品情報をさらに選択した案件情報の顧客製品IDで絞り込む。
    setFilteredCustomerProducts(selectedCustomerProducts.filter(m => {
      return m.customerProductId === selectedItem?.customerProductId;
    }));
    // MMI製品分類IDと名称があれば、MMI製品分類の選択値も更新
    changeProjectBasicInfo("mmProductCategoryId", selectedItem?.MMProductCategoryId ?? "");
    setSelectedMMIProductCategory({
      MMProductCategoryId: selectedItem?.MMProductCategoryId ?? "",
      MMProductCategoryName: selectedItem?.MMProductCategoryName ?? "",
    });
    // 用途
    changeProjectBasicInfo("applicationCategory", selectedItem?.applicationCategory);
  };

  /**
   * 顧客製品選択確定処理（SelectorLinkから選択時）
   * @param selectedItem 選択項目
   */
  const handleSelectCustomerProduct = async (selectedItem: Record<string, any> | null | undefined) => {
    // 顧客製品選択確定
    setSelectedCustomerProduct(selectedItem);
    // プロジェクト情報（RDS）の顧客製品IDに反映
    changeProjectBasicInfo("customerProductId", selectedItem?.customerProductId);
    // 顧客製品名テキスト欄にもセット
    changeProjectBasicInfo("customerProductName", selectedItem?.customerProductName || "");
    // 取得済みの案件情報をさらに選択した顧客製品情報の顧客製品IDで絞り込む。
    setFilteredCustomerOpportunities(selectedCustomerOpportunities.filter(m => m.customerProductId === selectedItem?.customerProductId));
    // EndUser名を連動セット（営業支援 r_customer_product.enduser_id → M_END_USER）
    if (selectedItem?.endUserName) {
      setEndUserList([selectedItem.endUserName]);
    }
    // 顧客業界を連動セット（顧客製品の全階層カテゴリIDを取得）
    if (selectedItem?.customerProductId) {
      const response = await WebAPI.request({
        query: `
          query getCategoryHierarchyByCustomerProductId($customerProductId: String) {
            getCategoryHierarchyByCustomerProductId(customerProductId: $customerProductId)
          }`,
        variables: { customerProductId: selectedItem.customerProductId }
      });
      const categoryIds: string[] | null = response?.data?.getCategoryHierarchyByCustomerProductId;
      if (categoryIds && categoryIds.length > 0) {
        changeProjectBasicInfo("customerIndustry", categoryIds[0]);
        const hierarchyNames = categoryIds
          .map(id => productCategories.find(c => c.productCategoryId === id)?.productCategoryName)
          .filter(Boolean)
          .join(" / ");
        changeProjectBasicInfo("customerIndustryHierarchy", hierarchyNames);
      }
    }
  };

  /**
   * End User選択確定処理（顧客マスタから選択）
   * 空行があればそこにセット、なければ新しい行を追加
   * @param selectedItem 選択項目
   */
  const handleSelectEndUser = (selectedItem: Record<string, any> | null | undefined) => {
    if (!selectedItem?.customerName) return;
    const name = selectedItem.customerName;
    const emptyIndex = endUserList.findIndex((v) => v.trim() === "");
    if (emptyIndex >= 0) {
      changeEndUser(emptyIndex, name);
    } else {
      setEndUserList((prev) => [...prev, name]);
    }
  };

  /**
   * MMI製品分類選択確定処理
   * @param selectedItem 選択項目
   */
  const handleSelectMMIProductCategory = (selectedItem: Record<string, any> | null | undefined) => {
    // MMI製品分類選択確定
    setSelectedMMIProductCategory(selectedItem as MMIProductCategoryType);

    // プロジェクト情報（RDS）の MMI製品分類IDに反映
    changeProjectBasicInfo("mmProductCategoryId", selectedItem?.MMProductCategoryId);
  };

  /**
   * 会社/工場選択確定処理（複数選択対応）
   * 通貨/税率が混在する場合はローカル通貨/税率選択モーダルを表示する。
   * 混在しない場合はそのまま確定処理を行う。
   */
  const handleSelectCompanyPlant = async () => {
    if (tempSelectedCompanyPlants.length === 0) return;

    // 通貨IDの重複排除
    const uniqueCurrencyIds = [...new Set(tempSelectedCompanyPlants.map(cp => cp.currencyId ?? "").filter(Boolean))];
    // 税率の重複排除（null/undefined は 0 として扱う）
    const uniqueTaxRates = [...new Set(tempSelectedCompanyPlants.map(cp => cp.taxRate ?? 0))];
    // 金利マスタ地域コードの重複排除
    const uniqueActiveAreaCodes = [...new Set(tempSelectedCompanyPlants.map(cp => cp.areaCode ?? "").filter(Boolean))];

    const isCurrencyMixed = uniqueCurrencyIds.length > 1;
    const isTaxRateMixed = uniqueTaxRates.length > 1;
    const isAreaCodesMixed = uniqueActiveAreaCodes.length > 1;

    if (isCurrencyMixed || isTaxRateMixed || isAreaCodesMixed) {
      // 混在あり：ローカル通貨/税率選択モーダルを開く
      setSelectedActiveCurrencyId(uniqueCurrencyIds[0] ?? "");
      setSelectedActiveTaxRate(uniqueTaxRates[0] ?? null);
      setSelectedActiveAreaCode(uniqueActiveAreaCodes[0] ?? "");

      if (isAreaCodesMixed) {
        // 金利マスタデータ取得
        const latestInterestRateData = await fetchLatestInterestRate() as InterestRateMaster[];
        const uniqueActiveAreaData = Array.from(
          new Map(
            latestInterestRateData.map(l => [l.areaCode, { areaCode: l.areaCode!, areaName: l.areaName! }])
          ).values()
        );

        setAreaData(uniqueActiveAreaData);
      }
      setIsCurrencyTaxModalOpen(true);
      return;
    }

    // 混在なし：そのまま確定
    const activeCurrencyId = uniqueCurrencyIds[0] ?? "";
    const activeTaxRate = uniqueTaxRates.length > 0 ? uniqueTaxRates[0] : null;
    const activeAreaCode = uniqueActiveAreaCodes[0] ?? "";
    applyCompanyPlantSelection(activeCurrencyId, activeTaxRate, activeAreaCode);
  };

  /**
   * 会社/工場選択を確定してプロジェクト情報に反映する。
   * @param activeCurrencyId 有効な通貨ID
   * @param activeTaxRate 有効な税率
   * @param activeAreaCode 有効な金利マスタ地域コード
   */
  const applyCompanyPlantSelection = (activeCurrencyId: string, activeTaxRate: number | null, activeAreaCode: string) => {
    // 先頭を代表として selectedCompanyPlant（後方互換用）に設定
    const firstCp = tempSelectedCompanyPlants[0];

    // companyPlantId：カンマ区切りで複数IDを保持
    const companyPlantIdStr = tempSelectedCompanyPlants.map(cp => cp.companyPlantId ?? "").filter(Boolean).join(",");
    changeProjectBasicInfo("companyPlantId", companyPlantIdStr);

    // CompanyPlantSnapshotListType を生成
    const snapshotItems: CompanyPlantSnapshotType[] = tempSelectedCompanyPlants.map(cp => {
      const isCompany = !cp.parentCompanyPlantId;
      let companyName = "";
      let companyNameEn = "";
      let plantName = "";
      let plantNameEn = "";
      if (isCompany) {
        companyName = cp.companyPlantName ?? "";
        companyNameEn = cp.companyPlantNameEn ?? "";
      } else {
        const parentCompany = (companyPlants ?? []).find(c => c.companyPlantId === cp.parentCompanyPlantId);
        if (parentCompany) {
          companyName = parentCompany.companyPlantName ?? "";
          companyNameEn = parentCompany.companyPlantNameEn ?? "";
          plantName = cp.companyPlantName ?? "";
          plantNameEn = cp.companyPlantNameEn ?? "";
        } else {
          companyName = cp.companyPlantName ?? "";
          companyNameEn = cp.companyPlantNameEn ?? "";
        }
      }
      return {
        companyName, companyNameEn,
        plantName, plantNameEn,
        currencyId: cp.currencyId ?? "",
        locationCountry: cp.locationCountry ?? "",
        areaCode: cp.areaCode ?? "",
        taxRate: cp.taxRate ?? null,
      };
    });

    const snapshotList: CompanyPlantSnapshotListType = {
      items: snapshotItems,
      activeCurrencyId,
      activeTaxRate,
      activeAreaCode
    };
    changeProjectBasicInfo("companyPlantSnapshot", JSON.stringify(snapshotList));

    onCompanyPlantModalClose();
  };

  /**
   * 事業部選択確定処理
   * @param selectedItem 選択項目
   */
  const handleSelectDivision = () => {
    // 事業部選択確定
    setSelectedDivision(tempSelectedDivision);

    // プロジェクト情報（RDS）の 事業部IDに反映
    changeProjectBasicInfo("divisionId", tempSelectedDivision?.organizationId ?? "");

    // 全階層を "/" 区切りで保存
    const hierarchyNames = tempSelectedDivisionPath.map(org => org.organizationName).join(" / ");
    changeProjectBasicInfo("divisionHierarchy", hierarchyNames);
    // 英語階層パス（organizationNameEn未設定の要素は日本語名にフォールバック）
    const hierarchyNamesEn = tempSelectedDivisionPath
      .map(org => getBilingualDisplayName(org, 'organizationName', 'organizationNameEn'))
      .join(" / ");
    changeProjectBasicInfo("divisionHierarchyEn", hierarchyNamesEn);

    onDivisionModalClose();
  };

  /**
   * 顧客業界選択確定処理
   * 選択された製品カテゴリの階層パスをcustomerIndustry/customerIndustryHierarchyに反映
   */
  const handleSelectCustomerIndustry = () => {
    if (tempSelectedIndustryPath.length === 0) return;

    // 大分類IDをcustomerIndustryにセット
    changeProjectBasicInfo("customerIndustry", tempSelectedIndustryPath[0].productCategoryId);

    // 全階層名を " / " 区切りでcustomerIndustryHierarchyにセット
    const hierarchyNames = tempSelectedIndustryPath.map(c => c.productCategoryName).join(" / ");
    changeProjectBasicInfo("customerIndustryHierarchy", hierarchyNames);

    onCustomerIndustryModalClose();
  };

  /**
   * 顧客業界選択モーダルを開く際に、既存の選択状態を復元する
   */
  const openCustomerIndustryModal = () => {
    // 既存のcustomerIndustryから選択パスを復元
    if (projectBasicInfo.customerIndustry && productCategories.length > 0) {
      const path: ProductCategoryType[] = [];
      // 大分類を探す
      const largeCategory = productCategories.find(
        c => c.productCategoryId === projectBasicInfo.customerIndustry && c.hierarchy === 1
      );
      if (largeCategory) {
        path.push(largeCategory);
        // customerIndustryHierarchyから中分類以降を復元
        if (projectBasicInfo.customerIndustryHierarchy) {
          const names = projectBasicInfo.customerIndustryHierarchy.split(" / ");
          let parentId = largeCategory.productCategoryId;
          for (let i = 1; i < names.length; i++) {
            const child = productCategories.find(
              c => c.upperLevelCategoryId === parentId && c.productCategoryName === names[i]
            );
            if (child) {
              path.push(child);
              parentId = child.productCategoryId;
            } else {
              break;
            }
          }
        }
      } else {
        // customerIndustryが大分類IDでない場合（中分類以降のIDの可能性）
        // customerIndustryHierarchyから復元を試みる
        if (projectBasicInfo.customerIndustryHierarchy) {
          const names = projectBasicInfo.customerIndustryHierarchy.split(" / ");
          let parentId: string | null = null;
          for (const name of names) {
            const category = productCategories.find(c => {
              if (parentId === null) return c.hierarchy === 1 && c.productCategoryName === name;
              return c.upperLevelCategoryId === parentId && c.productCategoryName === name;
            });
            if (category) {
              path.push(category);
              parentId = category.productCategoryId;
            } else {
              break;
            }
          }
        }
      }
      setTempSelectedIndustryPath(path);
    } else {
      setTempSelectedIndustryPath([]);
    }
    // フィルタ値をリセット
    setIndustryFilterValues(["", "", "", ""]);
    onCustomerIndustryModalOpen();
  };

  /**
   * 「最新の為替レート取得」ボタン押下時の処理
   * レート種別選択モーダルを開き、プレビュー用にレートを取得
   * @param e イベント情報
   */
  const handlePutCurrency = async (e: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedRateType(rateType);
    // ボタンのフォーカスを外す
    e.currentTarget.blur();
    // レート種別選択モーダルを開く
    onRateTypeModalOpen();

    // // プレビュー用にレートを取得
    // setIsLoadingPreview(true);
    // try {
    //   // 会計年度を算出（4月始まり：1〜3月は前年度）
    //   const now = new Date();
    //   const currentYear = (now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear()).toString();
    //   const [actualData, planData] = await Promise.all([
    //     getLatestCurrencyFromSas(),
    //     getCurrencyProspectFromSas(currentYear)
    //   ]);
    //   setPreviewActualRates(actualData);
    //   setPreviewPlanRates(planData);
    // } catch (error) {
    //   console.error('[handlePutCurrency] プレビュー用レート取得エラー:', error);
    // } finally {
    //   setIsLoadingPreview(false);
    // }
  }

  /**
   * 為替レート種別選択モーダルで「選択」ボタン押下時の処理
   */
  const handleRateTypeConfirm = () => {
    // 取得ボタンを押したとき
    setIsRateTypeConfirmClicked(true);

    if (!selectedRateType) return;

    // 選択されたレートをdbに保存するrateTypeにセット
    setRateType(selectedRateType);

    // モーダルを閉じる
    onRateTypeModalClose();

    // 既存PJの場合のみ通貨情報をDBに保存（新規PJは記入者確認時にputProjectBasicで保存）
    // if (projectId) {
    //   updateCurrencyAsync();
    // }
  }

  /**
   * 「確認者選択」ボタン押下時の処理
   */
  const handleConfirmAssignerSelection = async (e: React.MouseEvent<HTMLButtonElement>) => {

    // ボタンのフォーカスを外す
    e.currentTarget.blur();

    if (validate() === false) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.inputError'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // モーダルを開く
    onOpenAssignTabModal();
  };

  /**
   * 「記入者確認」ボタン押下時の処理
   */
  const handleClickRegistrantConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();

    if (!validate()) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.inputError'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // 担当者設定の必須チェック（未設定項目をまとめて表示）
    {
      const missingItems: string[] = [];
      const isRemandFlow = pagePermissions?.currentFlowId === WorkflowId.Remand;

      // 確認者
      if (!selectedConfirmorIds || selectedConfirmorIds.length === 0) {
        missingItems.push(t('features.InvestmentScreening.BasicInfo.label.confirmer'));
      } else if (isRemandFlow && !selectedConfirmorData) {
        missingItems.push(t('features.InvestmentScreening.BasicInfo.label.confirmer'));
      }

      // 承認者
      const hasApprover = approverAssignees.some(a => a.employees.length > 0);
      if (!hasApprover) {
        missingItems.push(t('features.InvestmentScreening.BasicInfo.label.approver'));
      } else if (isRemandFlow && !approverReconfirmed) {
        missingItems.push(t('features.InvestmentScreening.BasicInfo.label.approver'));
      }

      // CC（要の場合）
      if (projectBasicInfo.crossCheckRequired) {
        const checkedDepts = (projectBasicInfo.crossCheckDepartments ?? "").split(",").filter(v => v);
        const hasCCAssignee = checkedDepts.length > 0 && crossCheckAssignees.some(a => checkedDepts.includes(a.categoryId) && a.employees.length > 0);
        if (!hasCCAssignee) {
          missingItems.push(t('features.InvestmentScreening.BasicInfo.label.crossCheckAssignee'));
        } else if (isRemandFlow && !crossCheckReconfirmed) {
          missingItems.push(t('features.InvestmentScreening.BasicInfo.label.crossCheckAssignee'));
        }
      }

      // 資材担当（要の場合）
      if (projectBasicInfo.materialOfficerRequired) {
        const hasMOAssignee = materialOfficerAssignees.some(a => a.employees.length > 0);
        if (!hasMOAssignee) {
          missingItems.push(t('features.InvestmentScreening.BasicInfo.label.materialOfficerAssignee'));
        } else if (isRemandFlow && !materialOfficerReconfirmed) {
          missingItems.push(t('features.InvestmentScreening.BasicInfo.label.materialOfficerAssignee'));
        }
      }

      // 画面閲覧制限（「制限する」の場合）
      if (isViewRestrictionEnabled && projectBasicInfo.screenViewRestriction == null) {
        missingItems.push(t('features.InvestmentScreening.BasicInfo.label.screenViewRestriction'));
      }

      if (missingItems.length > 0) {
        const description = isRemandFlow
          ? (<>{t('features.InvestmentScreening.BasicInfo.label.remandReconfirmPrefix')}<br />{missingItems.join("、")}</>)
          : (<>{t('features.InvestmentScreening.BasicInfo.label.setFollowingItems')}<br />{missingItems.join("、")}</>);
        toast({
          title: TITLE,
          description,
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
        return;
      }
    }

    // 金利取得処理（取得失敗時は保存をブロック）
    const interestRateSuccess = await fetchAndSetInterestRate();
    if (!interestRateSuccess) {
      return;
    }

    // 事業部選択モーダルを一度も開いていない場合、selectedDivisionが
    // organizationNameEnを持たない仮データのままのため、マスタから最新化する
    if (!divisions) {
      await fetchDivisions();
    }

    onFinalConfirmationModalOpen();
  };

  /**
   * 金利マスタから基準金利を取得してスナップショットに設定する。
   * 該当年度のデータがない場合は最新金利を使用する。
   * @returns 金利取得に成功した場合true、失敗した場合false
   */
  const fetchAndSetInterestRate = async (): Promise<boolean> => {
    // 初期化
    setFetchedInterestRateInfo(null);

    // スナップショットからareaCodeを取得
    let snapshot: CompanyPlantSnapshotListType | null;
    try {
      snapshot = parseCompanyPlantSnapshot(projectBasicInfo.companyPlantSnapshot!);
      if (!snapshot) {
        throw Error(`Failed to parse companyPlantSnapshot`);
      }
    } catch (e) {
      console.error("Failed to parse companyPlantSnapshot:", e);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.companyPlantInfoFailed'),
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
      return false;
    }

    // areaCodeを取得
    const areaCode = snapshot.activeAreaCode;
    if (!areaCode) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.companyPlantNoAreaCode'),
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
      return false;
    }

    // 申請月から適用年度を算出
    const fiscalPeriod = getFiscalPeriod(projectBasicInfo.applicationMonth!);

    // 1. まず該当年度の金利を取得
    let interestRate = await fetchInterestRate(areaCode, fiscalPeriod);
    let isLatestUsed = false;
    let latestFiscalPeriod: string | undefined;

    // 2. 該当年度のデータがない場合は最新金利を取得
    if (interestRate === null) {
      const latestRate = await fetchLatestInterestRate(areaCode) as { rate: number; fiscalPeriod: string };
      if (latestRate) {
        interestRate = latestRate.rate;
        isLatestUsed = true;
        latestFiscalPeriod = latestRate.fiscalPeriod;

        // 最新金利を使用した旨をToast表示
        const targetFiscalYear = fiscalPeriod.split('-')[0];
        const latestFiscalYear = latestRate.fiscalPeriod.split('-')[0];
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BasicInfo.message.interestRateFallback', { targetYear: targetFiscalYear, latestYear: latestFiscalYear, rate: latestRate.rate }),
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
      } else {
        // 金利データが全くない場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BasicInfo.message.interestRateNotFound', { areaCode }),
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
        return false;
      }
    }

    // 3. 金利情報をstateに保存（モーダル表示用）
    setFetchedInterestRateInfo({
      rate: interestRate,
      fiscalPeriod: fiscalPeriod,
      isLatestUsed: isLatestUsed,
      latestFiscalPeriod: latestFiscalPeriod,
    });

    // 4. スナップショットを更新（areaCodeも保存して次回以降はマスタ参照不要に）
    const updatedSnapshot: CompanyPlantSnapshotType = {
      ...snapshot,
      areaCode: areaCode,
    };
    changeProjectBasicInfo("companyPlantSnapshot", JSON.stringify(updatedSnapshot));

    return true;
  };

  /**
   * 最終確認モーダルより保存処理を行う。
   */
  const handleFinalConfirmAndSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
    console.log("最終確認モーダルより保存処理");

    // ボタンのフォーカスを外す
    e.currentTarget.blur();

    // 必要なプロパティだけを抽出
    const filteredConfirmorData = selectedConfirmorData
      ? (selectedConfirmorData as FlowPersonData[]).map(({ flowId, employeeId, divisionId }) => ({
        flowId,
        employeeId,
        divisionId,
      }))
      : null;

    if (filteredConfirmorData) {
      if (projectBasicInfo.purpose === PurposeType.NewProduct.code) {
        // 「新製品」の場合、ワークフローデータが「生産性向上」なら「新製品製造」に振り替え
        filteredConfirmorData.filter(c => c.flowId === WorkflowId.SimulationProductivityImprovement).forEach(c => {
          c.flowId = WorkflowId.SimulationNewProduct;
        });
      } else {
        // 「増産」「更新・その他」「合理化」の場合、ワークフローデータが「新製品製造」なら「生産性向上」に振り替え
        filteredConfirmorData.filter(c => c.flowId === WorkflowId.SimulationNewProduct).forEach(c => {
          c.flowId = WorkflowId.SimulationProductivityImprovement;
        });
      }
    }

    // プロジェクト更新
    saveProjectBasic(filteredConfirmorData);


    //    if (!projectId || pagePermissions?.currentFlowId === WorkflowId.Remand) {
    //      // 入力担当者設定画面を表示して部署別に入力依頼をさせる。
    //      onOpenAssignTabModal();
    //    } else {
    //      // 更新時
    //      saveProjectBasic(null);
    //    }
    // 最終確認モーダルを閉じる。
    onFinalConfirmationModalClose();
  }

  /**
   * 確認者一覧をソートする。
   */
  const sortConfirmors = (confirmors: any[]) => {
    return confirmors.sort((a, b) => {
      const indexA = CONFIRMOR_SORT_ORDER.indexOf(a.divisionId);
      const indexB = CONFIRMOR_SORT_ORDER.indexOf(b.divisionId);
      return indexA - indexB;
    });
  };

  /**
   * 確認者一覧をグループ化する。
   */
  const groupConfirmorsByDivisionId = (confirmors: any[]) => {
    return confirmors.reduce((acc: any, curr: any) => {
      if (!acc[curr.divisionId]) {
        acc[curr.divisionId] = [];
      }
      acc[curr.divisionId].push(curr);
      return acc;
    }, {});
  };

  /**
   * 確認者一覧をソート、グループ化し、エントリ配列で返却。
   */
  const getGroupedAndSortedConfirmors = (confirmors: Confirmor[]) => {

    // confirmKbn を付与（空の employeeName をフィルタリング）
    const processedConfirmors = confirmors
      .filter((confirmor) => confirmor.employeeName?.trim())
      .map((confirmor) => ({
        ...confirmor,
        confirmKbn: getDivisionConfirmName(confirmor.divisionId) || "",
      }));
    // ソート
    const sortedConfirmors = sortConfirmors(processedConfirmors);
    // グループ化
    const groupedConfirmors = groupConfirmorsByDivisionId(sortedConfirmors);
    // [divisionId, employees[]] の形で返す
    return Object.entries(groupedConfirmors) as Array<[string, Confirmor[]]>;
  };

  /**
   * エラー情報を設定する。
   * @param name エラーとなったプロパティ名
   * @param errorMessage エラーメッセージ
   */
  const changeErrors = (name: string, errorMessage: string) => {
    setErrors((prevState) => ({
      ...prevState,
      [name]: { isError: true, errorMessage: `${errorMessage}` },
    }));
  };

  /**
   * 指定したプロパティのエラー情報をクリアする。
   * @param name クリアするプロパティ名
   */
  const clearErrors = (name: string) => {
    setErrors((prevState) => ({
      ...prevState,
      [name]: { isError: false, errorMessage: "" },
    }));
  };


  /**
   * バリデーションを行う。
   */
  const validate = () => {
    // 必須入力チェック
    const required = (value: string | undefined) => value?.trim() ?? "" !== "";

    // エラー情報初期化
    setErrors(JSON.parse(JSON.stringify(defaultErrors)));

    let isError = false;

    // 必須：設備投資PROJECT名
    if (!required(projectBasicInfo.projectName ?? "")) {
      changeErrors("projectName", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.projectName')));
      isError = true;
    }

    // 必須：顧客名（自由入力テキストのため customerName でバリデーション）
    if (!required(projectBasicInfo.customerName ?? "")) {
      changeErrors("customerId", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.customerName')));
      isError = true;
    }

    // 必須：正式社名
    if (!required(projectBasicInfo.customerFullName ?? "")) {
      changeErrors("customerFullName", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.customerFullName')));
      isError = true;
    }

    // 必須：顧客製品名（自由入力テキストのため customerProductName でバリデーション）
    if (!required(projectBasicInfo.customerProductName ?? "")) {
      changeErrors("customerProductId", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.customerProductName')));
      isError = true;
    }

    // 必須：案件名（自由入力テキストのため opportunityName でバリデーション）
    if (!required(projectBasicInfo.opportunityName ?? "")) {
      changeErrors("opportunityId", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.opportunityName')));
      isError = true;
    }

    // 必須：End User名（複数入力対応：1件以上の非空値が必要）
    if (!endUserList.some((v) => v.trim() !== "")) {
      changeErrors("endUserId", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.endUserName')));
      isError = true;
    }

    // 必須：顧客業界
    if (!required(projectBasicInfo.customerIndustry ?? "")) {
      changeErrors("customerIndustry", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.customerIndustry')));
      isError = true;
    }

    // 必須：MMI製品分類
    if (!required(projectBasicInfo.mmProductCategoryId ?? "")) {
      changeErrors("mmProductCategoryId", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.mmProductCategory')));
      isError = true;
    }

    // 必須：MMI製品名
    if (!required(projectBasicInfo.mmProductNameFreeWrite ?? "")) {
      changeErrors("mmProductNameFreeWrite", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.mmProductName')));
      isError = true;
    }

    // 必須：MMIパーツNo.
    if (projectPartNo.length === 0 || projectPartNo.every(item => (item?.partNo ?? "").trim() === "")) {
      changeErrors("partNo", requiredErrorMessage("PartNo."));
      isError = true;
    }

    // 必須：会社/工場
    if (!required(projectBasicInfo.companyPlantId ?? "")) {
      changeErrors("companyPlantId", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.companyPlant')));
      isError = true;
    }

    // 必須：事業部（IDと名称の両方が必要）
    if (!required(projectBasicInfo.divisionId ?? "") || !selectedDivision?.organizationName) {
      changeErrors("divisionId", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.division')));
      isError = true;
    }

    // 必須：申請部署
    if (!required(selectedRegistrantDivisionId)) {
      changeErrors("registrantDivisionId", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.registrantDivision')));
      isError = true;
    }

    // 必須：申請番号
    if (!required(projectBasicInfo.applicationNumber ?? "")) {
      changeErrors("applicationNumber", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.applicationNumber')));
      isError = true;
    }

    // 必須：設備計画番号（複数入力対応：1件以上の非空値が必要）
    if (!equipmentPlanNumberList.some((v) => v.trim() !== "")) {
      changeErrors("equipmentPlanNumber", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.equipmentPlanNumber')));
      isError = true;
    }

    // 必須：計画予算
    if (!required(projectBasicInfo.plannedBudget ?? "")) {
      changeErrors("plannedBudget", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.plannedBudget')));
      isError = true;
    }

    // 必須：予算額
    if (!projectBasicInfo.budgetAmount) {
      changeErrors("budgetAmount", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.budgetAmount')));
      isError = true;
    }

    // 申請月度
    // チェック用にYYYY-MM-DDの形式にしておく
    // ※年月だけでも可能だが、変な動作にならないよう明示的に形式を日まで含めた形にしておく
    const validAppMon: string = `${projectBasicInfo.applicationMonth}-01`;
    if (!required(projectBasicInfo.applicationMonth ?? "")) {
      // 必須
      changeErrors("applicationMonth", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.applicationMonth')));
      isError = true;
    } else if (!isValidDateFormat(validAppMon)) {
      // 日付フォーマット
      changeErrors("applicationMonth", monthFormatErrorMessage(t('features.InvestmentScreening.BasicInfo.field.applicationMonth')));
      isError = true;
    } else if (!isValidDateRange(new Date(validAppMon))) {
      // 日付有効範囲
      changeErrors("applicationMonth", monthRangeErrorMessage(t('features.InvestmentScreening.BasicInfo.field.applicationMonth')));
      isError = true;
    }

    // 日付比較処理
    const compareDate = (dateString1: string | null | undefined, dateString2: string | null | undefined) => {
      if (!dateString1) {
        return 1;
      } else if (!dateString2) {
        return -1;
      }

      let date1: Date;
      let date2: Date;

      try {
        date1 = new Date(dateString1);
      } catch (error) {
        return 1;
      }
      try {
        date2 = new Date(dateString2);
      } catch (error) {
        return -1;
      }

      if (date1 < date2) {
        // date2の方が未来日付
        return 1;
      } else if (date1 > date2) {
        // date1の方が未来日付
        return -1;
      } else {
        // 同じ
        return 0;
      }
    }

    // 量産開始日
    if (!required(projectBasicInfo.projectStartDate ?? "")) {
      // 必須
      changeErrors("projectStartDate", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.projectStartDate')));
      isError = true;
    } else if (!isValidDateFormat(projectBasicInfo.projectStartDate ?? "")) {
      // 日付フォーマット
      changeErrors("projectStartDate", dateFormatErrorMessage(t('features.InvestmentScreening.BasicInfo.field.projectStartDate')));
      isError = true;
    } else if (!isValidDateRange(new Date(projectBasicInfo.projectStartDate ?? ""))) {
      // 日付有効範囲
      changeErrors("projectStartDate", dateRangeErrorMessage(t('features.InvestmentScreening.BasicInfo.field.projectStartDate')));
      isError = true;
    }

    // 計上期間終了日（変数名はprojectEndDateのまま。実態は計上期間の終端であり、設備の実際のEOL予定日とは異なる）
    if (!required(projectBasicInfo.projectEndDate ?? "")) {
      // 必須
      changeErrors("projectEndDate", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.projectEndDate')));
      isError = true;
    } else if (!isValidDateFormat(projectBasicInfo.projectEndDate ?? "")) {
      // 日付フォーマット
      changeErrors("projectEndDate", dateFormatErrorMessage(t('features.InvestmentScreening.BasicInfo.field.projectEndDate')));
      isError = true;
    } else if (!isValidDateRange(new Date(projectBasicInfo.projectEndDate ?? ""))) {
      // 日付有効範囲
      changeErrors("projectEndDate", dateRangeErrorMessage(t('features.InvestmentScreening.BasicInfo.field.projectEndDate')));
      isError = true;
    } else if (compareDate(projectBasicInfo.projectStartDate, projectBasicInfo.projectEndDate) < 1) {
      // 日付有効範囲
      changeErrors("projectEndDate", afterStartDateErrorMessage(t('features.InvestmentScreening.BasicInfo.field.projectEndDate')));
      isError = true;
    }

    // EOL予定日（設備の実際のEOL予定日。計上期間終了日（projectEndDate）とは意味が異なる新規項目）
    if (!required(projectBasicInfo.eolDate ?? "")) {
      // 必須
      changeErrors("eolDate", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.eolDate')));
      isError = true;
    } else if (!isValidDateFormat(projectBasicInfo.eolDate ?? "")) {
      // 日付フォーマット
      changeErrors("eolDate", dateFormatErrorMessage(t('features.InvestmentScreening.BasicInfo.field.eolDate')));
      isError = true;
    } else if (!isValidDateRange(new Date(projectBasicInfo.eolDate ?? ""))) {
      // 日付有効範囲
      changeErrors("eolDate", dateRangeErrorMessage(t('features.InvestmentScreening.BasicInfo.field.eolDate')));
      isError = true;
    } else if (compareDate(projectBasicInfo.projectStartDate, projectBasicInfo.eolDate) < 1) {
      // 量産開始日より後であること
      changeErrors("eolDate", afterStartDateErrorMessage(t('features.InvestmentScreening.BasicInfo.field.eolDate')));
      isError = true;
    } else if (compareDate(projectBasicInfo.projectEndDate, projectBasicInfo.eolDate) < 0) {
      // 計上期間終了日（projectEndDate）以降であること（同日を許容）
      changeErrors("eolDate", t('features.InvestmentScreening.BasicInfo.validation.eolDateAfterProjectEndDate', { fieldName: t('features.InvestmentScreening.BasicInfo.field.eolDate') }));
      isError = true;
    }

    // 必須：設備区分
    if (!required(projectBasicInfo.equipmentCategory ?? "")) {
      changeErrors("equipmentCategory", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.equipmentCategory')));
      isError = true;
    }

    // 必須：設備用途区分
    if (!required(projectBasicInfo.equipmentUsageType ?? "")) {
      changeErrors("equipmentUsageType", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.equipmentUsageType')));
      isError = true;
    }

    // 必須：目的
    if (!required(projectBasicInfo.purpose ?? "")) {
      changeErrors("purpose", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.purpose')));
      isError = true;
    }

    // 必須：投資目的
    if (!required(projectBasicInfo.purposeOfInvestment ?? "")) {
      changeErrors("purposeOfInvestment", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.purposeOfInvestment')));
      isError = true;
    }

    // 過去プロジェクトの紐づけがない増産もあるのでコメントアウト
    // if (!hiddenPastProject) {
    //   // 必須：過去Project選択
    //   if (projectBasicInfo.purpose !== PurposeType.NewProduct.code) {
    //     if (!required(projectBasicInfo.pastProjectId ?? "")) {
    //       changeErrors("pastProjectId", `過去Project選択${requiredMessage}`);
    //       isError = true;
    //     }
    //   }
    // }

    if (projectBasicInfo.pastProjectId) {

      // 増産投資
      if (projectBasicInfo.purpose === PurposeType.IncreasedProduction.code) {
        // 増産投資量産開始日
        if (!required(projectBasicInfo.increasedProductionStartDate ?? "")) {
          changeErrors("increasedProductionStartDate", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.increasedProductionStartDate')));
          isError = true;
        } else if (!isValidDateFormat(projectBasicInfo.increasedProductionStartDate ?? "")) {
          // 日付フォーマット
          changeErrors("increasedProductionStartDate", dateFormatErrorMessage(t('features.InvestmentScreening.BasicInfo.field.increasedProductionStartDate')));
          isError = true;
        } else if (!isValidDateRange(new Date(projectBasicInfo.increasedProductionStartDate ?? ""))) {
          // 日付有効範囲
          changeErrors("increasedProductionStartDate", dateRangeErrorMessage(t('features.InvestmentScreening.BasicInfo.field.increasedProductionStartDate')));
          isError = true;
        } else if (compareDate(projectBasicInfo.projectStartDate, projectBasicInfo.increasedProductionStartDate) < 1) {
          // 日付有効範囲
          changeErrors("increasedProductionStartDate", afterStartDateErrorMessage(t('features.InvestmentScreening.BasicInfo.field.increasedProductionStartDate')));
          isError = true;
        }
      }

      // 更新投資
      if (projectBasicInfo.purpose === PurposeType.UpdateAndOther.code) {
        // 更新投資量産開始日
        if (!required(projectBasicInfo.updateAndOtherStartDate ?? "")) {
          changeErrors("updateAndOtherStartDate", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.updateAndOtherStartDate')));
          isError = true;
        } else if (!isValidDateFormat(projectBasicInfo.updateAndOtherStartDate ?? "")) {
          // 日付フォーマット
          changeErrors("updateAndOtherStartDate", dateFormatErrorMessage(t('features.InvestmentScreening.BasicInfo.field.updateAndOtherStartDate')));
          isError = true;
        } else if (!isValidDateRange(new Date(projectBasicInfo.updateAndOtherStartDate ?? ""))) {
          // 日付有効範囲
          changeErrors("updateAndOtherStartDate", dateRangeErrorMessage(t('features.InvestmentScreening.BasicInfo.field.updateAndOtherStartDate')));
          isError = true;
        } else if (compareDate(projectBasicInfo.projectStartDate, projectBasicInfo.updateAndOtherStartDate) < 1) {
          // 日付有効範囲
          changeErrors("updateAndOtherStartDate", afterStartDateErrorMessage(t('features.InvestmentScreening.BasicInfo.field.updateAndOtherStartDate')));
          isError = true;
        }
      }

      // 合理化投資
      if (projectBasicInfo.purpose === PurposeType.Rationalization.code) {
        // 合理化投資量産開始日
        if (!required(projectBasicInfo.rationalizationStartDate ?? "")) {
          changeErrors("rationalizationStartDate", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.rationalizationStartDate')));
          isError = true;
        } else if (!isValidDateFormat(projectBasicInfo.rationalizationStartDate ?? "")) {
          // 日付フォーマット
          changeErrors("rationalizationStartDate", dateFormatErrorMessage(t('features.InvestmentScreening.BasicInfo.field.rationalizationStartDate')));
          isError = true;
        } else if (!isValidDateRange(new Date(projectBasicInfo.rationalizationStartDate ?? ""))) {
          // 日付有効範囲
          changeErrors("rationalizationStartDate", dateRangeErrorMessage(t('features.InvestmentScreening.BasicInfo.field.rationalizationStartDate')));
          isError = true;
        } else if (compareDate(projectBasicInfo.projectStartDate, projectBasicInfo.rationalizationStartDate) < 1) {
          // 日付有効範囲
          changeErrors("rationalizationStartDate", afterStartDateErrorMessage(t('features.InvestmentScreening.BasicInfo.field.rationalizationStartDate')));
          isError = true;
        }
      }

      // 新製品投資
      if (projectBasicInfo.purpose === PurposeType.NewProduct.code) {
        // 新製品投資量産開始日
        if (!required(projectBasicInfo.newProductStartDate ?? "")) {
          changeErrors("newProductStartDate", requiredErrorMessage(t('features.InvestmentScreening.BasicInfo.field.newProductStartDate')));
          isError = true;
        } else if (!isValidDateFormat(projectBasicInfo.newProductStartDate ?? "")) {
          // 日付フォーマット
          changeErrors("newProductStartDate", dateFormatErrorMessage(t('features.InvestmentScreening.BasicInfo.field.newProductStartDate')));
          isError = true;
        } else if (!isValidDateRange(new Date(projectBasicInfo.newProductStartDate ?? ""))) {
          // 日付有効範囲
          changeErrors("newProductStartDate", dateRangeErrorMessage(t('features.InvestmentScreening.BasicInfo.field.newProductStartDate')));
          isError = true;
        } else if (compareDate(projectBasicInfo.projectStartDate, projectBasicInfo.newProductStartDate) < 1) {
          // 日付有効範囲
          changeErrors("newProductStartDate", afterStartDateErrorMessage(t('features.InvestmentScreening.BasicInfo.field.newProductStartDate')));
          isError = true;
        }
      }

    }

    return !isError;
  }

  /**
   * 担当者設定モーダルで設定した確認者情報を設定する。
   * @param flowPersonData フローIDと従業員IDのリスト
   */
  const cofirmAssignTabModal = async (flowPersonData: any) => {

    // 確認者登録用stateに設定
    setSelectedConfirmorData(flowPersonData);

    const employeeIds = flowPersonData.map((item: { employeeId: string }) => item.employeeId);
    // 重複を除外してemployeeIdだけの配列を作成
    const uniqueEmployeeIds: string[] = Array.from(new Set(employeeIds));
    // 従業員選択モーダル用に設定
    setSelectedConfirmorIds(uniqueEmployeeIds);

    // 重複を削除する関数
    const getUniqueFlowPersonData = (data: any[]) => {
      const uniqueSet = new Map();
      data.forEach(item => {
        const key = `${item.employeeId}-${item.divisionId}-${item.employeeName}`;
        if (!uniqueSet.has(key)) {
          uniqueSet.set(key, item);
        }
      });
      return Array.from(uniqueSet.values());
    };

    // 重複のないデータを取得
    const uniqueFlowPersonData = getUniqueFlowPersonData(flowPersonData);
    setDispConfirmor(uniqueFlowPersonData);

    toast({
      title: TITLE,
      description: t('features.InvestmentScreening.BasicInfo.message.registrantConfirmReflect'),
      status: 'info',
      duration: 5000,
      isClosable: true,
    });

    // モーダルを閉じる
    onCloseAssignTabModal();
  }


  /**
   * プロジェクト基本情報を保存する。
   * @param flowPersonData フローIDと従業員IDのリスト
   * @returns true：成功、false：失敗
   */
  const saveProjectBasic = async (flowPersonData: any) => {
    let newProjectId = null;


    if (!projectId) {
      // 新規プロジェクトIDを採番
      // 顧客名: 自由入力化に伴い、プロジェクトIDから顧客ID部分を除外
      newProjectId = generateProjectId(projectBasicInfo.mmProductCategoryId ?? "");

      projectBasicInfo.projectId = newProjectId;
    }

    const query = `
      mutation putProjectBasic($input: ProjectBasicInfoInput!) {
        putProjectBasic(input: $input) {status message}
      }
    `;

    // PartNoを整理（重複や空行の削除）
    const validPartNo = [...new Set(projectPartNo.filter(partNoInfo => (partNoInfo?.partNo ?? "") !== "").map(partNoInfo => partNoInfo.partNo))];

    // displayPartNo（変更後PartNo表示名）の受け渡し用（No.435対応）
    const partNoDetails = projectPartNo
      .filter(partNoInfo => (partNoInfo?.partNo ?? "") !== "")
      .map(partNoInfo => ({
        partNo: partNoInfo.partNo,
        displayPartNo: partNoInfo.displayPartNo || null,
      }));

    let approvalStatus = projectBasicInfo.approvalStatus;
    let applicationDay = projectBasicInfo.applicationDay;
    let isClearConfirm = false;
    if (approvalStatus === ApproveStatus.Remand.code) {
      // 「差戻し」の場合、「申請中」に戻す。
      approvalStatus = ApproveStatus.Pending.code;
      applicationDay = "";
      isClearConfirm = true;
    }

    // APIリクエスト実行
    const result = await WebAPI.request({
      query: query,
      variables: {
        input: {
          projectId: projectBasicInfo.projectId,
          projectName: projectBasicInfo.projectName,
          // 顧客製品名: 自由入力化に伴い空にリセット
          revisedCustomerProductNameByRevision: "",
          // 案件名: 自由入力化に伴い空にリセット
          revisedOpportunityNameByRevision: "",
          mmProductNameFreeWrite: projectBasicInfo.mmProductNameFreeWrite,
          purpose: projectBasicInfo.purpose,
          purposeOfInvestment: projectBasicInfo.purposeOfInvestment?.trim(),
          // 申請書様式（合理化/更新時のみ意味を持つ。それ以外はDetail固定で保存）
          applicationFormType: (projectBasicInfo.purpose === PurposeType.Rationalization.code
            || projectBasicInfo.purpose === PurposeType.UpdateAndOther.code)
            ? (projectBasicInfo.applicationFormType || ApplicationFormType.Detail.code)
            : ApplicationFormType.Detail.code,
          // 損益表示（簡易様式時のみ意味を持つ。それ以外はShow固定で保存）
          profitLossDisplayType: isSimpleApplicationForm(projectBasicInfo.purpose, projectBasicInfo.applicationFormType)
            ? (projectBasicInfo.profitLossDisplayType || ProfitLossDisplayType.Show.code)
            : ProfitLossDisplayType.Show.code,
          carModel: projectBasicInfo.carModel,
          specificationAndPerformance: projectBasicInfo.specificationAndPerformance,
          customerIndustry: projectBasicInfo.customerIndustry,
          customerIndustryHierarchy: projectBasicInfo.customerIndustryHierarchy,
          applicationCategory: projectBasicInfo.applicationCategory,
          applicationMonth: projectBasicInfo.applicationMonth,
          projectStartDate: projectBasicInfo.projectStartDate,
          projectEndDate: projectBasicInfo.projectEndDate,
          eolDate: projectBasicInfo.eolDate,
          equipmentCategory: projectBasicInfo.equipmentCategory,
          increasedProductionStartDate: projectBasicInfo.purpose === PurposeType.IncreasedProduction.code
            ? (projectBasicInfo.increasedProductionStartDate || projectBasicInfo.projectStartDate)
            : null,
          updateAndOtherStartDate: projectBasicInfo.purpose === PurposeType.UpdateAndOther.code
            ? (projectBasicInfo.updateAndOtherStartDate || projectBasicInfo.projectStartDate)
            : null,
          rationalizationStartDate: projectBasicInfo.purpose === PurposeType.Rationalization.code
            ? (projectBasicInfo.rationalizationStartDate || projectBasicInfo.projectStartDate)
            : null,
          newProductStartDate: projectBasicInfo.purpose === PurposeType.NewProduct.code
            ? (projectBasicInfo.newProductStartDate || projectBasicInfo.projectStartDate)
            : null,
          partNo: validPartNo,
          partNoDetails: partNoDetails,
          pastProjectId: projectBasicInfo.pastProjectId,
          pastProjectName: projectBasicInfo.pastProjectName,
          pastReviewDate: projectBasicInfo.pastReviewDate ?? null,
          approvalStatus: approvalStatus,
          updateDate: projectBasicInfo.updateDate,
          applicationDay: applicationDay,
          applicationNumber: projectBasicInfo.applicationNumber,
          equipmentPlanNumber: equipmentPlanNumberList.map((v) => v.trim()).filter((v) => v !== ""), // 設備計画番号: 複数入力を配列で保存
          plannedBudget: projectBasicInfo.plannedBudget,
          budgetAmount: projectBasicInfo.budgetAmount,
          equipmentUsageType: projectBasicInfo.equipmentUsageType,
          divisionHierarchy: projectBasicInfo.divisionHierarchy,
          divisionHierarchyEn: projectBasicInfo.divisionHierarchyEn,
          rateType: rateType,

          // 顧客ID: マスタ選択時はID、手動入力時はテキスト（カンマ区切り: "顧客名,正式社名"、値中のカンマはエスケープして保存）
          customerId: selectedCustomer?.customerId
            || joinEscapedList([projectBasicInfo.customerName, projectBasicInfo.customerFullName]),
          // 案件ID: マスタ選択時はID、手動入力時はテキスト（案件名）
          opportunityId: selectedOpportunity?.opportunityId || projectBasicInfo.opportunityName,
          // 顧客製品ID: マスタ選択時はID、手動入力時はテキスト（製品名）
          customerProductId: selectedCustomerProduct?.customerProductId || projectBasicInfo.customerProductName,
          mmProductCategoryId: projectBasicInfo.mmProductCategoryId,
          endUserId: joinEscapedList(endUserList), // End User名: 複数入力をカンマ区切り(値中のカンマはエスケープ)で保存
          divisionId: projectBasicInfo.divisionId,
          // 会社/工場情報
          companyPlantId: projectBasicInfo.companyPlantId,
          companyPlantSnapshot: projectBasicInfo.companyPlantSnapshot,
          baseInterestRate: fetchedInterestRateInfo?.rate ?? null,
          registrantEmployeeId: loginUserEmployeeId,
          registrantDivisionId: selectedRegistrantDivisionId,
          isClearConfirm: isClearConfirm,
          crossCheckRequired: projectBasicInfo.crossCheckRequired ?? false,
          crossCheckDepartments: projectBasicInfo.crossCheckRequired ? (projectBasicInfo.crossCheckDepartments ?? "") : "",
          crossCheckAssignees: projectBasicInfo.crossCheckRequired ? (() => {
            // チェックOFFの部門データを除外して保存
            try {
              const assignees = JSON.parse(projectBasicInfo.crossCheckAssignees ?? "[]");
              const checkedDepts = (projectBasicInfo.crossCheckDepartments ?? "").split(",").filter(v => v);
              const filtered = assignees.filter((a: any) => checkedDepts.includes(a.categoryId));
              return JSON.stringify(filtered);
            } catch { return projectBasicInfo.crossCheckAssignees ?? ""; }
          })() : "",
          materialOfficerRequired: projectBasicInfo.materialOfficerRequired ?? false,
          materialOfficerAssignees: projectBasicInfo.materialOfficerRequired ? (projectBasicInfo.materialOfficerAssignees ?? "") : "",
          approverAssignees: projectBasicInfo.approverAssignees ?? "",
          screenViewRestriction: projectBasicInfo.screenViewRestriction ?? null,
          updateDateRDS: projectBasicInfo.updateDateRDS,
          ...(reviewDate && { reviewDate }),
        }
      }
    });

    console.log(`response = `, result);

    if (result?.data?.putProjectBasic?.status === "success") {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.saveSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // 保存成功により目的・過去PJ紐づけが DB に反映されたため、保存済み値を更新する
      setSavedEquipmentSource({
        purpose: projectBasicInfo.purpose ?? null,
        pastProjectId: projectBasicInfo.pastProjectId ?? null,
        pastReviewDate: projectBasicInfo.pastReviewDate ?? null,
      });

      // 保存成功により送信したPartNoはDB登録済みとなるため、registeredPartNosを再構築する（No.435対応）
      setRegisteredPartNos(validPartNo.filter((p): p is string => !!p));
      setSavedPartNoSnapshot(validPartNo.filter((p): p is string => !!p));

      if (flowPersonData) {
        // 新規、差戻、ワークフロー未登録の場合:true 条件以外:false
        const shouldNumberBrNo =
          !!newProjectId ||
          pagePermissions?.currentFlowId === WorkflowId.Remand ||
          userPermissions.length === 0;
        // 入力依頼通知
        await sendConfirmWorkflow(flowPersonData, shouldNumberBrNo);
      }

      if (beforePurpose && beforePurpose === PurposeType.NewProduct.code &&
        [PurposeType.IncreasedProduction.code, PurposeType.UpdateAndOther.code, PurposeType.Rationalization.code].includes(projectBasicInfo.purpose || "")) {
        // 編集前と編集後で目的が異なる場合
        await updateWorkflowForPurpose(projectBasicInfo.projectId, beforePurpose, projectBasicInfo.purpose);
      } else if (beforePurpose && projectBasicInfo.purpose === PurposeType.NewProduct.code &&
        [PurposeType.IncreasedProduction.code, PurposeType.UpdateAndOther.code, PurposeType.Rationalization.code].includes(beforePurpose || "")) {
        // 編集前と編集後で目的が異なる場合
        await updateWorkflowForPurpose(projectBasicInfo.projectId, beforePurpose, projectBasicInfo.purpose);
      }

      if (newProjectId) {
        // 新規登録時：為替レート最新化
        await updateCurrencyAsync();
      } else {
        const currencyData = await getCurrencyById(projectId);
        if (currencyData.length === 0) {
          // 既存データのうち、プロジェクト単位で通貨情報が未登録：為替レート最新化
          await updateCurrencyAsync();
        }
      }

      if (newProjectId) {
        // プロジェクトID変更通知
        await onProjectIdChange(newProjectId);
      }

      // 権限情報・プロジェクト情報を再取得（目的・設備用途区分の変更を親コンポーネントに反映）
      await refreshUserPermissions();

      // newProjectIdが存在しない、かつフロー状態が差し戻しではない場合にデータ再取得
      if (!newProjectId) {
        // プロジェクト情報再取得
        await fetchProject();
      }

      return true;
    } else if (result?.data?.putProjectBasic.status === 'expired') {
      toast({
        title: TITLE,
        description: Messages.expiredMessage,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      console.warn(`Conflict error. putProjectBasic.`);
    } else if (result?.data?.putProjectBasic?.status === "duplicateKeyError") {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.saveFailedRetry'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return false;
    } else if (result?.data?.putProjectBasic?.status === "permissionDenied") {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.noEditPermission'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`putProjectBasic: permission denied`);
      return false;
    } else if (result?.data?.putProjectBasic?.status === "duplicate") {
      // 同時判別方式: 両方の重複フラグを確認
      const msg = result?.data?.putProjectBasic?.message || "";
      if (msg.includes("申請番号")) {
        changeErrors("applicationNumber", t('features.InvestmentScreening.BasicInfo.validation.applicationNumberDuplicate'));
      }
      if (msg.includes("設備計画番号")) {
        changeErrors("equipmentPlanNumber", t('features.InvestmentScreening.BasicInfo.validation.equipmentPlanNumberDuplicate'));
      }
      toast({
        title: TITLE,
        description: msg || t('features.InvestmentScreening.BasicInfo.validation.duplicateError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    } else {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API putProjectBasic. errors = ${JSON.stringify(result?.errors)}`);
      return false;
    }
  }

  /**
   * 為替情報（通貨情報）を最新に更新する。
   */
  const updateCurrencyAsync = async () => {
    let currencyDataToSave: Currency[];
    let ratePeriod: string = '';

    if (rateType === 'actual') {
      // 実勢レート取得（営業支援API経由）
      const latestData = await getLatestCurrencyFromSas();
      // Currency型に変換
      currencyDataToSave = latestData.map((item, index) => ({
        currencyId: item.currencyId,
        currencyName: item.currencyName,
        exchangeRate: item.exchangeRate,
        order: index + 1,
      }));
      // 実勢レートの場合はYYYY-MM形式で保存
      if (latestData.length > 0 && latestData[0].rateMonth) {
        ratePeriod = latestData[0].rateMonth.substring(0, 7); // YYYY-MM-DD → YYYY-MM
      }
    } else {
      // 計画レート取得（営業支援API経由）
      // 会計年度を算出（4月始まり：1〜3月は前年度）
      const now = new Date();
      const currentYear = (now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear()).toString();
      const prospectData = await getCurrencyProspectFromSas(currentYear);
      // Currency型に変換
      currencyDataToSave = prospectData.map((item, index) => ({
        currencyId: item.currencyId,
        currencyName: item.currencyName,
        exchangeRate: item.exchangeRate,
        order: index + 1,
      }));
      // 計画レートの場合はYYYY形式で保存
      if (prospectData.length > 0 && prospectData[0].rateYear) {
        ratePeriod = prospectData[0].rateYear;
      }
    }

    // 楽観的ロック: 既存アイテムには DB の updateDate をセット、新規アイテムは updateDate なし（強制上書き）
    // updateCurrencyAsync は外部API（営業支援API）からの最新化フローのため、
    // 既存アイテムも updateDate なしの強制上書きとする
    const currencyDataWithoutUpdateDate = currencyDataToSave.map(({ updateDate: _omit, ...rest }) => rest);

    // プロジェクト単位で通貨情報保存（rateType, ratePeriodも同時に保存）
    await putCurrency((projectBasicInfo.projectId)!, currencyDataWithoutUpdateDate, rateType!, ratePeriod);
    setCurrencyUpdated(prev => !prev);
  }

  /**
   * 通貨情報を登録または更新する。
   * @param projectId プロジェクトID
   * @param currencyData 通貨情報
   * @param rateTypeValue レート種別（actual/plan）
   * @param ratePeriodValue レート期間（実勢レート: YYYY-MM, 計画レート: YYYY）
   */
  const putCurrency = async (projectId: string, currencyData: Currency[], rateTypeValue: string, ratePeriodValue: string) => {
    const query = `
      mutation putCurrency($input: CurrencyListInput!) {
        putCurrency(input: $input) {status,message,updateDate}
      }
    `;

    // APIリクエスト実行
    const result = await WebAPI.request({
      query: query,
      variables: {
        input: {
          projectId: projectId,
          currencyData: currencyData,
          rateType: rateTypeValue,
          ratePeriod: ratePeriodValue,
        }
      }
    });

    console.log(`putCurrency response = ${JSON.stringify(result)}`);

    if (result?.data?.putCurrency?.status === "success") {
      // 排他制御用に updateDate を更新
      if (result?.data?.putCurrency?.updateDate) {
        setProjectBasicInfo(prev => ({
          ...prev,
          updateDate: result.data.putCurrency.updateDate
        }));
      }
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.currencyInfoSaved'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return true;
    } else {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.currencyInfoSaveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API putCurrency. errors = ${JSON.stringify(result)}`);
      return false;
    }

  }

  /**
   * 入力依頼通知を行う。（ワークフロー登録）
   * @param flowPersonData フローIDと従業員IDのリスト
   * @param shouldNumberBrNo 新規、差戻の場合:true それ以外:false
   * @returns true：成功、false：失敗
   */
  const sendConfirmWorkflow = async (flowPersonData: any, shouldNumberBrNo: boolean) => {
    // GraphQLクエリ定義
    const query = `
         mutation putConfirmWorkflow($input: FlowPersonUpdateListInput!) {
            putConfirmWorkflow(input: $input) {status}
         }
      `;

    // APIリクエスト実行
    const result = await WebAPI.request({
      query: query,
      variables: {
        input: {
          projectId: projectBasicInfo.projectId,
          NumberingBrNo: shouldNumberBrNo,
          flowPersonData: flowPersonData,
          ...(reviewDate && { reviewDate }),
        }
      }
    });


    // 成功時処理(送信者変更なし)
    if (result?.data?.putConfirmWorkflow.status === 'successNoSenderChange') {
      return true;
    }

    // 成功時処理
    if (result?.data?.putConfirmWorkflow.status === 'success') {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.workflowRegistered'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return true;
    } else {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.workflowRegisterFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API putConfirmWorkflow. errors = ${JSON.stringify(result?.errors)}`);
      return false;
    }
  }

  /**
   * 目的変更に合わせてワークフローのフロー区分更新
   * @param projectId プロジェクトID
   * @param srcPurpose 編集前：目的
   * @param destPurpose 編集後：目的
   * @returns 
   */
  const updateWorkflowForPurpose = async (projectId: string | null | undefined, srcPurpose: string | null | undefined, destPurpose: string | null | undefined) => {

    const functionName = "updateWorkflowForPurpose";

    try {
      // GraphQLクエリ定義
      const query = `
         mutation updateWorkflowForPurpose($input: WorkflowForPurposeInput!) {
            updateWorkflowForPurpose(input: $input) {status, message}
         }
      `;

      // APIリクエスト実行
      const result = await WebAPI.request({
        query: query,
        variables: {
          input: {
            projectId: projectId,
            srcFlowId: srcPurpose === PurposeType.NewProduct.code ? WorkflowId.SimulationNewProduct : WorkflowId.SimulationProductivityImprovement,
            destFlowId: destPurpose === PurposeType.NewProduct.code ? WorkflowId.SimulationNewProduct : WorkflowId.SimulationProductivityImprovement,
          }
        }
      });

      console.log(`${functionName} response = `, result);

      // 成功時処理
      if (result?.data?.updateWorkflowForPurpose.status === 'success') {
        console.log(`${functionName} success`);
        return true;
      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BasicInfo.message.workflowRegisterFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API ${functionName}. errors = `, result?.errors);
        return false;
      }
    } catch (error) {
      console.error(`Exception to call API ${functionName}. errors = `, error);
    }
  }

  /**
   * トピックス表示モーダルを表示する。
   */
  const handleOpenTopicModal = async () => {
    let tempTopicsInfo = null;

    if ((topicsInfo?.opportunityId !== selectedOpportunity.opportunityId)) {
      const columns =
        `opportunityId
        opportunityName
        currentStatus
        applicationCategory
        MMProductQuantityPerMonth
        MMProductUnitPrice
        massProduction
        partsNumber
        TTLQtyPerMonth
        unitPerSet
        mileStone1
        mileStone2
        mileStone3
        mileStone4
        mileStone5
        mileStone6
        schedule1
        schedule2
        schedule3
        schedule4
        schedule5
        schedule6
        MMProductCategory {
            deleted
            id
            name
        }

        MPLocationList {
            deleted
            id
            name
        }
        currency {
            deleted
            id
            name
        }
        customer {
            deleted
            id
            name
        }
        customerProduct {
            deleted
            id
            name
            imageFileName
        }
        endUser {
            deleted
            id
            name
        }
        opportunityStage {
            deleted
            id
            name
        }
        salesPerson {
            deleted
            id
            name
        }
        salesUnit {
            deleted
            id
            name
        }
        competitorList {
            deleted
            id
            name
        }
        channelList {
            channelLevel
            channelId
            clientType
            clientName
        }`;

      // トピックス情報取得（variables方式）
      const topicsPayload = {
        query: `
          query getOpportunityById($opportunityId: String) {
            getOpportunityById(opportunityId: $opportunityId) {
              ${columns}
            }
          }`,
        variables: { opportunityId: projectBasicInfo.opportunityId }
      };
      const topicsResponse = await WebAPI.request(topicsPayload);
      tempTopicsInfo = topicsResponse?.data?.getOpportunityById;
      if (tempTopicsInfo && tempTopicsInfo.opportunityId == null) {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BasicInfo.message.topicsFetchFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        tempTopicsInfo = null;
        setTopicsInfo(null);
        return;
      }
      setTopicsInfo(tempTopicsInfo);
    }

    if (topicsInfo || tempTopicsInfo) {
      onTopicsModalOpen();
    }
  }

  /**
   * 過去Project選択モーダルを表示する。
   */
  const handlePastProject = async () => {
    // 検索条件・結果を初期化
    setSearchPartNoPrefix("");
    setPastProjectData(null);
    setFilterPastProjectItems(emptyFilterPastProjectItems());
    setSelectedPastProject(null);
    setSelectedPastPartNos([]);

    // モーダル表示
    onOpenPastProject();
  }

  /**
   * PartNo前方一致で過去プロジェクトを検索する（モーダル内の検索ボタン押下時）。
   */
  const searchPastProjectByPartNo = async () => {
    if (!searchPartNoPrefix || searchPartNoPrefix.trim() === "") {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.partNoRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (searchPartNoPrefix.trim().length < 3) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.partNoMin3'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 検索実行
    const result = await fetchPastProjectByPrefix(searchPartNoPrefix.trim());
    setPastProjectData(result.projects);

    // 0件メッセージ
    if (result.projects && result.projects.length === 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.searchNoResult'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }

    // 100件超過メッセージ
    if (result.hasMore) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BasicInfo.message.searchTooMany'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }

    // フィルター情報初期化
    setFilterPastProjectItems(emptyFilterPastProjectItems());

    // 選択値設定（既に選択済みのプロジェクトがあれば維持）
    if (result.projects) {
      const pastProject = result.projects.find(p => p.projectId === projectBasicInfo.pastProjectId);
      setSelectedPastProject(pastProject || null);
      // 選択済みプロジェクトのPartNoもデフォルト選択
      if (pastProject?.partNo) {
        const pastPartNos = pastProject.partNo.split(",").map(pn => pn.trim()).filter(pn => pn !== "");
        setSelectedPastPartNos(pastPartNos);
      }
    } else {
      setSelectedPastProject(null);
    }
  }

  // ========== DEBUG: 一括確認ボタン（テスト用・後で削除） ==========
  const DEBUG_CONFIRM_ALL_MAP: Record<string, string[]> = {
    "2": ["companyDivision", "salesDivision"],                                      // 数量・価格
    "3": ["companyDivision", "salesDivision", "procurementDivision"],               // 顧客取り決め事項
    "4": ["companyDivision", "salesDivision"],                                      // スケジュール
    "6": ["companyDivision", "procurementDivision"],                                // 製造工程
    "7": ["companyDivision", "procurementDivision"],                                // 設備投資
    "8": ["companyDivision", "companyHeadDivision"],                                // P&L
    "9": ["companyDivision", "companyHeadDivision"],                                // 対象PJ
    "10": ["companyDivision", "companyHeadDivision"],                                // Simulation（新製品）
    "11": ["companyDivision", "companyHeadDivision"],                                // Simulation（生産性向上）
    "12": ["companyDivision", "companyHeadDivision", "salesDivision", "procurementDivision"], // 申請書
    // 振り返りモード時のみ有効: 振り返り3画面（13:営業視点 / 14:事業視点 / 15:今後の見込み）
    ...(reviewDate ? getReviewDebugConfirmMap() : {}),
  };
  const [debugConfirmRunning, setDebugConfirmRunning] = useState(false);
  const handleDebugConfirmAll = async () => {
    if (!projectId || !loginUserEmployeeId) {
      toast({ title: t('common.label.error'), description: t('features.InvestmentScreening.BasicInfo.message.projectOrEmployeeMissing'), status: "error", duration: 3000 });
      return;
    }
    if (!window.confirm(t('features.InvestmentScreening.BasicInfo.label.bulkConfirmPrompt', { projectId, employeeId: loginUserEmployeeId }))) return;
    setDebugConfirmRunning(true);
    let successCount = 0;
    let failCount = 0;
    try {
      for (const [flowId, divisions] of Object.entries(DEBUG_CONFIRM_ALL_MAP)) {
        if (projectBasicInfo.purpose === PurposeType.NewProduct.code && flowId === WorkflowId.SimulationProductivityImprovement) {
          continue;
        } else if (projectBasicInfo.purpose !== PurposeType.NewProduct.code && flowId === WorkflowId.SimulationNewProduct) {
          continue;
        }

        for (const division of divisions) {
          const result = await updateConfirm(WebAPI, projectId, flowId, loginUserEmployeeId, division, "true", reviewDate);
          if (result) { successCount++; } else { failCount++; }
        }
      }
      toast({ title: t('features.InvestmentScreening.BasicInfo.message.bulkConfirmTitle'), description: t('features.InvestmentScreening.BasicInfo.message.bulkConfirmResult', { success: successCount, failed: failCount }), status: failCount === 0 ? "success" : "warning", duration: 5000 });
    } catch (error) {
      toast({ title: t('features.InvestmentScreening.BasicInfo.message.bulkConfirmErrorTitle'), description: String(error), status: "error", duration: 5000 });
    } finally {
      setDebugConfirmRunning(false);
    }
  };

  // ========== DEBUG: 担当者一括設定ボタン（テスト用・後で削除） ==========
  // 確認者・承認者・CC・資材担当のすべてにログインユーザーを設定する（state を埋めるのみ、保存は手動）
  const handleDebugFillAllAssignees = () => {
    if (!loginUserEmployeeId || !loginUserEmployeeName || !loginUserDivisionId) {
      toast({ title: t('common.label.error'), description: "ログインユーザー情報が取得できません", status: "error", duration: 3000 });
      return;
    }

    // 1. 確認者: purpose × reviewMode に対応する有効な (flowId × divisionId) ペア全てに自分を登録
    // divisionId は ComfirmDivisions の値（"company", "companyHead", "sales", "procurement"）
    const validPairs = getValidFlowDivisionPairs(projectBasicInfo.purpose, !!reviewDate);
    const flowPersonData: any[] = validPairs.map(pair => ({
      flowId: pair.flowId,
      divisionId: pair.divisionId,
      employeeId: loginUserEmployeeId,
      employeeName: loginUserEmployeeName,
    }));
    cofirmAssignTabModal(flowPersonData);

    // 2. 承認者・CC・資材担当の共通 employee
    const selfEmployee: CategoryEmployee = {
      employeeId: loginUserEmployeeId,
      employeeName: loginUserEmployeeName,
      divisionId: loginUserDivisionId,
      divisionName: loginUserDivisionName,
    };

    // 3. 承認者: 全カテゴリに自分を登録
    const approverAssigneesAll: CategoryAssignee[] = getApproverCategories().map(cat => ({
      categoryId: cat.id,
      categoryName: cat.label,
      employees: [selfEmployee],
    }));
    setApproverAssignees(approverAssigneesAll);
    changeProjectBasicInfo("approverAssignees", JSON.stringify(approverAssigneesAll));
    setApproverReconfirmed(true);

    // 4. CC: 全カテゴリに自分を登録
    const crossCheckAssigneesAll: CategoryAssignee[] = Object.values(getCrossCheckCategories()).map(cat => ({
      categoryId: cat.id,
      categoryName: cat.label,
      employees: [selfEmployee],
    }));
    setCrossCheckAssignees(crossCheckAssigneesAll);
    changeProjectBasicInfo("crossCheckAssignees", JSON.stringify(crossCheckAssigneesAll));
    setCrossCheckReconfirmed(true);

    // 5. 資材担当: カテゴリに自分を登録
    const materialOfficerAssigneesAll: CategoryAssignee[] = getMaterialOfficerCategories().map(cat => ({
      categoryId: cat.id,
      categoryName: cat.label,
      employees: [selfEmployee],
    }));
    setMaterialOfficerAssignees(materialOfficerAssigneesAll);
    changeProjectBasicInfo("materialOfficerAssignees", JSON.stringify(materialOfficerAssigneesAll));
    setMaterialOfficerReconfirmed(true);

    toast({
      title: "一括担当者設定(DEBUG)",
      description: "確認者・承認者・CC・資材担当を自分に設定しました。「保存」ボタンで反映してください。",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };
  // ========== DEBUG END ==========

  // approvalStatus → そのステージで承認すべき flowId 配列
  // 並列ブロックは全ての flowId を順に承認して putApproveComplete で GATE 遷移させる
  const DEBUG_APPROVE_STAGE_MAP: { status: string; flowIds: string[] }[] = [
    { status: ApproveStatus.ProductDirector.code, flowIds: [WorkflowId.ProductDirector] },
    { status: ApproveStatus.BusinessManager.code, flowIds: [WorkflowId.BusinessManager] },
    { status: ApproveStatus.CCMaterialCheck.code, flowIds: [WorkflowId.CrossCheck, WorkflowId.MaterialOfficerRequester, WorkflowId.MaterialOfficer] },
    { status: ApproveStatus.HQApproval.code, flowIds: [WorkflowId.SalesHQManagerRequester, WorkflowId.SalesHQManager, WorkflowId.BusinessHQManagerRequester, WorkflowId.BusinessHQManager] },
    { status: ApproveStatus.PreReview.code, flowIds: [WorkflowId.PreReview] },
    { status: ApproveStatus.HQReview.code, flowIds: [WorkflowId.HQReview] },
    { status: ApproveStatus.PresidentReport.code, flowIds: [WorkflowId.PresidentReport] },
  ];

  // 承認結果更新API呼び出し（DEBUG用）
  // BEが業務ロジックで false を返した場合（承認者として未登録 / 既に次ステータスへ遷移済み等）は
  // 例外ではなく "skipped" として扱う。ネットワーク/例外のみを "failed" とする。
  const debugUpdateApprovalResult = async (targetFlowId: string): Promise<'ok' | 'skipped' | 'failed'> => {
    try {
      const result = await WebAPI.request({
        query: `
          mutation updateApprovalResult(
            $projectId: String,
            $flowId: String,
            $employeeId: String,
            $comment: String,
            $approvalResult: String,
            $chairName: String,
            $reviewDate: String
          ) {
            updateApprovalResult(
              projectId: $projectId,
              flowId: $flowId,
              employeeId: $employeeId,
              comment: $comment,
              approvalResult: $approvalResult,
              chairName: $chairName,
              reviewDate: $reviewDate
            ) {
              result
            }
          }
        `,
        variables: {
          projectId,
          flowId: targetFlowId,
          employeeId: loginUserEmployeeId,
          comment: "DEBUG",
          approvalResult: "1",
          chairName: targetFlowId === WorkflowId.HQReview ? "DEBUG" : undefined,
          ...(reviewDate && { reviewDate }),
        },
      });
      return result?.data?.updateApprovalResult?.result === 'true' ? 'ok' : 'skipped';
    } catch (error) {
      console.error("debugUpdateApprovalResult ERROR:", error);
      return 'failed';
    }
  };

  // ワークフローID単位の承認状況取得API（未承認者が残っていないか確認）
  const debugGetApproveStatusByFlowId = async (targetFlowId: string): Promise<boolean> => {
    try {
      const result = await WebAPI.request({
        query: `
          query getApproveStatusByFlowId($projectId: String, $flowId: String, $reviewDate: String) {
            getApproveStatusByFlowId(projectId: $projectId, flowId: $flowId, reviewDate: $reviewDate) {
              result
            }
          }
        `,
        variables: { projectId, flowId: targetFlowId, ...(reviewDate && { reviewDate }) },
      });
      return result?.data?.getApproveStatusByFlowId?.result === 'true';
    } catch (error) {
      console.error("debugGetApproveStatusByFlowId ERROR:", error);
      return false;
    }
  };

  // 承認完了登録API（GATE遷移: 次ステータスへ）
  const debugPutApproveComplete = async (): Promise<boolean> => {
    try {
      const result = await WebAPI.request({
        query: `
          mutation putApproveComplete($input: ApproveCompleteInput!) {
            putApproveComplete(input: $input) { status }
          }
        `,
        variables: {
          input: {
            projectId,
            employeeId: loginUserEmployeeId,
            divisionId: loginUserDivisionId,
            ...(reviewDate && { reviewDate }),
          },
        },
      });
      return result?.data?.putApproveComplete?.status === 'success';
    } catch (error) {
      console.error("debugPutApproveComplete ERROR:", error);
      return false;
    }
  };

  const [debugApproveRunning, setDebugApproveRunning] = useState(false);
  const handleDebugApproveAll = async () => {
    if (!projectId || !loginUserEmployeeId) {
      toast({ title: t('common.label.error'), description: t('features.InvestmentScreening.BasicInfo.message.projectOrEmployeeMissing'), status: "error", duration: 3000 });
      return;
    }
    const currentStatus = projectBasicInfo.approvalStatus;
    if (!window.confirm(t('features.InvestmentScreening.BasicInfo.label.bulkApprovePrompt', { projectId, employeeId: loginUserEmployeeId, status: currentStatus }))) return;

    setDebugApproveRunning(true);
    let okCount = 0;
    let skippedCount = 0;
    let ngCount = 0;
    try {
      // 現ステータスに該当するステージ以降を順次進める
      // この案件に承認行（r_ceas_workflow）がある flowId だけを呼ぶ。
      // 行がない flowId（要否「否」の工程・依頼者マスタ未登録など）を呼ぶと、
      // バックエンドが PermissionDenied を出力し CloudWatch アラームが発報するため。
      // 取得に失敗した場合は null のままとし、従来どおり全 flowId を呼ぶ。
      let assignedFlowIds: Set<string> | null = null;
      try {
        const listResponse = await WebAPI.request({
          query: `query getApproverList($projectId: String, $divisionId: String, $reviewDate: String) {
            getApproverList(projectId: $projectId, divisionId: $divisionId, reviewDate: $reviewDate) {
              flowId
            }
          }`,
          variables: { projectId, divisionId: loginUserDivisionId, ...(reviewDate && { reviewDate }) },
        });
        const rows = listResponse?.data?.getApproverList;
        if (Array.isArray(rows)) {
          assignedFlowIds = new Set(rows.map((row: { flowId: string }) => String(row.flowId)));
        }
      } catch (error) {
        console.error("debugGetApproverList ERROR:", error);
      }
      const startIdx = DEBUG_APPROVE_STAGE_MAP.findIndex(s => s.status === currentStatus);
      if (startIdx < 0) {
        toast({ title: t('features.InvestmentScreening.BasicInfo.message.bulkApproveErrorTitle'), description: t('features.InvestmentScreening.BasicInfo.message.bulkApproveInvalidStatus', { status: currentStatus }), status: "warning", duration: 5000 });
        return;
      }

      for (let i = startIdx; i < DEBUG_APPROVE_STAGE_MAP.length; i++) {
        const stage = DEBUG_APPROVE_STAGE_MAP[i];
        // 1. ステージ内の全 flowId について updateApprovalResult を実行
        //    GATE 遷移後に呼び出した flowId は BE 側で弾かれるが、業務上は想定内なので skipped 扱い
        for (const fid of stage.flowIds) {
          // 承認行がない flowId は呼ばずにスキップ扱い（PermissionDenied を出さないため）
          if (assignedFlowIds && !assignedFlowIds.has(fid)) {
            skippedCount++;
            continue;
          }
          const r = await debugUpdateApprovalResult(fid);
          if (r === 'ok') okCount++;
          else if (r === 'skipped') skippedCount++;
          else ngCount++;
        }
        // 2. 承認完了（PresidentReport）ステージのみ putApproveComplete を呼んで approvalStatus='90' へ遷移
        //    （他の直列/並列ステージは BE 側の updateApprovalResult が次ステータスへ遷移させる）
        if (stage.status === ApproveStatus.PresidentReport.code) {
          const gateOk = await debugPutApproveComplete();
          if (gateOk) okCount++; else ngCount++;
        }
      }

      toast({
        title: t('features.InvestmentScreening.BasicInfo.message.bulkApproveTitle'),
        description: t('features.InvestmentScreening.BasicInfo.message.bulkApproveResult', { success: okCount, skipped: skippedCount, failed: ngCount }),
        status: ngCount === 0 ? "success" : "warning",
        duration: 5000,
      });
    } catch (error) {
      toast({ title: t('features.InvestmentScreening.BasicInfo.message.bulkApproveErrorTitle'), description: String(error), status: "error", duration: 5000 });
    } finally {
      setDebugApproveRunning(false);
    }
  };
  // ========== DEBUG END ==========

  /** レイアウト */
  return (
    <>
      {/* ========== DEBUG: 一括確認ボタン（テスト用・後で削除） ========== */}
      {localStorage.getItem("DEBUG") === "true" && (
        <Box position="fixed" top="10vh" right="20px" zIndex={1000}>
          <HStack spacing={2}>
            <Button
              colorScheme="red"
              size="sm"
              onClick={handleDebugConfirmAll}
              isLoading={debugConfirmRunning}
              loadingText={t('features.InvestmentScreening.BasicInfo.label.executing')}
            >
              {t('features.InvestmentScreening.BasicInfo.label.debugConfirmAll')}
            </Button>
            <Button
              colorScheme="purple"
              size="sm"
              onClick={handleDebugApproveAll}
              isLoading={debugApproveRunning}
              loadingText={t('features.InvestmentScreening.BasicInfo.label.executing')}
            >
              {t('features.InvestmentScreening.BasicInfo.label.debugApproveAll')}
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={handleDebugFillAllAssignees}
            >
              一括担当者設定(DEBUG)
            </Button>
          </HStack>
        </Box>
      )}
      {/* ========== DEBUG END ========== */}

      <Box
        position="fixed"
        top="10vh"
        ml="2vw"
        mr="2vw"
        left="250px"
        width="calc(96vw - 250px)" // 左右の余白（4vw + 5vw）を引いた幅
        height="85vh"
        p={6}
        overflowY="auto"
        bg="white"
        textAlign="left"
      >
        <fieldset disabled={!pagePermissions?.isEditable}>
          <Box maxWidth={`${wrapWidth}px`} as="form" autoComplete="off" onSubmit={(e: any) => e.preventDefault()}>
            {/* <Box mb="10px" color="blue">
            <Text>{t('features.InvestmentScreening.BasicInfo.label.projectIdPrefix')}{projectBasicInfo.projectId} </Text>
          </Box> */}

            {/* 設備投資PROJECT名 */}
            <FormControl isInvalid={errors.projectName.isError}>
              <Flex align="center" >
                <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                  {t('features.InvestmentScreening.BasicInfo.field.projectName')}
                </FormLabel>
                <VStack align="left">
                  <Input
                    name="projectName"
                    value={projectBasicInfo.projectName || ""}
                    onChange={handleChangeProjectBasicInfo}
                    placeholder={t('features.InvestmentScreening.BasicInfo.label.projectNamePlaceholder')}
                    width={`${valueWidth}px`}
                    isInvalid={errors.projectName.isError}
                  />
                  <FormErrorMessage mt={0}>{errors.projectName.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <Wrap spacing={4} mt={4}>
              {/* 左側のフォーム */}
              <WrapItem width={`${wrapColumnWidth}px`}>
                <VStack align="flex-start" spacing={4}>
                  {/* 顧客名: 自由入力化に伴い、HelpIcon・Text表示・SelectorButtonをコメントアウト */}
                  {/* <FormControl isInvalid={errors.customerId.isError}>
                    <Flex align="center" >
                      <FormLabel style={labelStyle} width={`${labelWidth - 30 - 5}px`} >
                        顧客名
                      </FormLabel>
                      <Box width="30px" mr="5px">
                        <HelpIcon>
                          <Text>
                            {t('features.InvestmentScreening.BasicInfo.help.customerSelectHelp1')}<br />
                            {t('features.InvestmentScreening.BasicInfo.help.customerSelectHelp2')}
                          </Text>
                        </HelpIcon>
                      </Box>
                      <Box mt={2} width={`${valueWidth}px`} >
                        <Flex align="center" >
                          <Text fontWeight="bold" color="teal.500" width="80px">顧客名：</Text>
                          <Text fontWeight="bold" color="teal.500"
                            border={errors.customerId.isError ? "1px solid red" : ""}
                            width={`${valueWidth - 80}px`} height="24px">
                            {selectedCustomer?.customerName}
                          </Text>
                        </Flex>
                        <Flex align="center" >
                          <Text fontWeight="bold" color="teal.500" width="80px">正式社名：</Text>
                          <Text fontWeight="bold" color="teal.500"
                            border={errors.customerId.isError ? "1px solid red" : ""}
                            width={`${valueWidth - 80}px`} height="24px">
                            {selectedCustomer?.customerFullName}
                          </Text>
                        </Flex>
                        <FormErrorMessage mt={2}>{errors.customerId.errorMessage}</FormErrorMessage>
                      </Box>
                      <Text sx={countLinkStyle}>
                        <SelectorButton
                          title={t('features.InvestmentScreening.BasicInfo.label.selectCustomerName')} colorScheme="gray"
                          idName="customerId" displayName="customerFullName" subDisplayName="customerName"
                          listData={customers} prevSelectedItem={selectedCustomer}
                          handleSetSelection={handleConfirmSelection} handleFetchData={() => { }}
                        />
                      </Text>
                    </Flex>
                  </FormControl> */}
                  <FormControl isInvalid={errors.customerId.isError}>
                    <Flex>
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} mt="5px">
                        {t('features.InvestmentScreening.BasicInfo.field.customerName')}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          name="customerName"
                          value={projectBasicInfo.customerName ?? ""}
                          onChange={(e) => {
                            handleChangeProjectBasicInfo(e);
                            // 手動入力時は選択状態をクリア
                            if (selectedCustomer) {
                              setSelectedCustomer(null);
                              setSelectedOpportunity(null);
                              setSelectedCustomerProduct(null);
                              setFilteredCustomerOpportunities([]);
                              setFilteredCustomerProducts([]);
                            }
                          }}
                          placeholder={t('features.InvestmentScreening.BasicInfo.label.noData')}
                          width={`${valueWidth}px`}
                          isInvalid={errors.customerId.isError}
                        />
                        <FormErrorMessage mt={0}>{errors.customerId.errorMessage}</FormErrorMessage>
                      </VStack>
                      <VStack spacing={3} align="start" flexShrink={0}>
                        <SelectorButton
                          title={t('features.InvestmentScreening.BasicInfo.label.selectCustomerName')}
                          colorScheme="gray"
                          idName="customerId"
                          displayName="customerFullName"
                          subDisplayName="customerName"
                          listData={customers}
                          prevSelectedItem={selectedCustomer}
                          handleSetSelection={handleConfirmSelection}
                          handleFetchData={() => { }}
                        />
                      </VStack>
                    </Flex>
                  </FormControl>
                  <FormControl isInvalid={errors.customerFullName.isError}>
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.customerFullName')}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          name="customerFullName"
                          value={projectBasicInfo.customerFullName ?? ""}
                          onChange={handleChangeProjectBasicInfo}
                          placeholder={t('features.InvestmentScreening.BasicInfo.label.noData')}
                          width={`${valueWidth}px`}
                          isInvalid={errors.customerFullName.isError}
                        />
                        <FormErrorMessage mt={0}>{errors.customerFullName.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                  </FormControl>
                  <FormControl isInvalid={errors.customerProductId.isError}>
                    <Flex align="center">
                      <FormLabel style={{ ...labelStyle, position: "relative" }} width={`${labelWidth}px`}>
                        <Text as="span" display="block">
                          {t('features.InvestmentScreening.BasicInfo.field.customerProductName')}
                        </Text>
                        {filteredCustomerProducts.length > 0 && (
                          <Text
                            as="span"
                            display={isJapaneseLocale ? undefined : "block"}
                            textAlign="right"
                            fontSize="sm"
                            color="blue.500"
                            textDecoration="underline"
                            cursor={!pagePermissions?.isEditable ? "not-allowed" : "pointer"}
                            whiteSpace="nowrap"
                            fontWeight="normal"
                            {...(isJapaneseLocale
                              ? { position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)" }
                              : {})}
                            onClick={!pagePermissions?.isEditable ? undefined : (e) => {
                              e.preventDefault();
                              setTempSelectedCustomerProduct(selectedCustomerProduct);
                              setCustomerProductFilterValue("");
                              onCustomerProductModalOpen();
                            }}
                          >
                            {filteredCustomerProducts.length}{t('common.label.count')}
                          </Text>
                        )}
                      </FormLabel>
                      {/* 顧客製品名: 自由入力化に伴い、SelectorLinkをコメントアウト */}
                      {/* < SelectorLink
                        title="顧客製品名を選択"
                        displayName="customerProductName"
                        idName="customerProductId"
                        listData={filteredCustomerProducts}
                        handleSetSelection={handleSelectCustomerProduct}
                        prevSelectedItem={selectedCustomerProduct}
                      /> */}
                      <VStack align="left">
                        <Input
                          name="customerProductName"
                          value={projectBasicInfo.customerProductName ?? ""}
                          onChange={(e) => {
                            handleChangeProjectBasicInfo(e);
                            // 手動入力時は選択状態をクリア
                            if (selectedCustomerProduct) {
                              setSelectedCustomerProduct(null);
                            }
                          }}
                          placeholder={t('features.InvestmentScreening.BasicInfo.label.noData')}
                          width={`${valueWidth}px`}
                          isInvalid={errors.customerProductId.isError}
                        />
                        <FormErrorMessage mt={0}>{errors.customerProductId.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                    {/* 顧客製品選択モーダル */}
                    {isCustomerProductModalOpen && (
                      <Modal isOpen={isCustomerProductModalOpen} onClose={onCustomerProductModalClose} size="xl">
                        <ModalOverlay />
                        <ModalContent>
                          <ModalHeader>{t('features.InvestmentScreening.BasicInfo.modal.selectCustomerProduct')}</ModalHeader>
                          <ModalBody>
                            <VStack spacing={4}>
                              <Input placeholder={t('features.InvestmentScreening.BasicInfo.label.searchPlaceholder')} value={customerProductFilterValue} onChange={(e) => setCustomerProductFilterValue(e.target.value)} />
                              <Text fontWeight="bold" color="teal.500" alignSelf="flex-start">
                                {t('features.InvestmentScreening.BasicInfo.modal.selected')}: {tempSelectedCustomerProduct?.customerProductName ?? ""}
                              </Text>
                              <List spacing={3} overflowY="auto" maxHeight="300px" w="100%">
                                {filteredCustomerProducts
                                  .filter((r) => (r.customerProductName ?? "").toLowerCase().includes(customerProductFilterValue.toLowerCase()))
                                  .map((record) => (
                                    <ListItem
                                      as="button"
                                      display="block"
                                      w="100%"
                                      textAlign="left"
                                      key={record.customerProductId}
                                      p={2}
                                      borderRadius="md"
                                      bg={tempSelectedCustomerProduct?.customerProductId === record.customerProductId ? "teal.100" : "blackAlpha.100"}
                                      _hover={{ bg: "blue.100" }}
                                      onClick={() => setTempSelectedCustomerProduct(record)}
                                      cursor="pointer"
                                    >
                                      {record.customerProductName}
                                    </ListItem>
                                  ))}
                              </List>
                            </VStack>
                          </ModalBody>
                          <ModalFooter>
                            <Button colorScheme="blue" mr={1} ml={1}
                              isDisabled={!tempSelectedCustomerProduct}
                              onClick={() => {
                                handleSelectCustomerProduct(tempSelectedCustomerProduct);
                                onCustomerProductModalClose();
                              }}>
                              {t('common.button.confirmAction')}
                            </Button>
                            <Button colorScheme="gray" mr={1} ml={1} onClick={onCustomerProductModalClose}>
                              {t('common.button.close')}
                            </Button>
                          </ModalFooter>
                        </ModalContent>
                      </Modal>
                    )}
                  </FormControl>
                  <FormControl isInvalid={errors.opportunityId.isError}>
                    <Flex align="center">
                      <FormLabel style={{ ...labelStyle, position: "relative" }} width={`${labelWidth}px`}>
                        <Text as="span" display="block">
                          {t('features.InvestmentScreening.BasicInfo.field.opportunityName')}
                        </Text>
                        {filteredCustomerOpportunities.length > 0 && (
                          <Text
                            as="span"
                            display={isJapaneseLocale ? undefined : "block"}
                            textAlign="right"
                            fontSize="sm"
                            color="blue.500"
                            textDecoration="underline"
                            cursor={!pagePermissions?.isEditable ? "not-allowed" : "pointer"}
                            whiteSpace="nowrap"
                            fontWeight="normal"
                            {...(isJapaneseLocale
                              ? { position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)" }
                              : {})}
                            onClick={!pagePermissions?.isEditable ? undefined : (e) => {
                              e.preventDefault();
                              setTempSelectedOpportunity(selectedOpportunity);
                              setOpportunityFilterValue("");
                              onOpportunityModalOpen();
                            }}
                          >
                            {filteredCustomerOpportunities.length}{t('common.label.count')}
                          </Text>
                        )}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          name="opportunityName"
                          value={projectBasicInfo.opportunityName ?? ""}
                          placeholder={t('features.InvestmentScreening.BasicInfo.label.noData')}
                          width={`${valueWidth}px`}
                          onChange={(e) => {
                            handleChangeProjectBasicInfo(e);
                            // 手動入力時は選択状態をクリア
                            if (selectedOpportunity) {
                              setSelectedOpportunity(null);
                            }
                          }}
                          isInvalid={errors.opportunityId.isError}
                        />
                        <FormErrorMessage mt={0}>{errors.opportunityId.errorMessage}</FormErrorMessage>
                      </VStack>
                      {/* トピックス詳細: 案件がマスタ選択されている場合のみ活性化 */}
                      {/* minWidth: 日本語/英語で文言長が異なりボタン幅が変わると、二列/一列の切替タイミングが言語間でズレるため、両言語で同じ幅になるよう固定 */}
                      <Button ml={4} minWidth="100px" colorScheme="blue" onClick={handleOpenTopicModal} isDisabled={!selectedOpportunity?.opportunityId}>
                        {t('features.InvestmentScreening.BasicInfo.action.detail')}
                      </Button>
                    </Flex>
                    {/* 案件選択モーダル */}
                    {isOpportunityModalOpen && (
                      <Modal isOpen={isOpportunityModalOpen} onClose={onOpportunityModalClose} size="xl">
                        <ModalOverlay />
                        <ModalContent>
                          <ModalHeader>{t('features.InvestmentScreening.BasicInfo.modal.selectOpportunity')}</ModalHeader>
                          <ModalBody>
                            <VStack spacing={4}>
                              <Input placeholder={t('features.InvestmentScreening.BasicInfo.label.searchPlaceholder')} value={opportunityFilterValue} onChange={(e) => setOpportunityFilterValue(e.target.value)} />
                              <Text fontWeight="bold" color="teal.500" alignSelf="flex-start">
                                {t('features.InvestmentScreening.BasicInfo.modal.selected')}: {tempSelectedOpportunity?.opportunityName ?? ""}
                              </Text>
                              <List spacing={3} overflowY="auto" maxHeight="300px" w="100%">
                                {filteredCustomerOpportunities
                                  .filter((r) => (r.opportunityName ?? "").toLowerCase().includes(opportunityFilterValue.toLowerCase()))
                                  .map((record) => (
                                    <ListItem
                                      as="button"
                                      display="block"
                                      w="100%"
                                      textAlign="left"
                                      key={record.opportunityId}
                                      p={2}
                                      borderRadius="md"
                                      bg={tempSelectedOpportunity?.opportunityId === record.opportunityId ? "teal.100" : "blackAlpha.100"}
                                      _hover={{ bg: "blue.100" }}
                                      onClick={() => setTempSelectedOpportunity(record)}
                                      cursor="pointer"
                                    >
                                      {record.opportunityName}
                                    </ListItem>
                                  ))}
                              </List>
                            </VStack>
                          </ModalBody>
                          <ModalFooter>
                            <Button colorScheme="blue" mr={1} ml={1}
                              isDisabled={!tempSelectedOpportunity}
                              onClick={() => {
                                handleSelectOpportunity(tempSelectedOpportunity);
                                onOpportunityModalClose();
                              }}>
                              {t('common.button.confirmAction')}
                            </Button>
                            <Button colorScheme="gray" mr={1} ml={1} onClick={onOpportunityModalClose}>
                              {t('common.button.close')}
                            </Button>
                          </ModalFooter>
                        </ModalContent>
                      </Modal>
                    )}
                  </FormControl>
                  {/* End User名: SelectorButton から自由入力 Input に変更 */}
                  {/* <FormControl isInvalid={errors.endUserId.isError}>
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        End User名
                      </FormLabel>
                      <VStack align="left">
                        <Text
                          width={`${valueWidth}px`}
                          border={errors.endUserId.isError ? "1px solid red" : ""}
                          borderRadius="5"
                          pl="16px"
                          textColor={selectedEndUser?.customerName ? "inherit" : "#718096"}
                        >
                          {selectedEndUser?.customerName || t('features.InvestmentScreening.BasicInfo.label.noData')}
                        </Text>
                        <FormErrorMessage mt={0}>{errors.endUserId.errorMessage}</FormErrorMessage>
                      </VStack>
                      <SelectorButton
                        title={t('features.InvestmentScreening.BasicInfo.label.selectEndUserName')}
                        colorScheme="gray"
                        idName="customerId"
                        displayName="customerName"
                        listData={customers}
                        prevSelectedItem={selectedEndUser}
                        handleSetSelection={handleSelectEndUser}
                        handleFetchData={() => { }}
                      />
                    </Flex>
                  </FormControl> */}
                  {/* End User名: 複数入力対応 + SelectorButton復活 */}
                  <FormControl isInvalid={errors.endUserId.isError}>
                    <Flex>
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} mt="5px">
                        {t('features.InvestmentScreening.BasicInfo.field.endUserName')}
                      </FormLabel>
                      <VStack align="left">
                        <VStack maxHeight="140px" pr={endUserList.length > 3 ? 2 : 0}
                          width="435px" overflowY="auto">
                          {endUserList.map((name, idx) => {
                            const canDelete = endUserList.length > 1;
                            return (
                              <InputGroup key={idx}>
                                <Input
                                  value={name}
                                  onChange={(e) => changeEndUser(idx, e.target.value)}
                                  placeholder={t('features.InvestmentScreening.BasicInfo.label.noData')}
                                  width="100%"
                                  minHeight="40px"
                                  pr={canDelete ? "40px" : undefined}
                                  isInvalid={idx === 0 ? errors.endUserId.isError : false}
                                />
                                {canDelete && (
                                  <InputRightElement height="100%">
                                    <IconButton
                                      aria-label="Delete End User"
                                      icon={<RiDeleteBin6Fill />}
                                      size="sm"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => removeEndUserByIndex(idx)}
                                    />
                                  </InputRightElement>
                                )}
                              </InputGroup>
                            );
                          })}
                        </VStack>
                        <FormErrorMessage mt={0}>{errors.endUserId.errorMessage}</FormErrorMessage>
                      </VStack>
                      <IconButton
                        aria-label="Add End User"
                        icon={<FaPlusCircle />}
                        onClick={addEndUser}
                        colorScheme="blue"
                        ml={2}
                        mt="5px"
                        width="30px"
                        minWidth="30px"
                        height="30px"
                      />
                      <SelectorButton
                        title={t('features.InvestmentScreening.BasicInfo.label.selectEndUserName')}
                        colorScheme="gray"
                        idName="customerId"
                        displayName="customerName"
                        listData={customers}
                        prevSelectedItem={null}
                        handleSetSelection={handleSelectEndUser}
                        handleFetchData={() => { }}
                      />
                    </Flex>
                  </FormControl>
                  <FormControl isInvalid={errors.carModel.isError}>
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.carModel')}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          name="carModel"
                          value={projectBasicInfo.carModel ?? ""}
                          onChange={handleChangeProjectBasicInfo}
                          placeholder={t('features.InvestmentScreening.BasicInfo.label.noData')}
                          width={`${valueWidth}px`}
                          isInvalid={errors.carModel.isError}
                        />
                        <FormErrorMessage mt={0}>{errors.carModel.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                  </FormControl>
                  {/* 用途: 自由入力化に伴い、読み取り専用TextをInputに変更 */}
                  <Flex align="center">
                    <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                      {t('features.InvestmentScreening.BasicInfo.field.applicationCategory')}
                    </FormLabel>
                    <VStack align="left">
                      <Input
                        name="applicationCategory"
                        value={projectBasicInfo.applicationCategory ?? ""}
                        onChange={handleChangeProjectBasicInfo}
                        placeholder={t('features.InvestmentScreening.BasicInfo.label.noData')}
                        width={`${valueWidth}px`}
                      />
                    </VStack>
                  </Flex>
                  <FormControl isInvalid={errors.customerIndustry.isError}>
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.customerIndustry')}
                      </FormLabel>
                      <VStack align="left">
                        <Text
                          width={`${valueWidth}px`}
                          border={errors.customerIndustry.isError ? "1px solid red" : ""}
                          borderRadius="5"
                          pl="16px"
                          textColor={projectBasicInfo.customerIndustryHierarchy || productCategories.find(c => c.productCategoryId === projectBasicInfo.customerIndustry)?.productCategoryName ? "inherit" : "#718096"}
                        >
                          {projectBasicInfo.customerIndustryHierarchy
                            || productCategories.find(c => c.productCategoryId === projectBasicInfo.customerIndustry)?.productCategoryName
                            || t('features.InvestmentScreening.BasicInfo.label.noData')}
                        </Text>
                        <FormErrorMessage mt={0}>{errors.customerIndustry.errorMessage}</FormErrorMessage>
                      </VStack>
                      {/* minWidth: 日本語/英語で文言長が異なりボタン幅が変わると、二列/一列の切替タイミングが言語間でズレるため、両言語で同じ幅になるよう固定 */}
                      <Button colorScheme="blue" ml={4} minWidth="100px" onClick={openCustomerIndustryModal}>
                        {t('features.InvestmentScreening.BasicInfo.action.select')}
                      </Button>
                    </Flex>
                  </FormControl>
                </VStack>
              </WrapItem>

              {/* 右側のフォーム */}
              <WrapItem width={`${wrapColumnWidth}px`}>
                <VStack align="flex-start" spacing={4}>
                  <FormControl isInvalid={errors.mmProductCategoryId.isError}>
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.mmProductCategory')}
                      </FormLabel>
                      <VStack align="left">
                        <Text
                          width={`${valueWidth}px`}
                          border={errors.mmProductCategoryId.isError ? "1px solid red" : ""}
                          borderRadius="5"
                          pl="16px"
                          textColor={selectedMMIProductCategory?.MMProductCategoryName ? "inherit" : "#718096"}
                        >
                          {selectedMMIProductCategory?.MMProductCategoryName || t('features.InvestmentScreening.BasicInfo.label.noData')}
                        </Text>
                        <FormErrorMessage mt={0}>{errors.mmProductCategoryId.errorMessage}</FormErrorMessage>
                      </VStack>
                      <SelectorButton
                        title={t('features.InvestmentScreening.BasicInfo.label.selectMmiProductCategory')}
                        colorScheme="blue"
                        displayName="MMProductCategoryName"
                        idName="MMProductCategoryId"
                        listData={mmiProductCategories}
                        prevSelectedItem={selectedMMIProductCategory}
                        handleSetSelection={handleSelectMMIProductCategory}
                        handleFetchData={fetchMMIProductCategories}
                      />
                    </Flex>
                  </FormControl>
                  <FormControl isInvalid={errors.mmProductNameFreeWrite.isError}>
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.mmProductName')}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          name="mmProductNameFreeWrite"
                          value={projectBasicInfo.mmProductNameFreeWrite ?? ""}
                          onChange={handleChangeProjectBasicInfo}
                          width={`${valueWidth}px`}
                          isInvalid={errors.mmProductNameFreeWrite.isError}
                        />
                        <FormErrorMessage mt={0}>{errors.mmProductNameFreeWrite.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                  </FormControl>
                  <FormControl isInvalid={errors.partNo.isError}>
                    <Flex >
                      <FormLabel style={labelStyle} width={`${labelWidth - 30 - 5}px`} mt="5px" >
                        {t('features.InvestmentScreening.BasicInfo.field.mmiPartNo')}
                      </FormLabel>
                      {(() => {
                        const quantityScreenName = projectBasicInfo.equipmentUsageType === EquipmentUsageType.GeneralPurpose.code
                          ? t('features.InvestmentScreening.QuantityAndPrice.titleAssumed')
                          : t('features.InvestmentScreening.QuantityAndPrice.titleSales');
                        return (
                          <Box width="30px" mr="5px" mt="5px">
                            <HelpIcon width="550px">
                              <Text>
                                {t('features.InvestmentScreening.BasicInfo.help.partNoRegisterHelp')}
                                {'\n\n'}
                                {t('features.InvestmentScreening.BasicInfo.help.partNoWarning')}<br />
                                {quantityScreenName}{t('features.InvestmentScreening.BasicInfo.help.partNoWarningQuantity')}<br />
                                {t('features.InvestmentScreening.BasicInfo.help.partNoWarningNote')}
                              </Text>
                            </HelpIcon>
                          </Box>
                        );
                      })()}
                      {/* <Box width="30px" mr="5px" mt="5px">
                        <HelpIcon width="550px">
                          <Text>
                            {t('features.InvestmentScreening.BasicInfo.help.partNoRegisterHelp')}
                            {'\n\n'}
                            {t('features.InvestmentScreening.BasicInfo.help.partNoNotRegisterHelp', { quantityScreenName })}
                            {'\n\n'}
                            {t('features.InvestmentScreening.BasicInfo.help.partNoWarning')}<br />
                            {projectBasicInfo.equipmentUsageType === EquipmentUsageType.GeneralPurpose.code ? t('features.InvestmentScreening.QuantityAndPrice.titleAssumed') : t('features.InvestmentScreening.QuantityAndPrice.titleSales')}{t('features.InvestmentScreening.BasicInfo.help.partNoWarningQuantity')}<br />
                            {t('features.InvestmentScreening.BasicInfo.help.partNoWarningNote')}
                          </Text>
                        </HelpIcon>
                      </Box> */}
                      <VStack align="left">
                        <VStack maxHeight="140px" pr={(projectPartNo?.length ?? 0) > 3 ? 2 : 0} width="435px" overflowY="auto">
                          {projectPartNo.map((partNoInfo, idx: number) => {
                            const trimmedPartNo = partNoInfo?.partNo?.trim() || "";
                            const isLocked = lockedPartNos.includes(trimmedPartNo);
                            const canDelete = projectPartNo.length > 1 && !isLocked;
                            const isDisplayPartNoMode = displayPartNoToggleKeys.includes(trimmedPartNo);
                            // 過去Project紐づけでロックされたPartNoも変更後パーツNo（displayPartNo）の入力を可能にする
                            const canToggleDisplayPartNo = registeredPartNos.includes(trimmedPartNo) || isLocked;
                            return (
                              <Flex key={idx} direction="column" gap={0} width="100%">
                                <Flex gap={2} width="100%">
                                  <InputGroup>
                                    <Input
                                      onChange={(event) => {
                                        if (isDisplayPartNoMode) {
                                          if (isDuplicatePartNoValue(event.target.value, idx)) {
                                            changeErrors("partNo", t('features.InvestmentScreening.BasicInfo.validation.samePartNo'));
                                            return;
                                          }
                                          clearErrors("partNo");
                                          changeProjectDisplayPartNo(idx, event.target.value);
                                          return;
                                        }
                                        if (isDuplicatePartNoValue(event.target.value, idx)) {
                                          changeErrors("partNo", t('features.InvestmentScreening.BasicInfo.validation.samePartNo'));
                                          return;
                                        }
                                        clearErrors("partNo");
                                        changeProjectPartNo(idx, event.target.value);
                                      }}
                                      value={isDisplayPartNoMode ? (partNoInfo?.displayPartNo ?? "") : partNoInfo?.partNo}
                                      placeholder={isDisplayPartNoMode ? t('features.InvestmentScreening.BasicInfo.label.displayPartNoPlaceholder') : undefined}
                                      width="100%"
                                      minHeight="40px"
                                      maxLength={500}
                                      pr="72px"
                                      isInvalid={idx === 0 ? errors.partNo.isError : false}
                                      isReadOnly={isLocked && !isDisplayPartNoMode}
                                      bg={isLocked && !isDisplayPartNoMode ? "gray.100" : "white"}
                                      title={isLocked && !isDisplayPartNoMode ? t('features.InvestmentScreening.BasicInfo.label.lockedPartNoTitle') : ""}
                                    />
                                    <InputRightElement width="72px" height="100%" gap={1} justifyContent="flex-end" pr={1}>
                                      <Tooltip label={t('features.InvestmentScreening.BasicInfo.label.toggleDisplayPartNoTitle')}>
                                        <IconButton
                                          aria-label={t('features.InvestmentScreening.BasicInfo.label.toggleDisplayPartNoAriaLabel')}
                                          icon={<FiEdit3 />}
                                          size="sm"
                                          colorScheme={isDisplayPartNoMode ? "blue" : "gray"}
                                          variant={isDisplayPartNoMode ? "solid" : "ghost"}
                                          isDisabled={!canToggleDisplayPartNo}
                                          onClick={() => toggleDisplayPartNoMode(idx, trimmedPartNo)}
                                        />
                                      </Tooltip>
                                      {canDelete && (
                                        <IconButton
                                          aria-label="Delete PartNo."
                                          icon={<RiDeleteBin6Fill />}
                                          size="sm"
                                          colorScheme="red"
                                          variant="ghost"
                                          onClick={() => removePartNoByIndex(idx)}
                                        />
                                      )}
                                    </InputRightElement>
                                  </InputGroup>
                                </Flex>
                                {isDisplayPartNoMode && (
                                  <Flex align="center" gap={1} mt={1} color="gray.500" fontSize="sm">
                                    <FiEdit3 size={12} />
                                    <Text>{t('features.InvestmentScreening.BasicInfo.label.tentativePartNoLabel', { partNo: partNoInfo?.partNo })}</Text>
                                  </Flex>
                                )}
                              </Flex>
                            );
                          })}
                        </VStack>
                        <FormErrorMessage mt={0}>{errors.partNo.errorMessage}</FormErrorMessage>
                      </VStack>
                      <IconButton
                        aria-label="Add PartNo."
                        icon={<FaPlusCircle />}
                        onClick={() => addPartNo()}
                        colorScheme="blue"
                        ml={2}
                        mt="5px"
                        width="30px"
                        minWidth="30px"
                        height="30px"
                      />
                    </Flex>
                  </FormControl>
                  <FormControl isInvalid={errors.companyPlantId.isError}>
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.companyPlant')}
                      </FormLabel>
                      {/* 会社/工場  */}
                      <VStack align="left" maxH="130px" overflowY="auto" width={`${valueWidth}px`} >
                        <Box
                          border={errors.companyPlantId.isError ? "1px solid red" : ""}
                          borderRadius="5"
                          pl="16px"
                          py={1}
                        >
                          {(() => {
                            // スナップショット（複数対応型）から表示データを取得
                            const snapshotList = parseCompanyPlantSnapshot(projectBasicInfo?.companyPlantSnapshot);
                            if (snapshotList && snapshotList.items.length > 0) {
                              if (!snapshotList || snapshotList.items.length === 0) {
                                return (
                                  <>
                                    <Text as="span">{t('features.InvestmentScreening.BasicInfo.label.noData')}</Text>
                                  </>
                                );
                              }
                              return snapshotList.items.map((item, idx) => (
                                <React.Fragment key={idx}>
                                  <Text>
                                    {getBilingualDisplayName(item, 'companyName', 'companyNameEn')}
                                    {item.plantName ? ` / ${getBilingualDisplayName(item, 'plantName', 'plantNameEn')}` : ''}
                                  </Text>
                                </React.Fragment>
                              ));
                            }
                          })()}
                        </Box>
                        <FormErrorMessage mt={0}>{errors.companyPlantId.errorMessage}</FormErrorMessage>
                      </VStack>
                      <Button colorScheme="blue" ml={4} onClick={() => {
                        // モーダルを開く前に現在の選択状態で tempSelectedCompanyPlants を初期化
                        const snapshot = parseCompanyPlantSnapshot(projectBasicInfo?.companyPlantSnapshot);
                        const ids = (projectBasicInfo?.companyPlantId ?? "").split(",").map(id => id.trim()).filter(Boolean);
                        // companyPlants が未取得の場合はスナップショットから仮エントリーを作成して初期化
                        if (snapshot && snapshot.items.length > 0) {
                          const initList: CompanyPlantType[] = snapshot.items.map((item, idx) => ({
                            companyPlantId: ids[idx] ?? "",
                            companyPlantName: item.plantName
                              ? item.plantName
                              : (item.companyName ?? ""),
                            companyPlantNameEn: item.plantName
                              ? (item.plantNameEn ?? "")
                              : (item.companyNameEn ?? ""),
                            parentCompanyPlantId: item.plantName ? (ids[idx] ?? null) : null,
                            currencyId: item.currencyId ?? "",
                            locationCountry: item.locationCountry ?? "",
                            areaCode: item.areaCode ?? "",
                            taxRate: item.taxRate ?? undefined,
                          }));
                          setTempSelectedCompanyPlants(initList);
                          setFocusedTagIndex(0);
                        } else {
                          setTempSelectedCompanyPlants([]);
                          setFocusedTagIndex(null);
                        }
                        fetchCompanyPlants();
                        onCompanyPlantModalOpen();
                      }} minWidth="100px">
                        {t('features.InvestmentScreening.BasicInfo.action.select')}
                      </Button>
                    </Flex>
                  </FormControl>

                  {/* 通貨情報（有効通貨を表示） */}
                  {(() => {
                    const activeCurrencyId = parseCompanyPlantSnapshot(projectBasicInfo?.companyPlantSnapshot)?.activeCurrencyId;
                    //const activeCurrencyData = parseCompanyPlantSnapshot(projectBasicInfo?.companyPlantSnapshot)?.activeTaxRate;

                    if (!activeCurrencyId) {
                      return null;
                    }

                    const currency = (() => {
                      if (projectId && currencyDataById.length > 0) return currencyDataById?.find(cu => cu.currencyId === activeCurrencyId);
                      //if (projectId) return activeCurrencyData;
                      if (rateType === "plan") return previewPlanRates?.find(cu => cu.currencyId === activeCurrencyId);
                      if (rateType === "actual") return previewActualRates?.find(cu => cu.currencyId === activeCurrencyId);
                    })();

                    const formatRate = (rate?: number | null) => {

                      if (rate === null || rate === undefined) {
                        return "--";
                      }

                      const str = String(rate);
                      const parts = str.split(".");

                      if (parts.length === 1) {
                        return rate.toFixed(2);
                      }

                      const decimals = parts[1].length;

                      if (decimals <= 2) {
                        return rate.toFixed(2);
                      }

                      return str;
                    };

                    const formattedRate = formatRate(currency?.exchangeRate);

                    return (
                      <Text ml={188} mt={-3} fontSize="sm">
                        {t('features.InvestmentScreening.BasicInfo.field.bookkeepingCurrency')}：{currency?.currencyName ?? "--"}
                        {" / "}
                        {t('features.InvestmentScreening.BasicInfo.field.exchangeRate')}：{formattedRate}
                      </Text>
                    );
                  })()}

                  <FormControl isInvalid={errors.divisionId.isError}>
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.division')}
                      </FormLabel>
                      <VStack align="left">
                        <Text
                          width={`${valueWidth}px`}
                          border={errors.divisionId.isError ? "1px solid red" : ""}
                          borderRadius="5"
                          pl="16px"
                          textColor={getDivisionHierarchyDisplay(projectBasicInfo) || getBilingualDisplayName(selectedDivision, 'organizationName', 'organizationNameEn') ? "inherit" : "#718096"}
                        >
                          {getDivisionHierarchyDisplay(projectBasicInfo) || getBilingualDisplayName(selectedDivision, 'organizationName', 'organizationNameEn') || t('features.InvestmentScreening.BasicInfo.label.noData')}
                        </Text>
                        <FormErrorMessage mt={0}>{errors.divisionId.errorMessage}</FormErrorMessage>
                      </VStack>
                      <Button colorScheme="blue" ml={4} onClick={() => {
                        if (!projectBasicInfo.applicationMonth) {
                          toast({
                            title: TITLE,
                            description: t('features.InvestmentScreening.BasicInfo.message.applicationMonthFirst'),
                            status: 'warning',
                            duration: 5000,
                            isClosable: true,
                          });
                          return;
                        }
                        onDivisionModalOpen();
                      }} minWidth="100px">
                        {t('features.InvestmentScreening.BasicInfo.action.select')}
                      </Button>
                    </Flex>
                  </FormControl>
                  <FormControl isInvalid={errors.specificationAndPerformance.isError}>
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.specificationAndPerformance')}
                      </FormLabel>
                      <VStack align="left">
                        <Textarea
                          name="specificationAndPerformance"
                          value={projectBasicInfo.specificationAndPerformance ?? ""}
                          onChange={handleChangeProjectBasicInfo}
                          placeholder={t('features.InvestmentScreening.BasicInfo.label.specPlaceholder')}
                          resize="none"
                          width={`${valueWidth}px`}
                          isInvalid={errors.specificationAndPerformance.isError}
                        />
                        <FormErrorMessage mt={0}>{errors.specificationAndPerformance.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                  </FormControl>
                </VStack>
              </WrapItem>
            </Wrap>

            <VStack>
              {/* 申請番号 */}
              <FormControl isInvalid={errors.applicationNumber.isError} mt="20px">
                <Flex align="center">
                  <FormLabel style={labelStyle} width={`${labelWidth - 35}px`}>
                    {t('features.InvestmentScreening.BasicInfo.field.applicationNumber')}
                  </FormLabel>
                  <Box width="30px" mr="5px">
                    <HelpIcon width="420px">
                      <Text whiteSpace="pre-wrap">
                        {t('features.InvestmentScreening.BasicInfo.help.tooltipNotAutoGenerated')}
                      </Text>
                    </HelpIcon>
                  </Box>
                  <Input
                    name="applicationNumber"
                    value={projectBasicInfo.applicationNumber ?? ""}
                    onChange={handleChangeProjectBasicInfo}
                    placeholder={t('features.InvestmentScreening.BasicInfo.label.applicationNumberPlaceholder')}
                    width={`${valueWidth}px`}
                    isInvalid={errors.applicationNumber.isError}
                  />
                </Flex>
                <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.applicationNumber.errorMessage}</FormErrorMessage>
              </FormControl>

              {/* 設備計画番号: 複数入力対応 */}
              <FormControl isInvalid={errors.equipmentPlanNumber.isError} mt={4}>
                <Flex align="center">
                  <FormLabel style={labelStyle} width={`${labelWidth - 35}px`} mt="5px">
                    {t('features.InvestmentScreening.BasicInfo.field.equipmentPlanNumber')}
                  </FormLabel>
                  <Box width="30px" mr="5px">
                    <HelpIcon width="420px">
                      <Text whiteSpace="pre-wrap">
                        {t('features.InvestmentScreening.BasicInfo.help.tooltipNotAutoGenerated')}
                      </Text>
                    </HelpIcon>
                  </Box>
                  <VStack align="left">
                    <VStack maxHeight="140px" pr={equipmentPlanNumberList.length > 3 ? 2 : 0}
                      width={`${valueWidth}px`} overflowY="auto">
                      {equipmentPlanNumberList.map((value, idx) => {
                        const canDelete = equipmentPlanNumberList.length > 1;
                        return (
                          <InputGroup key={idx}>
                            <Input
                              value={value}
                              onChange={(event) => {
                                if (equipmentPlanNumberList.includes(event.target.value)) {
                                  changeErrors("equipmentPlanNumber", t('features.InvestmentScreening.BasicInfo.validation.sameEquipmentPlanNumber'));
                                  return;
                                }
                                changeEquipmentPlanNumber(idx, event.target.value);
                              }}
                              placeholder={t('features.InvestmentScreening.BasicInfo.label.equipmentPlanNumberPlaceholder')}
                              width="100%"
                              minHeight="40px"
                              pr={canDelete ? "40px" : undefined}
                              isInvalid={idx === 0 ? errors.equipmentPlanNumber.isError : false}
                            />
                            {canDelete && (
                              <InputRightElement height="100%">
                                <IconButton
                                  aria-label="Delete Equipment Plan Number"
                                  icon={<RiDeleteBin6Fill />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => removeEquipmentPlanNumberByIndex(idx)}
                                />
                              </InputRightElement>
                            )}
                          </InputGroup>
                        );
                      })}
                    </VStack>
                    <FormErrorMessage mt={0}>{errors.equipmentPlanNumber.errorMessage}</FormErrorMessage>
                  </VStack>
                  <IconButton
                    aria-label="Add Equipment Plan Number"
                    icon={<FaPlusCircle />}
                    onClick={addEquipmentPlanNumber}
                    colorScheme="blue"
                    ml={2}
                    mt="5px"
                    width="30px"
                    minWidth="30px"
                    height="30px"
                  />
                </Flex>
              </FormControl>

              {/* 計画予算 */}
              <FormControl mt={4}>
                <Flex align="flex-start">
                  <FormLabel style={labelStyle} width={`${labelWidth}px`} mt={2}>
                    {t('features.InvestmentScreening.BasicInfo.field.plannedBudget')}
                  </FormLabel>
                  <RadioGroup
                    name="plannedBudget"
                    value={projectBasicInfo.plannedBudget ?? ""}
                    onChange={(value) => changeProjectBasicInfo("plannedBudget", value)}
                  >
                    <Stack direction="row" spacing={8} mt={2}>
                      <Radio value={PlannedBudgetType.WithinBudget.code} isInvalid={errors.plannedBudget.isError}>{t('features.InvestmentScreening.BasicInfo.option.withinBudget')}</Radio>
                      <Radio value={PlannedBudgetType.OutsideBudget.code} isInvalid={errors.plannedBudget.isError}>{t('features.InvestmentScreening.BasicInfo.option.outsideBudget')}</Radio>
                    </Stack>
                    {errors.plannedBudget.isError && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {errors.plannedBudget.errorMessage}
                      </Text>
                    )}
                  </RadioGroup>
                  {/* 予算額入力欄 */}
                  <Box ml={8}>
                    <Flex align="center">
                      <CustomNumberInput
                        placeholder={t('features.InvestmentScreening.BasicInfo.field.budgetAmount')}
                        value={projectBasicInfo.budgetAmount ?? 0}
                        onChange={(e) => changeProjectBasicInfo("budgetAmount", Number(e) || null)}
                        isInvalid={errors.budgetAmount.isError}
                      />
                      <Text ml={2}>JPY</Text>
                    </Flex>
                    {errors.budgetAmount.isError && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {errors.budgetAmount.errorMessage}
                      </Text>
                    )}
                  </Box>
                </Flex>
              </FormControl>
              {/* 申請部署 */}
              <FormControl isInvalid={errors.registrantDivisionId.isError}>
                <Flex align="center">
                  <FormLabel style={labelStyle} width={`${labelWidth - 30 - 5}px`}>
                    {t('features.InvestmentScreening.BasicInfo.field.registrantDivision')}
                  </FormLabel>
                  <Box width="30px" mr="5px">
                    <HelpIcon width="420px">
                      <Text whiteSpace="pre-wrap">
                        {t('features.InvestmentScreening.BasicInfo.help.registrantDivisionHelp')}
                      </Text>
                    </HelpIcon>
                  </Box>
                  <VStack align="left">
                    <Select
                      value={selectedRegistrantDivisionId}
                      onChange={(e) => setSelectedRegistrantDivisionId(e.target.value)}
                      width={`${valueWidth}px`}
                      isInvalid={errors.registrantDivisionId.isError}
                    >
                      {loginUserAffiliations.map((affiliation) => (
                        <option key={affiliation.organizationId} value={affiliation.organizationId}>
                          {affiliation.organizationName}
                        </option>
                      ))}
                      {/* 保存済みの申請部署がloginUserAffiliationsに含まれない場合（承認者等が閲覧時）のフォールバック */}
                      {projectBasicInfo.registrantDivisionId &&
                        projectBasicInfo.registrantDivisionName &&
                        !loginUserAffiliations.some(a => a.organizationId === projectBasicInfo.registrantDivisionId) && (
                          <option value={projectBasicInfo.registrantDivisionId}>
                            {projectBasicInfo.registrantDivisionName}
                          </option>
                        )}
                    </Select>
                    <FormErrorMessage mt={0}>{errors.registrantDivisionId.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>
              {/* 申請月度 */}
              <FormControl isInvalid={errors.applicationMonth.isError} mt={4}>
                <Flex align="center">
                  <FormLabel style={labelStyle} width={`${labelWidth - 30 - 5}px`} >
                    {t('features.InvestmentScreening.BasicInfo.field.applicationMonth')}
                  </FormLabel>
                  <Box width="30px" mr="5px">
                    <HelpIcon width="420px">
                      <Text>
                        {t('features.InvestmentScreening.BasicInfo.help.applicationMonthHelp')}
                      </Text>
                    </HelpIcon>
                  </Box>
                  <Input
                    type="month"
                    name="applicationMonth"
                    value={projectBasicInfo.applicationMonth ?? ""}
                    onChange={(event) => {
                      if (event.target.value.length > 7) {
                        return;
                      }
                      // 申請月度が変更された場合、事業部選択をリセット
                      if (projectBasicInfo.applicationMonth && event.target.value !== projectBasicInfo.applicationMonth) {
                        if (selectedDivision) {
                          setSelectedDivision(null);
                          changeProjectBasicInfo("divisionId", "");
                          changeProjectBasicInfo("divisionHierarchy", "");
                          changeProjectBasicInfo("divisionHierarchyEn", "");
                          toast({
                            title: TITLE,
                            description: t('features.InvestmentScreening.BasicInfo.message.divisionSelectionReset'),
                            status: 'info',
                            duration: 5000,
                            isClosable: true,
                          });
                        }

                        if (divisions) {
                          setDivisions(null); // キャッシュをクリア（新しい年度で再取得するため）
                          setTempSelectedDivisionPath([]);
                        }
                      }
                      handleChangeProjectBasicInfo(event);
                    }}
                    width="162px"
                  />
                </Flex>
                <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.applicationMonth.errorMessage}</FormErrorMessage>
              </FormControl>
              {/* 量産開始日 */}
              <Flex align="flex-start" width="100%" mt={4}>
                <FormControl isInvalid={errors.projectStartDate.isError} width="480px">
                  <Flex align="center">
                    <FormLabel style={labelStyle} width={`${labelWidth}px`}>
                      {t('features.InvestmentScreening.BasicInfo.field.projectStartDate')}
                    </FormLabel>
                    <Input
                      type="date"
                      name="projectStartDate"
                      value={projectBasicInfo.projectStartDate ?? ""}
                      onChange={(event) => {
                        if (event.target.value.length > 10) {
                          return;
                        }
                        handleChangeProjectBasicInfo(event);
                      }}
                      isDisabled={Boolean(projectBasicInfo.pastProjectId)}
                      width="162px"
                    />
                  </Flex>
                  <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.projectStartDate.errorMessage}</FormErrorMessage>
                </FormControl>
                {projectBasicInfo.purpose === PurposeType.IncreasedProduction.code && projectBasicInfo.pastProjectId &&
                  <FormControl isInvalid={errors.increasedProductionStartDate.isError} ml="20px">
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.increasedProductionStartDate')}
                      </FormLabel>
                      <Input
                        width="162px"
                        type="date"
                        name="increasedProductionStartDate"
                        value={projectBasicInfo.increasedProductionStartDate ?? ""}
                        onChange={(event) => {
                          if (event.target.value.length > 10) {
                            return;
                          }
                          handleChangeProjectBasicInfo(event);
                        }}
                      />
                    </Flex>
                    <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.increasedProductionStartDate.errorMessage}</FormErrorMessage>
                  </FormControl>
                }

                {/* 更新投資量産開始日 */}
                {projectBasicInfo.purpose === PurposeType.UpdateAndOther.code && projectBasicInfo.pastProjectId &&
                  <FormControl isInvalid={errors.updateAndOtherStartDate.isError} ml="20px">
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.updateAndOtherStartDate')}
                      </FormLabel>
                      <Input
                        width="162px"
                        type="date"
                        name="updateAndOtherStartDate"
                        value={projectBasicInfo.updateAndOtherStartDate ?? ""}
                        onChange={(event) => {
                          if (event.target.value.length > 10) {
                            return;
                          }
                          handleChangeProjectBasicInfo(event);
                        }}
                      />
                    </Flex>
                    <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.updateAndOtherStartDate.errorMessage}</FormErrorMessage>
                  </FormControl>
                }

                {/* 合理化投資量産開始日 */}
                {projectBasicInfo.purpose === PurposeType.Rationalization.code && projectBasicInfo.pastProjectId &&
                  <FormControl isInvalid={errors.rationalizationStartDate.isError} ml="20px">
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.rationalizationStartDate')}
                      </FormLabel>
                      <Input
                        width="162px"
                        type="date"
                        name="rationalizationStartDate"
                        value={projectBasicInfo.rationalizationStartDate ?? ""}
                        onChange={(event) => {
                          if (event.target.value.length > 10) {
                            return;
                          }
                          handleChangeProjectBasicInfo(event);
                        }}
                      />
                    </Flex>
                    <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.rationalizationStartDate.errorMessage}</FormErrorMessage>
                  </FormControl>
                }

                {/* 新製品投資量産開始日 */}
                {projectBasicInfo.purpose === PurposeType.NewProduct.code && projectBasicInfo.pastProjectId &&
                  <FormControl isInvalid={errors.newProductStartDate.isError} ml="20px">
                    <Flex align="center">
                      <FormLabel style={labelStyle} width={`${labelWidth}px`} >
                        {t('features.InvestmentScreening.BasicInfo.field.newProductStartDate')}
                      </FormLabel>
                      <Input
                        width="162px"
                        type="date"
                        name="newProductStartDate"
                        value={projectBasicInfo.newProductStartDate ?? ""}
                        onChange={(event) => {
                          if (event.target.value.length > 10) {
                            return;
                          }
                          handleChangeProjectBasicInfo(event);
                        }}
                      />
                    </Flex>
                    <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.newProductStartDate.errorMessage}</FormErrorMessage>
                  </FormControl>
                }

              </Flex>

              <Flex align="flex-start" width="100%" mt="20px">
                {/* 計上期間終了日（変数名projectEndDateのまま。実態は計上期間の終端であり、設備の実際のEOL予定日とは異なる） */}
                <FormControl isInvalid={errors.projectEndDate.isError} width="480px">
                  <Flex align="center">
                    <FormLabel style={labelStyle} width={`${labelWidth}px`}>
                      {t('features.InvestmentScreening.BasicInfo.field.projectEndDate')}
                    </FormLabel>
                    <Input
                      type="date"
                      name="projectEndDate"
                      value={projectBasicInfo.projectEndDate ?? ""}
                      onChange={(event) => {
                        if (event.target.value.length > 10) {
                          return;
                        }
                        handleChangeProjectBasicInfo(event);
                      }}
                      width="162px"
                    />
                    <Box ml={1}>
                      <HelpIcon>
                        <Text whiteSpace="pre-wrap">
                          {t('features.InvestmentScreening.BasicInfo.help.projectEndDateDesc', {
                            startDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectStartDate'),
                            endDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectEndDate'),
                          })}
                        </Text>
                      </HelpIcon>
                    </Box>
                  </Flex>
                  <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.projectEndDate.errorMessage}</FormErrorMessage>
                </FormControl>
              </Flex>

              <Flex align="flex-start" width="100%" mt="20px">
                {/* EOL予定日（設備の実際のEOL予定日。計上期間終了日（projectEndDate）とは意味が異なる新規項目） */}
                <FormControl isInvalid={errors.eolDate.isError} width="480px">
                  <Flex align="center">
                    <FormLabel style={labelStyle} width={`${labelWidth}px`}>
                      {t('features.InvestmentScreening.BasicInfo.field.eolDate')}
                    </FormLabel>
                    <Input
                      type="date"
                      name="eolDate"
                      value={projectBasicInfo.eolDate ?? ""}
                      onChange={(event) => {
                        if (event.target.value.length > 10) {
                          return;
                        }
                        handleChangeProjectBasicInfo(event);
                      }}
                      width="162px"
                    />
                  </Flex>
                  <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.eolDate.errorMessage}</FormErrorMessage>
                </FormControl>
              </Flex>

              {/* 設備区分 */}
              <FormControl mt={4} isInvalid={errors.equipmentCategory.isError}>
                <Flex align="flex-start" width="100%">
                  <FormLabel style={labelStyle} width={`${labelWidth}px`} mt={2}>
                    {t('features.InvestmentScreening.BasicInfo.field.equipmentCategory')}
                  </FormLabel>
                  <CheckboxGroup
                    value={projectBasicInfo.equipmentCategory ? projectBasicInfo.equipmentCategory.split(",") : []}
                    onChange={(values: string[]) => {
                      changeProjectBasicInfo("equipmentCategory", values.join(","));
                    }}
                  >
                    <Flex wrap="wrap" gap={4} mt={2}>
                      {Object.values(EquipmentCategory).map(({ code }) => (
                        <Checkbox key={code} value={code}>
                          {getEquipmentCategoryNameByCode(code)}
                        </Checkbox>
                      ))}
                    </Flex>
                  </CheckboxGroup>
                </Flex>
                <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.equipmentCategory.errorMessage}</FormErrorMessage>
              </FormControl>

              {/* 設備用途区分 */}
              <FormControl mt={4} isInvalid={errors.equipmentUsageType.isError}>
                <Flex align="flex-start">
                  <FormLabel style={labelStyle} width={`${labelWidth}px`} mt={2}>
                    {t('features.InvestmentScreening.BasicInfo.field.equipmentUsageType')}
                  </FormLabel>
                  <RadioGroup
                    name="equipmentUsageType"
                    value={projectBasicInfo.equipmentUsageType ?? ""}
                    onChange={(value) => changeProjectBasicInfo("equipmentUsageType", value)}
                  >
                    <Stack direction="row" spacing={8} mt={2}>
                      <Radio value={EquipmentUsageType.Dedicated.code}>{getEquipmentUsageTypeNameByCode(EquipmentUsageType.Dedicated.code)}</Radio>
                      <Radio value={EquipmentUsageType.GeneralPurpose.code}>{getEquipmentUsageTypeNameByCode(EquipmentUsageType.GeneralPurpose.code)}</Radio>
                    </Stack>
                  </RadioGroup>
                </Flex>
                <FormErrorMessage mt={0} ml={`${labelWidth + 10}px`}>{errors.equipmentUsageType.errorMessage}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={errors.purpose.isError || errors.pastProjectId.isError}>
                <Flex align="flex-start" width="100%">
                  <Box height="40px" display="flex" alignItems="center">
                    <FormLabel style={labelStyle} minWidth={`${labelWidth - 30 - 5}px`}>
                      {t('features.InvestmentScreening.BasicInfo.field.purpose')}
                    </FormLabel>
                    <Box width="30px" mr="5px">
                      <HelpIcon>
                        <Text>
                          {t('features.InvestmentScreening.BasicInfo.help.purposeHelp1')}<br />
                          {t('features.InvestmentScreening.BasicInfo.help.purposeHelp2')}<br />
                          {t('features.InvestmentScreening.BasicInfo.help.purposeHelp3')}<br /><br />
                          {t('features.InvestmentScreening.BasicInfo.help.purposeHelp4')}<br />
                          {t('features.InvestmentScreening.BasicInfo.help.purposeHelp5')}<br />{t('features.InvestmentScreening.BasicInfo.help.purposeHelp6')}
                        </Text>
                      </HelpIcon>
                    </Box>
                  </Box>
                  <VStack align="left">
                    <ButtonGroup spacing={4} isAttached variant="outline" border={errors.purpose.isError ? "1px solid red" : "1px solid grey.100"} borderRadius="5">
                      {Object.entries(PurposeTypeCodeToNameMap).map(([code]) => (
                        <Button
                          key={code}
                          width={140}
                          onClick={() => {
                            changeProjectBasicInfo("purpose", code);
                          }}
                          variant={projectBasicInfo.purpose === code ? "solid" : "outline"}
                          colorScheme={projectBasicInfo.purpose === code ? "green" : "gray"}
                          // 振り返りモードでは目的変更不可（振り返りは申請時目的に対する実績検証のため）
                          isDisabled={Boolean(reviewDate)}
                        >
                          {getPurposeTypeNameByCode(code)}
                        </Button>
                      ))}
                    </ButtonGroup>
                    <FormErrorMessage mt={0}>{errors.purpose.errorMessage}</FormErrorMessage>
                  </VStack>

                  <VStack align="start" display={hiddenPastProject ? "none" : "block"}>
                    <HStack spacing={2}>
                      <Button
                        colorScheme="blue"
                        onClick={handlePastProject}
                        ml={2}
                        minW={150}
                        border={errors.pastProjectId.isError ? "2px solid red" : ""}
                      >
                        {t('features.InvestmentScreening.BasicInfo.field.pastProjectSelection')}
                      </Button>
                      {projectBasicInfo.pastProjectId && (
                        <HStack
                          bg="blue.50"
                          border="1px solid"
                          borderColor="blue.200"
                          borderRadius="md"
                          px={3}
                          py={1}
                          spacing={2}
                        >
                          <Text fontSize="sm" color="blue.700" fontWeight="medium">
                            {t('features.InvestmentScreening.BasicInfo.modal.currentlySelected')}: {projectBasicInfo.pastProjectName}
                            {projectBasicInfo.pastReviewDate
                              ? t('features.InvestmentScreening.BasicInfo.modal.pastReviewDateSuffix', { reviewDate: projectBasicInfo.pastReviewDate })
                              : ""}
                          </Text>
                          <IconButton
                            aria-label="Clear selection"
                            icon={<FaTimes />}
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => {
                              changeProjectBasicInfo("pastProjectId", null);
                              changeProjectBasicInfo("pastProjectName", null);
                              changeProjectBasicInfo("pastReviewDate", null);
                              setSelectedPastProject(null);
                              setLockedPartNos([]);
                            }}
                          />
                        </HStack>
                      )}
                    </HStack>
                    <FormErrorMessage mt={0} ml={2}>{errors.pastProjectId.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>
              {/* 入力方式（合理化・更新案件のみ表示） */}
              {(projectBasicInfo.purpose === PurposeType.Rationalization.code
                || projectBasicInfo.purpose === PurposeType.UpdateAndOther.code) && (
                  <FormControl mt={4}>
                    <Flex align="flex-start">
                      <Box height="40px" display="flex" alignItems="center">
                        <FormLabel style={labelStyle} minWidth={`${labelWidth - 30 - 5}px`}>
                          {t('features.InvestmentScreening.BasicInfo.field.applicationFormType')}
                        </FormLabel>
                        <Box width="30px" mr="5px">
                          <HelpIcon width="680px">
                            <Text fontSize="sm" whiteSpace="nowrap">
                              {t('features.InvestmentScreening.BasicInfo.help.applicationFormTypeHelp1')}<br />
                              {t('features.InvestmentScreening.BasicInfo.help.applicationFormTypeHelp2')}<br />
                              {t('features.InvestmentScreening.BasicInfo.help.applicationFormTypeHelp3')}<br />
                              {t('features.InvestmentScreening.BasicInfo.help.applicationFormTypeHelp4')}
                            </Text>
                          </HelpIcon>
                        </Box>
                      </Box>
                      <RadioGroup
                        name="applicationFormType"
                        value={projectBasicInfo.applicationFormType ?? ApplicationFormType.Detail.code}
                        onChange={(value) => changeProjectBasicInfo("applicationFormType", value)}
                        isDisabled={projectBasicInfo.approvalStatus === ApproveStatus.ApprovalComplete.code || Boolean(reviewDate)}
                      >
                        <Stack direction="row" spacing={8} mt={2}>
                          <Radio value={ApplicationFormType.Detail.code}>{ApplicationFormType.Detail.name}</Radio>
                          <Radio value={ApplicationFormType.Simple.code}>{ApplicationFormType.Simple.name}</Radio>
                        </Stack>
                      </RadioGroup>
                    </Flex>
                  </FormControl>
                )}
              {/* 損益表示（簡易様式選択時のみ表示） */}
              {isSimpleApplicationForm(projectBasicInfo.purpose, projectBasicInfo.applicationFormType) && (
                <FormControl mt={4}>
                  <Flex align="flex-start">
                    <Box height="40px" display="flex" alignItems="center">
                      <FormLabel style={labelStyle} minWidth={`${labelWidth - 30 - 5}px`}>
                        {t('features.InvestmentScreening.BasicInfo.field.profitLossDisplayType')}
                      </FormLabel>
                      <Box width="30px" mr="5px">
                        <HelpIcon width="680px">
                          <Text fontSize="sm" whiteSpace="nowrap">
                            {t('features.InvestmentScreening.BasicInfo.help.profitLossDisplayTypeHelp1')}<br />
                            {t('features.InvestmentScreening.BasicInfo.help.profitLossDisplayTypeHelp2')}<br />
                            {t('features.InvestmentScreening.BasicInfo.help.profitLossDisplayTypeHelp3')}
                          </Text>
                        </HelpIcon>
                      </Box>
                    </Box>
                    <RadioGroup
                      name="profitLossDisplayType"
                      value={projectBasicInfo.profitLossDisplayType ?? ProfitLossDisplayType.Show.code}
                      onChange={(value) => changeProjectBasicInfo("profitLossDisplayType", value)}
                      isDisabled={projectBasicInfo.approvalStatus === ApproveStatus.ApprovalComplete.code || Boolean(reviewDate)}
                    >
                      <Stack direction="row" spacing={8} mt={2}>
                        <Radio value={ProfitLossDisplayType.Show.code}>{ProfitLossDisplayType.Show.name}</Radio>
                        <Radio value={ProfitLossDisplayType.Hide.code}>{ProfitLossDisplayType.Hide.name}</Radio>
                      </Stack>
                    </RadioGroup>
                  </Flex>
                </FormControl>
              )}
              {/* 投資目的 */}
              <FormControl mt={4} isInvalid={errors.purposeOfInvestment.isError}>
                <Flex width="100%" >
                  <FormLabel style={labelStyle} width={`${labelWidth}px`} mt="5px">
                    {t('features.InvestmentScreening.BasicInfo.field.purposeOfInvestment')}
                  </FormLabel>
                  <VStack align="stretch" width="100%">
                    <Textarea
                      name="purposeOfInvestment"
                      value={projectBasicInfo.purposeOfInvestment ?? ""}
                      onChange={handleChangeProjectBasicInfo}
                      placeholder={t('features.InvestmentScreening.BasicInfo.label.purposeOfInvestmentPlaceholder')}
                      resize="none"
                      mr={`${40 + (16 * 2) + 7}px`}
                      width="100%"
                      minWidth={`${valueWidth}px`}
                      flexShrink={2}
                      isInvalid={errors.purposeOfInvestment.isError}
                    />
                    <FormErrorMessage mt={0}>{errors.purposeOfInvestment.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              {/* 確認者一覧 */}
              <FormControl mt={2}>
                <Flex align="top" width="100%" >
                  <HStack spacing={1} align="center" flexShrink={0} mr={3}>
                    <FormLabel style={labelStyle} m={0} minWidth={`${labelWidth}px`} >
                      {t('features.InvestmentScreening.BasicInfo.field.confirmors')}
                    </FormLabel>
                    <HelpIcon width="620px">
                      <Box fontSize="sm">
                        <Text>
                          {t('features.InvestmentScreening.BasicInfo.help.confirmorsHelp1')}
                        </Text>
                      </Box>
                    </HelpIcon>
                  </HStack>
                  <VStack align="stretch" width="100%">
                    <HStack justifyContent="flex-start">
                      <Button
                        size="sm"
                        colorScheme="gray"
                        onClick={() => openSpreadSheetModal('features.InvestmentScreening.BasicInfo.label.confirmerManualName')}
                      >
                        {t('features.InvestmentScreening.BasicInfo.label.confirmerManualName')}
                      </Button>
                    </HStack>
                    {dispConfirmor?.length > 0 ? (
                      <Box borderWidth="1px" borderRadius="md" overflow="hidden" borderColor="gray.200">
                        <Table colorScheme="gray">
                          <Thead>
                            <Tr>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="5%" textAlign="center">No.</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="20%">{t('features.InvestmentScreening.BasicInfo.label.confirmCategory')}</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="35%">{t('features.InvestmentScreening.BasicInfo.label.employeeName')}</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="20%" textAlign="center">{t('features.InvestmentScreening.BasicInfo.label.previousSentDate')}</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="20%" textAlign="center">{t('features.InvestmentScreening.BasicInfo.label.inputRequestMail')}</Th>
                            </Tr>
                          </Thead>

                          <Tbody>
                            {getGroupedAndSortedConfirmors(dispConfirmor).map(([divisionId, employees], divisionIndex) => {
                              const no = divisionIndex + 1;
                              return employees.map((employee: Confirmor, index) => (
                                <Tr key={`${divisionId}-${index}`}>
                                  {/* No.列表示。一つのdivisionIdにつき最初の行のみ表示 */}
                                  {index === 0 && (
                                    <Td
                                      rowSpan={employees.length}
                                      borderRight="1px solid"
                                      borderColor="gray.200"
                                      textAlign="center"
                                      verticalAlign="middle"
                                    >
                                      {no}
                                    </Td>
                                  )}
                                  {/* 部署IDセルの結合 */}
                                  {index === 0 && (
                                    <Td
                                      rowSpan={employees.length}
                                      borderRight="1px solid"
                                      borderColor="gray.200"
                                      textAlign="left"
                                      verticalAlign="middle"
                                    >
                                      {employee.confirmKbn}
                                    </Td>
                                  )}
                                  <Td borderRight="1px solid" borderColor="gray.200">
                                    {employee.employeeName}
                                  </Td>
                                  {/* 前回送信日時列：確認区分ごとに最初の行のみ表示 */}
                                  {index === 0 && (
                                    <Td
                                      rowSpan={employees.length}
                                      borderRight="1px solid"
                                      borderColor="gray.200"
                                      textAlign="center"
                                      verticalAlign="middle"
                                    >
                                      {(() => {
                                        const status = mailSentStatus.find(s => s.divisionId === divisionId);
                                        if (status?.sentAt) {
                                          // sentAtは送信直後・DB取得時のいずれもUTCのISO 8601形式（Z付き）で統一されているため、
                                          // dayjsでパースするとブラウザのタイムゾーンに自動変換される
                                          const formattedDate = formatDateByLocale(status.sentAt, true);
                                          return (
                                            <VStack spacing={0}>
                                              <Text>
                                                {formattedDate}
                                              </Text>
                                              {status.sentByEmployeeName && (
                                                <Text fontSize="xs">
                                                  {t('features.InvestmentScreening.BasicInfo.label.sender')}： {status.sentByEmployeeName}
                                                </Text>
                                              )}
                                            </VStack>
                                          );
                                        }
                                        return <Text color="gray.400" fontSize="sm">-</Text>;
                                      })()}
                                    </Td>
                                  )}
                                  {/* 操作列：確認区分ごとに最初の行のみ表示 */}
                                  {index === 0 && (
                                    <Td
                                      rowSpan={employees.length}
                                      borderRight="1px solid"
                                      borderColor="gray.200"
                                      textAlign="center"
                                      verticalAlign="middle"
                                    >
                                      {(() => {
                                        // 記入者確認未完了（projectIdがない場合）
                                        const isRegistrantNotConfirmed = !projectId;

                                        // 送信中かどうか
                                        const isSending = sendingMailDivisionId === divisionId;

                                        // 確認者に未保存の変更があるか（dispConfirmor と savedConfirmor を比較）
                                        const hasUnsavedChanges = (() => {
                                          if (!savedConfirmor || !dispConfirmor) return false;
                                          const savedIds = savedConfirmor.map((c: Confirmor) => c.employeeId).sort();
                                          const dispIds = dispConfirmor.map((c: Confirmor) => c.employeeId).sort();
                                          return JSON.stringify(savedIds) !== JSON.stringify(dispIds);
                                        })();

                                        // 担当者未選択の場合：「-」を表示
                                        if (employees.length === 0 || !employees[0]?.employeeId) {
                                          return <Text color="gray.400" fontSize="sm">-</Text>;
                                        }

                                        // 送信ボタン表示（再送信は常に可能）
                                        return (
                                          <VStack spacing={1}>
                                            <Button
                                              size="sm"
                                              colorScheme="blue"
                                              isDisabled={isRegistrantNotConfirmed || isSending || hasUnsavedChanges}
                                              isLoading={isSending}
                                              onClick={() => {
                                                // 送信対象の従業員情報を設定してモーダルを開く
                                                const employeeIds = employees.map((e: Confirmor) => e.employeeId);
                                                const employeeNames = employees.map((e: Confirmor) => e.employeeName || '');
                                                setMailConfirmTarget({
                                                  divisionId,
                                                  divisionName: employees[0]?.confirmKbn || '',
                                                  employeeIds,
                                                  employeeNames,
                                                });
                                                onMailConfirmOpen();
                                              }}
                                            >
                                              {t('features.InvestmentScreening.BasicInfo.action.sendMail')}
                                            </Button>
                                            {hasUnsavedChanges && (
                                              <Text fontSize="xs" color="red.500" textAlign="center">
                                                {t('features.InvestmentScreening.BasicInfo.label.saveBeforeSend')}
                                              </Text>
                                            )}
                                          </VStack>
                                        );
                                      })()}
                                    </Td>
                                  )}
                                </Tr>
                              ));
                            })}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : (
                      <Text color="gray.500" mt={2}>{t('features.InvestmentScreening.BasicInfo.label.noConfirmors')}</Text>
                    )}
                  </VStack>
                </Flex>
              </FormControl>

              {/* クロスチェック要否 */}
              <FormControl mt={2}>
                <Flex align="flex-start">
                  <HStack spacing={1} mt={2} align="center" flexShrink={0} mr={3}>
                    <FormLabel style={labelStyle} m={0} minWidth="160px">
                      {t('features.InvestmentScreening.BasicInfo.field.crossCheckRequired')}
                    </FormLabel>
                    <HelpIcon width="620px">
                      <Box fontSize="sm">
                        <Text mb={2} style={{ textIndent: "-2.2em", paddingLeft: "2.2em" }}>
                          {t('features.InvestmentScreening.BasicInfo.help.crossCheckHelp1')}
                        </Text>
                        <Text mb={2} style={{ textIndent: "-2.2em", paddingLeft: "2.2em" }}>
                          {t('features.InvestmentScreening.BasicInfo.help.crossCheckHelp2')}
                        </Text>
                        <Text mb={2} style={{ textIndent: "-2.2em", paddingLeft: "2.2em" }}>
                          {t('features.InvestmentScreening.BasicInfo.help.crossCheckHelp3')}
                        </Text>
                        <Text style={{ textIndent: "-2.2em", paddingLeft: "2.2em" }}>
                          {t('features.InvestmentScreening.BasicInfo.help.crossCheckHelp4')}
                        </Text>
                      </Box>
                    </HelpIcon>
                  </HStack>
                  <VStack align="flex-start" spacing={2} width="100%">
                    <HStack spacing={4} align="center">
                      <RadioGroup
                        name="crossCheckRequired"
                        value={projectBasicInfo.crossCheckRequired ? "1" : "0"}
                        onChange={(value) => {
                          const isRequired = value === "1";
                          changeProjectBasicInfo("crossCheckRequired", isRequired);
                          if (!isRequired) {
                            changeProjectBasicInfo("crossCheckDepartments", "");
                          }
                        }}
                      >
                        <Stack direction="row" spacing={8} mt={2}>
                          <Radio value="1" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}>{t('features.InvestmentScreening.BasicInfo.option.required')}</Radio>
                          <Radio value="0" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}>{t('features.InvestmentScreening.BasicInfo.option.notRequired')}</Radio>
                        </Stack>
                      </RadioGroup>
                      {projectBasicInfo.crossCheckRequired && (
                        <Button
                          size="sm"
                          colorScheme="blue"
                          mt={2}
                          isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant || !(projectBasicInfo.crossCheckDepartments ?? "").length}
                          onClick={onOpenCrossCheckAssignModal}
                        >
                          {t('features.InvestmentScreening.BasicInfo.action.confirmerSelection')}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        colorScheme="gray"
                        mt={2}
                        onClick={() => openSpreadSheetModal('features.InvestmentScreening.BasicInfo.label.crossCheckManualName')}
                      >
                        {t('features.InvestmentScreening.BasicInfo.label.crossCheckManualName')}
                      </Button>
                    </HStack>
                    {projectBasicInfo.crossCheckRequired && (
                      <>
                        <CheckboxGroup
                          value={projectBasicInfo.crossCheckDepartments ? projectBasicInfo.crossCheckDepartments.split(",") : []}
                          onChange={(values: string[]) => {
                            changeProjectBasicInfo("crossCheckDepartments", values.join(","));
                          }}
                        >
                          <Stack direction="row" spacing={8} ml={2}>
                            <Checkbox value="manufacturing" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}>{t('features.InvestmentScreening.BasicInfo.category.manufacturing')}</Checkbox>
                            <Checkbox value="engineering" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}>{t('features.InvestmentScreening.BasicInfo.category.engineering')}</Checkbox>
                            <Checkbox value="it" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}>{t('features.InvestmentScreening.BasicInfo.category.it')}</Checkbox>
                          </Stack>
                        </CheckboxGroup>
                      </>
                    )}
                  </VStack>
                </Flex>
              </FormControl>
              {projectBasicInfo.crossCheckRequired && (projectBasicInfo.crossCheckDepartments ?? "").length > 0 && (
                <FormControl mt={2}>
                  <Flex align="top" width="100%">
                    <FormLabel style={labelStyle} minWidth={`${labelWidth}px`}>
                      {t('features.InvestmentScreening.BasicInfo.field.ccAssignees')}
                    </FormLabel>
                    <VStack align="stretch" width="100%">
                      <Box borderWidth="1px" borderRadius="md" overflow="hidden" borderColor="gray.200">
                        <Table colorScheme="gray">
                          <Thead>
                            <Tr>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="5%" textAlign="center">No.</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="20%">{t('features.InvestmentScreening.BasicInfo.label.department')}</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">{t('features.InvestmentScreening.BasicInfo.label.employeeName')}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {(() => {
                              const deptOrder = ["manufacturing", "engineering", "it"];
                              const checkedDepts = (projectBasicInfo.crossCheckDepartments ?? "").split(",").filter(v => v)
                                .sort((a, b) => deptOrder.indexOf(a) - deptOrder.indexOf(b));
                              const deptNames: Record<string, string> = { manufacturing: t('features.InvestmentScreening.BasicInfo.category.manufacturing'), engineering: t('features.InvestmentScreening.BasicInfo.category.engineering'), it: t('features.InvestmentScreening.BasicInfo.category.it') };
                              return checkedDepts.map((deptId, deptIndex) => {
                                const assignee = crossCheckAssignees.find(a => a.categoryId === deptId);
                                const employees = assignee?.employees || [];
                                if (employees.length === 0) {
                                  return (
                                    <Tr key={deptId}>
                                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="center" verticalAlign="middle">{deptIndex + 1}</Td>
                                      <Td borderRight="1px solid" borderColor="gray.200" textAlign="left" verticalAlign="middle">{deptNames[deptId] || deptId}</Td>
                                      <Td borderRight="1px solid" borderColor="gray.200" color="gray.400">—</Td>
                                    </Tr>
                                  );
                                }
                                return employees.map((emp, empIndex) => (
                                  <Tr key={`${deptId}-${empIndex}`}>
                                    {empIndex === 0 && (
                                      <Td rowSpan={employees.length} borderRight="1px solid" borderColor="gray.200" textAlign="center" verticalAlign="middle">{deptIndex + 1}</Td>
                                    )}
                                    {empIndex === 0 && (
                                      <Td rowSpan={employees.length} borderRight="1px solid" borderColor="gray.200" textAlign="left" verticalAlign="middle">{assignee?.categoryName || deptNames[deptId] || deptId}</Td>
                                    )}
                                    <Td borderRight="1px solid" borderColor="gray.200">{emp.employeeName}</Td>
                                  </Tr>
                                ));
                              });
                            })()}
                          </Tbody>
                        </Table>
                      </Box>
                    </VStack>
                  </Flex>
                </FormControl>
              )}

              {/* 資材担当役員 */}
              <FormControl mt={2}>
                <Flex align="flex-start">
                  <HStack spacing={1} mt={2} align="center" flexShrink={0} mr={3}>
                    <FormLabel style={labelStyle} m={0} minWidth="160px">
                      {t('features.InvestmentScreening.BasicInfo.field.materialOfficerRequired')}
                    </FormLabel>
                    <HelpIcon>
                      <Text>
                        {t('features.InvestmentScreening.BasicInfo.help.materialOfficerHelp1')}<br />{t('features.InvestmentScreening.BasicInfo.help.materialOfficerHelp2')}
                      </Text>
                      <Text>
                        {t('features.InvestmentScreening.BasicInfo.help.materialOfficerHelp3')}
                      </Text>
                      <Text>
                        {t('features.InvestmentScreening.BasicInfo.help.materialOfficerHelp4')}
                      </Text>
                    </HelpIcon>
                  </HStack>
                  <VStack align="flex-start" spacing={2} width="100%">
                    <HStack spacing={4} align="center">
                      <RadioGroup
                        name="materialOfficerRequired"
                        value={projectBasicInfo.materialOfficerRequired ? "1" : "0"}
                        onChange={(value) => changeProjectBasicInfo("materialOfficerRequired", value === "1")}
                      >
                        <Stack direction="row" spacing={8} mt={2}>
                          <Radio value="1" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}>{t('features.InvestmentScreening.BasicInfo.option.required')}</Radio>
                          <Radio value="0" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}>{t('features.InvestmentScreening.BasicInfo.option.notRequired')}</Radio>
                        </Stack>
                      </RadioGroup>
                      {projectBasicInfo.materialOfficerRequired && (
                        <Button
                          size="sm"
                          colorScheme="blue"
                          mt={2}
                          isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}
                          onClick={onOpenMaterialOfficerAssignModal}
                        >
                          {t('features.InvestmentScreening.BasicInfo.action.confirmerSelection')}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        colorScheme="gray"
                        mt={2}
                        onClick={() => openSpreadSheetModal('features.InvestmentScreening.BasicInfo.label.materialOfficerManualName')}
                      >
                        {t('features.InvestmentScreening.BasicInfo.label.materialOfficerManualName')}
                      </Button>
                    </HStack>
                  </VStack>
                </Flex>
              </FormControl>
              {projectBasicInfo.materialOfficerRequired && materialOfficerAssignees.filter(a => a.employees.length > 0).length > 0 && (
                <FormControl mt={2}>
                  <Flex align="top" width="100%">
                    <FormLabel style={labelStyle} minWidth={`${labelWidth}px`}>
                      {t('features.InvestmentScreening.BasicInfo.field.materialOfficerAssignees')}
                    </FormLabel>
                    <VStack align="stretch" width="100%">
                      <Box borderWidth="1px" borderRadius="md" overflow="hidden" borderColor="gray.200">
                        <Table colorScheme="gray">
                          <Thead>
                            <Tr>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="5%" textAlign="center">No.</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="20%">{t('features.InvestmentScreening.BasicInfo.label.department')}</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">{t('features.InvestmentScreening.BasicInfo.label.employeeName')}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {materialOfficerAssignees.filter(a => a.employees.length > 0).map((assignee, deptIndex) => (
                              assignee.employees.map((emp, empIndex) => (
                                <Tr key={`material-${assignee.categoryId}-${empIndex}`}>
                                  {empIndex === 0 && (
                                    <Td
                                      rowSpan={assignee.employees.length}
                                      borderRight="1px solid"
                                      borderColor="gray.200"
                                      textAlign="center"
                                      verticalAlign="middle"
                                    >
                                      {deptIndex + 1}
                                    </Td>
                                  )}
                                  {empIndex === 0 && (
                                    <Td
                                      rowSpan={assignee.employees.length}
                                      borderRight="1px solid"
                                      borderColor="gray.200"
                                      textAlign="left"
                                      verticalAlign="middle"
                                    >
                                      {getMaterialOfficerCategories().find(c => c.id === assignee.categoryId)?.label || assignee.categoryName}
                                    </Td>
                                  )}
                                  <Td borderRight="1px solid" borderColor="gray.200">
                                    {emp.employeeName}
                                  </Td>
                                </Tr>
                              ))
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </VStack>
                  </Flex>
                </FormControl>
              )}

              {/* 画面閲覧制限（制限している画面名は営業も見える基本情報には出さず、モーダル内だけで扱う） */}
              <FormControl mt={2}>
                <Flex align="flex-start">
                  <HStack spacing={1} mt={2} align="center" flexShrink={0} mr={3}>
                    <FormLabel style={labelStyle} m={0} minWidth="160px">
                      {t('features.InvestmentScreening.BasicInfo.field.screenViewRestriction')}
                    </FormLabel>
                    <HelpIcon>
                      <Text>
                        {t('features.InvestmentScreening.BasicInfo.help.screenViewRestrictionHelp')}
                      </Text>
                    </HelpIcon>
                  </HStack>
                  <HStack spacing={4} align="center">
                    <RadioGroup
                      name="screenViewRestriction"
                      value={isViewRestrictionEnabled ? "1" : "0"}
                      onChange={(value) => {
                        const isEnabled = value === "1";
                        setIsViewRestrictionEnabled(isEnabled);
                        if (!isEnabled) {
                          changeProjectBasicInfo("screenViewRestriction", null);
                        }
                      }}
                    >
                      <Stack direction="row" spacing={8} mt={2}>
                        <Radio value="1" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}>{t('features.InvestmentScreening.BasicInfo.option.restrict')}</Radio>
                        <Radio value="0" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}>{t('features.InvestmentScreening.BasicInfo.option.notRestrict')}</Radio>
                      </Stack>
                    </RadioGroup>
                    {isViewRestrictionEnabled && (
                      <>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          mt={2}
                          isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}
                          onClick={onOpenViewRestrictionModal}
                        >
                          {t('features.InvestmentScreening.BasicInfo.action.viewRestrictionSetting')}
                        </Button>
                        <Text mt={2} fontSize="sm" color={projectBasicInfo.screenViewRestriction != null ? "blue.600" : "red.500"}>
                          {projectBasicInfo.screenViewRestriction != null
                            ? t('features.InvestmentScreening.BasicInfo.label.viewRestrictionConfigured')
                            : t('features.InvestmentScreening.BasicInfo.label.viewRestrictionNotConfigured')}
                        </Text>
                      </>
                    )}
                  </HStack>
                </Flex>
              </FormControl>

              {/* 承認者一覧 */}
              <FormControl mt={2}>
                <Flex align="top" width="100%">
                  <FormLabel style={labelStyle} minWidth={`${labelWidth}px`}>
                    {t('features.InvestmentScreening.BasicInfo.field.approvers')}
                  </FormLabel>
                  <VStack align="stretch" width="100%">
                    <HStack justifyContent="flex-start">
                      <Button
                        size="sm"
                        colorScheme="gray"
                        onClick={() => openSpreadSheetModal('features.InvestmentScreening.BasicInfo.label.approverManualName')}
                      >
                        {t('features.InvestmentScreening.BasicInfo.label.approverManualName')}
                      </Button>
                    </HStack>
                    {approverAssignees.filter(a => a.employees.length > 0).length > 0 ? (
                      <Box borderWidth="1px" borderRadius="md" overflow="hidden" borderColor="gray.200">
                        <Table colorScheme="gray">
                          <Thead>
                            <Tr>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="5%" textAlign="center">No.</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100" width="20%">{t('features.InvestmentScreening.BasicInfo.label.approvalCategory')}</Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.100">{t('features.InvestmentScreening.BasicInfo.label.employeeName')}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {approverAssignees.filter(a => a.employees.length > 0).map((assignee, deptIndex) => (
                              assignee.employees.map((emp, empIndex) => (
                                <Tr key={`approver-${assignee.categoryId}-${empIndex}`}>
                                  {empIndex === 0 && (
                                    <Td
                                      rowSpan={assignee.employees.length}
                                      borderRight="1px solid"
                                      borderColor="gray.200"
                                      textAlign="center"
                                      verticalAlign="middle"
                                    >
                                      {deptIndex + 1}
                                    </Td>
                                  )}
                                  {empIndex === 0 && (
                                    <Td
                                      rowSpan={assignee.employees.length}
                                      borderRight="1px solid"
                                      borderColor="gray.200"
                                      textAlign="left"
                                      verticalAlign="middle"
                                    >
                                      {getApproverCategories().find(c => c.id === assignee.categoryId)?.label || assignee.categoryName}
                                    </Td>
                                  )}
                                  <Td borderRight="1px solid" borderColor="gray.200">
                                    {emp.employeeName}
                                  </Td>
                                </Tr>
                              ))
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : (
                      <Text color="gray.500" mt={2}>{t('features.InvestmentScreening.BasicInfo.label.noApprovers')}</Text>
                    )}
                  </VStack>
                </Flex>
              </FormControl>
            </VStack>

            {/* 下部ボタン */}
            <HStack pt={8} justifyContent="flex-end" spacing={4}>
              <Button
                colorScheme="blue"
                isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant || !!reviewDate}
                onClick={(e) => handlePutCurrency(e)}
              >
                {projectId ? t('features.InvestmentScreening.BasicInfo.action.getCurrencyRate') : t('features.InvestmentScreening.BasicInfo.action.selectCurrencyRate')}
              </Button>
              <Button
                colorScheme="blue"
                isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}
                onClick={(e) => handleConfirmAssignerSelection(e)}
              >
                {t('features.InvestmentScreening.BasicInfo.action.confirmerSelection')}
              </Button>

              <Button
                colorScheme="blue"
                isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant}
                onClick={() => onOpenApproverAssignModal()}
              >
                {t('features.InvestmentScreening.BasicInfo.action.approverSelection')}
              </Button>

              <Button colorScheme="blue" isDisabled={hasfetchException || !pagePermissions?.eabledRegistrant} onClick={handleClickRegistrantConfirm}>
                {t('features.InvestmentScreening.BasicInfo.action.registrantConfirm')}
              </Button>
            </HStack>

            {/* 記入者名 */}
            {projectId && (
              <Text align="right" mr="20px" mt="3px" fontWeight="semibold">
                {employeeData?.employeeName}
              </Text>
            )}
          </Box >
        </fieldset>
      </Box >

      {/* 案件詳細モーダル */}
      <TopicsModal isOpen={isTopicsModalOpen} onClose={onTopicsModalClose} topicsInfo={topicsInfo} />

      {/* 過去Project選択モーダル */}
      <Modal isOpen={isOpenPastProject} onClose={onClosePastProject}>
        <ModalOverlay />
        <ModalContent minW="1755px" minH="800px">
          <ModalHeader>{t('features.InvestmentScreening.BasicInfo.modal.pastProjectSelection')}</ModalHeader>
          <ModalBody>
            {/* 現在選択中のプロジェクト表示 */}
            {projectBasicInfo.pastProjectId && (
              <Box
                bg="blue.50"
                border="1px solid"
                borderColor="blue.300"
                borderRadius="md"
                p={3}
                mb={4}
              >
                <HStack spacing={4}>
                  <Text fontWeight="bold" color="blue.700">{t('features.InvestmentScreening.BasicInfo.modal.currentlySelected')}:</Text>
                  <Text>
                    {projectBasicInfo.pastProjectName}
                    {projectBasicInfo.pastReviewDate
                      ? t('features.InvestmentScreening.BasicInfo.modal.pastReviewDateSuffix', { reviewDate: projectBasicInfo.pastReviewDate })
                      : ""}
                  </Text>
                </HStack>
              </Box>
            )}
            {/* PartNo検索エリア */}
            <HStack mb={4} spacing={4}>
              <FormControl w="400px">
                <FormLabel fontWeight="bold" mb={1}>{t('features.InvestmentScreening.BasicInfo.modal.partNoForwardMatch')}</FormLabel>
                <Input
                  value={searchPartNoPrefix}
                  onChange={(e) => setSearchPartNoPrefix(e.target.value)}
                  placeholder={t('features.InvestmentScreening.BasicInfo.label.partNoInputPlaceholder')}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      searchPastProjectByPartNo();
                    }
                  }}
                />
              </FormControl>
              <Button
                colorScheme="blue"
                onClick={searchPastProjectByPartNo}
                mt="auto"
              >
                {t('features.InvestmentScreening.BasicInfo.action.search')}
              </Button>
            </HStack>
            <TableContainer h="430px" overflowY="auto" border="1px solid #F2F2F2" >
              <Table className="mm-ceas-table" minW={1620} layout="fixed">
                <colgroup>
                  <col width="*" />
                  <col width="250px" />
                  <col width="250px" />
                  <col width="250px" />
                  <col width="150px" />
                  <col width="120px" />
                  <col width="160px" />
                  <col width="300px" />
                </colgroup>
                <Thead position="sticky" top={0} bg="gray.100" zIndex="10" boxShadow={`0 -1px 0 #EEEEEE`}>
                  <Tr>
                    <Th>
                      {t('features.InvestmentScreening.BasicInfo.field.projectName')}
                      <FilterTextBox
                        filterValue={filterPastProjectItems.projectName}
                        setFilterValue={(value: string) => { changeFilterPastProjectItems("projectName", value) }}
                        placeholder={t('common.placeholder.partialMatch')}
                      />
                    </Th>
                    <Th>
                      {t('features.InvestmentScreening.BasicInfo.field.customerName')}
                      <FilterTextBox
                        filterValue={filterPastProjectItems.customerName}
                        setFilterValue={(value: string) => { changeFilterPastProjectItems("customerName", value) }}
                        placeholder={t('common.placeholder.partialMatch')}
                      />
                    </Th>
                    <Th>
                      {t('features.InvestmentScreening.BasicInfo.field.customerProductName')}
                      <FilterTextBox
                        filterValue={filterPastProjectItems.customerProductName}
                        setFilterValue={(value: string) => { changeFilterPastProjectItems("customerProductName", value) }}
                        placeholder={t('common.placeholder.partialMatch')}
                      />
                    </Th>
                    <Th>
                      {t('features.InvestmentScreening.BasicInfo.field.purpose')}
                      <FilterDropdown
                        filterValue={filterPastProjectItems.purpose}
                        setFilterValue={(value: string) => { changeFilterPastProjectItems("purpose", value) }}
                        dataSource={pastProjectData || []}
                        propName="purpose"
                        displayNameFunc={getPurposeTypeNameByCode}
                      />
                    </Th>
                    <Th>
                      {t('features.InvestmentScreening.BasicInfo.modal.approvalDate')}
                      <FilterDateRange
                        filterStartValue={filterPastProjectItems.approveDayStart || null}
                        setFilterStartValue={(value: Date | null) => { changeFilterPastProjectItems("approveDayStart", value) }}
                        filterEndValue={filterPastProjectItems.approveDayEnd || null}
                        setFilterEndValue={(value: Date | null) => { changeFilterPastProjectItems("approveDayEnd", value) }}
                      />
                    </Th>
                    <Th>
                      {t('features.InvestmentScreening.BasicInfo.modal.reviewDate')}
                    </Th>
                    <Th>
                      {t('features.InvestmentScreening.BasicInfo.modal.investmentAcceptanceDate')}
                      <FilterDateRange
                        filterStartValue={filterPastProjectItems.projectStartDateStart || null}
                        setFilterStartValue={(value: Date | null) => { changeFilterPastProjectItems("projectStartDateStart", value) }}
                        filterEndValue={filterPastProjectItems.projectStartDateEnd || null}
                        setFilterEndValue={(value: Date | null) => { changeFilterPastProjectItems("projectStartDateEnd", value) }}
                      />
                    </Th>
                    <Th>
                      Part No.
                      <FilterTextBox
                        filterValue={filterPastProjectItems.partNo}
                        setFilterValue={(value: string) => { changeFilterPastProjectItems("partNo", value) }}
                        placeholder={t('common.placeholder.partialMatch')}
                      />
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filterPastProjectData?.map((p, idx) => (
                    <Tr key={idx} _hover={{ bg: "blue.100" }} bg={selectedPastProject?.projectId === p.projectId ? "teal.100" : ""} onClick={() => {
                      // 選択
                      setSelectedPastProject(p);
                      // 過去ProjectのPartNoリストを初期化（全てデフォルト選択）
                      const pastPartNos = p.partNo?.split(",").map(pn => pn.trim()).filter(pn => pn !== "") || [];
                      setSelectedPastPartNos(pastPartNos);
                    }}>
                      <Td style={pastProjectCellStyle}>
                        <Link color="blue" textDecorationLine="underline" whiteSpace="normal" wordBreak="break-word" onClick={() => {
                          const newWindow = window.open("", "_blank");
                          if (!newWindow) return;

                          // ウィンドウ名：サブウィンドウであることを通知するため、重要
                          newWindow.name = SubWindowInvestmentScreeningDetailName;
                          // サブウィンドウモードフラグ（subWindowReviewDate が無くても親の reviewDate を継承させないため必須）
                          newWindow.sessionStorage.setItem("subWindowMode", "1");
                          // セッションストレージでProjectIdを受け渡す
                          newWindow.sessionStorage.setItem("projectId", p.projectId || "");

                          // 一覧取得時点の承認済み振り返り日（p.reviewDate）をサブウィンドウ専用キーで受け渡す
                          // 通常キー(reviewDate)は親ウィンドウと共有されるため使わない
                          if (p.reviewDate) {
                            newWindow.sessionStorage.setItem("subWindowReviewDate", p.reviewDate);
                          }

                          // 遷移先
                          newWindow.location.href = `${window.origin}/investment-screening-detail/`;
                        }}>{p.projectName}</Link>
                      </Td>
                      <Td style={pastProjectCellStyle} whiteSpace="normal" wordBreak="break-word">{p.customerName}</Td>
                      <Td style={pastProjectCellStyle} whiteSpace="normal" wordBreak="break-word">{p.customerProductName}</Td>
                      <Td style={pastProjectCellStyle}>{getPurposeTypeNameByCode(p.purpose || "")}</Td>
                      <Td style={pastProjectCellStyle}>{p.approveDay}</Td>
                      <Td style={pastProjectCellStyle}>{p.reviewDate ?? ""}</Td>
                      <Td style={pastProjectCellStyle}>{p.projectStartDate}</Td>
                      <Td style={pastProjectCellStyle}>
                        {(p.partNoDetails && p.partNoDetails.length > 0)
                          ? p.partNoDetails.map((d, i) => (
                            <Box key={i} mb={i < p.partNoDetails!.length - 1 ? 1 : 0}>
                              {d.displayPartNo
                                ? (
                                  <>
                                    <Text>{d.displayPartNo}</Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {t('features.InvestmentScreening.BasicInfo.label.tentativePartNoLabel', { partNo: d.partNo })}
                                    </Text>
                                  </>
                                )
                                : <Text>{d.partNo}</Text>
                              }
                            </Box>
                          ))
                          : p.partNo?.split(",").map((partNoStr, i) => (
                            <Text key={i}>{partNoStr}</Text>
                          ))
                        }
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>

            {/* 選択したプロジェクトのPartNo表示エリア */}
            {selectedPastProject && selectedPastProject.partNo && (
              <Box mt={4} p={3} border="1px solid" borderColor="gray.300" borderRadius="md" bg="gray.50">
                <Text fontWeight="bold" mb={2}>{t('features.InvestmentScreening.BasicInfo.field.mmiPartNo')}:</Text>
                <Box>
                  <Wrap spacing={4}>
                    {selectedPastProject.partNo.split(",").map((pn, i) => {
                      const partNo = pn.trim();
                      if (!partNo) return null;
                      const isAlreadyExists = projectPartNo.some(p => p.partNo?.trim() === partNo);
                      return (
                        <WrapItem key={i}>
                          <Checkbox
                            value={partNo}
                            isDisabled={true}
                            isChecked={true}
                          >
                            {partNo}
                            {isAlreadyExists && <Text as="span" fontSize="xs" color="gray.500" ml={1}>（{t('features.InvestmentScreening.BasicInfo.label.alreadyEntered')}）</Text>}
                          </Checkbox>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                </Box>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={async (e) => {
                e.currentTarget.blur();
                changeProjectBasicInfo("pastProjectId", selectedPastProject?.projectId);
                changeProjectBasicInfo("pastProjectName", selectedPastProject?.projectName);
                changeProjectBasicInfo("pastReviewDate", selectedPastProject?.reviewDate ?? null);
                changeProjectBasicInfo("projectStartDate", selectedPastProject?.projectStartDate);

                // 過去Projectの全てのPartNoを処理
                if (selectedPastProject && selectedPastProject.partNo) {
                  // 過去Projectの全てのPartNoを取得
                  const allPastPartNos = selectedPastProject.partNo.split(",").map(p => p.trim()).filter(p => p);

                  // 過去ProjectのdisplayPartNo（変更後パーツNo）は一覧取得時のpartNoDetailsから取得する（再クエリ不要）
                  const pastDisplayPartNoMap: Record<string, string> = (selectedPastProject.partNoDetails || [])
                    .reduce((acc: Record<string, string>, d) => {
                      const key = d.partNo?.trim() || "";
                      if (key && d.displayPartNo) {
                        acc[key] = d.displayPartNo;
                      }
                      return acc;
                    }, {});

                  const currentPartNos = projectPartNo.map(p => p.partNo?.trim() || "");
                  // 未入力のPartNoのみ追加
                  const newPartNos = allPastPartNos.filter(p => !currentPartNos.includes(p));
                  // 既に入力済みのPartNoは、過去ProjectのdisplayPartNoで上書き更新（No.435対応）
                  const existingPartNos = allPastPartNos.filter(p => currentPartNos.includes(p));
                  if (existingPartNos.length > 0) {
                    setProjectPartNo((prevState) => prevState.map(p => {
                      const key = p.partNo?.trim() || "";
                      if (existingPartNos.includes(key)) {
                        return { ...p, displayPartNo: pastDisplayPartNoMap[key] || "" };
                      }
                      return p;
                    }));
                    // displayPartNoが設定されている行は、トグルON状態にする
                    const existingToggleKeys = existingPartNos.filter(p => !!pastDisplayPartNoMap[p]);
                    if (existingToggleKeys.length > 0) {
                      setDisplayPartNoToggleKeys((prevState) => Array.from(new Set([...prevState, ...existingToggleKeys])));
                    }
                  }
                  if (newPartNos.length > 0) {
                    const newPartNoObjects: PartNo[] = newPartNos.map(p => ({
                      partNo: p,
                      displayPartNo: pastDisplayPartNoMap[p] || "",
                    }));
                    setProjectPartNo((prevState) => [...prevState, ...newPartNoObjects]);
                    // displayPartNoが設定されている行は、トグルON状態にする
                    const newToggleKeys = newPartNos.filter(p => !!pastDisplayPartNoMap[p]);
                    if (newToggleKeys.length > 0) {
                      setDisplayPartNoToggleKeys((prevState) => Array.from(new Set([...prevState, ...newToggleKeys])));
                    }
                    toast({
                      title: TITLE,
                      description: t('features.InvestmentScreening.BasicInfo.message.partNoAdded', { partNos: newPartNos.join(", ") }),
                      status: 'info',
                      duration: 3000,
                      isClosable: true,
                    });
                  }
                  // 全てのPartNoを編集不可リストに設定
                  setLockedPartNos(allPastPartNos);
                } else {
                  // PartNoがない場合はlockedPartNosをクリア
                  setLockedPartNos([]);
                }

                onClosePastProject();
              }}
              isDisabled={!selectedPastProject}
              ml={1} mr={1}
            >
              {t('common.button.confirmAction')}
            </Button>
            <Button colorScheme="gray" ml={1} mr={1} onClick={onClosePastProject}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >

      {/* 会社/工場選択モーダル */}
      <Modal isOpen={isCompanyPlantModalOpen} onClose={onCompanyPlantModalClose} size="6xl" >
        <ModalOverlay />
        <ModalContent maxH="86vh">
          <ModalHeader>
            {t('features.InvestmentScreening.BasicInfo.modal.selectCompanyPlant')}
          </ModalHeader>
          <ModalBody>
            <HStack align="flex-start">
              <Box overflowY="auto" ref={companyPlantModalBodyRef} w="100%" maxH="70vh">
                {/* 全階層を統一的に動的生成 */}
                {(() => {
                  const levelTitles = [t('features.InvestmentScreening.BasicInfo.label.companyLevel'), t('features.InvestmentScreening.BasicInfo.label.plantLevel')];

                  // グループのソート順（アルファベット + 数字/その他）
                  const groupOrder = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '#'];

                  // 表示する階層を計算
                  // 会社（トップレベル）はparentCompanyPlantId = null
                  const levels: { parentId: string | null; levelIndex: number }[] = [];
                  levels.push({ parentId: null, levelIndex: 0 });
                  tempSelectedCompanyPlantPath.forEach((pathItem, index) => {
                    levels.push({ parentId: pathItem.companyPlantId, levelIndex: index + 1 });
                  });

                  return levels.map(({ parentId, levelIndex }) => {
                    // 会社（トップレベル）: parentCompanyPlantId が null/undefined/空文字
                    const items = (companyPlants ?? []).filter((cp) => {
                      if (parentId === null) {
                        return !cp.parentCompanyPlantId && !cp.deleted;
                      }
                      return cp.parentCompanyPlantId === parentId && !cp.deleted;
                    });
                    if (items.length === 0) return null;

                    // 会社レベル（levelIndex === 0）はAccordionでグループ化
                    if (levelIndex === 0) {
                      // グループ化（英語名でソート・グループ化）
                      const grouped: { [key: string]: CompanyPlantType[] } = {};
                      items.forEach((item) => {
                        const key = getGroupKey(item.companyPlantNameEn);
                        if (!grouped[key]) grouped[key] = [];
                        grouped[key].push(item);
                      });
                      // 各グループ内を英語名でソート
                      Object.keys(grouped).forEach((key) => {
                        grouped[key].sort((a, b) => (a.companyPlantNameEn || "").localeCompare(b.companyPlantNameEn || ""));
                      });

                      // グループをソート
                      const sortedGroups = Object.keys(grouped).sort((a, b) => {
                        const aIndex = groupOrder.indexOf(a);
                        const bIndex = groupOrder.indexOf(b);
                        if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                        if (aIndex === -1) return 1;
                        if (bIndex === -1) return -1;
                        return aIndex - bIndex;
                      });

                      return (
                        <Box key={`level-${levelIndex}`}>
                          <Text fontWeight="bold" mb={2}>{levelTitles[levelIndex]}</Text>
                          <Accordion
                            allowToggle
                            index={accordionIndex}
                            onChange={(idx) => setAccordionIndex(typeof idx === 'number' ? idx : undefined)}
                          >
                            {sortedGroups.map((groupKey) => (
                              <AccordionItem key={groupKey}>
                                <h2>
                                  <AccordionButton>
                                    <Box flex="1" textAlign="left" fontWeight="semibold">
                                      {groupKey} ({grouped[groupKey].length})
                                    </Box>
                                    <AccordionIcon />
                                  </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4}>
                                  <HStack spacing={2} wrap="wrap">
                                    {grouped[groupKey].map((item) => (
                                      <Button
                                        key={item.companyPlantId}
                                        size="sm"
                                        colorScheme={
                                          tempSelectedCompanyPlantPath[levelIndex]?.companyPlantId === item.companyPlantId
                                            ? "blue" : "gray"
                                        }
                                        onClick={() => {
                                          const newPath = tempSelectedCompanyPlantPath.slice(0, levelIndex);
                                          newPath.push(item);

                                          const hasCompant = tempSelectedCompanyPlants.some(p => p.companyPlantId === item.companyPlantId);
                                          if (hasCompant) {
                                            // 既に選択中の場合は追加しない
                                            if (tempSelectedCompanyPlant?.companyPlantId !== item.companyPlantId) {
                                              toast({
                                                title: TITLE,
                                                description: t('features.InvestmentScreening.BasicInfo.message.alreadySelected'),
                                                status: 'warning',
                                                duration: 3000,
                                                isClosable: true,
                                              });
                                            }
                                            return;
                                          }

                                          if (tempSelectedCompanyPlants.length >= MAX_COMPANY_PLANT_COUNT && focusedTagIndex === null) {
                                            toast({
                                              title: TITLE,
                                              description: t('features.InvestmentScreening.BasicInfo.message.maxSelectOver', { count: MAX_COMPANY_PLANT_COUNT }),
                                              status: 'warning',
                                              duration: 3000,
                                              isClosable: true,
                                            });
                                            return;
                                          }

                                          setTempSelectedCompanyPlantPath(newPath);
                                          setTempSelectedCompanyPlant(item);
                                          // 工場があるか判定
                                          const hasPlants = (companyPlants ?? []).some(
                                            (cp) => cp.parentCompanyPlantId === item.companyPlantId && !cp.deleted
                                          );
                                          if (hasPlants) {
                                            // 工場あり：工場選択待ち（末端ではない）- 工場セクションまでスクロール
                                            setTimeout(() => {
                                              companyPlantModalBodyRef.current?.scrollTo({
                                                top: companyPlantModalBodyRef.current.scrollHeight,
                                                behavior: "smooth"
                                              });
                                            }, 100);
                                          } else {
                                            // 工場なし（会社単体が末端）：選択リストへ追加または更新
                                            if (focusedTagIndex !== null) {
                                              // フォーカス中 Tag のエントリーを上書き更新
                                              setTempSelectedCompanyPlants(prev => prev.map((cp, i) => i === focusedTagIndex ? item : cp));
                                              setFocusedTagIndex(null);
                                            } else {
                                              // 未フォーカス：末尾に追加
                                              setTempSelectedCompanyPlants(prev => [...prev, item]);
                                            }
                                          }
                                        }}
                                      >
                                        {getBilingualDisplayName(item, 'companyPlantName', 'companyPlantNameEn')}
                                      </Button>
                                    ))}
                                  </HStack>
                                </AccordionPanel>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </Box>
                      );
                    }

                    // 工場レベルは従来通り
                    return (
                      <Box mt={4} key={`level-${levelIndex}`}>
                        <Text fontWeight="bold">{levelTitles[levelIndex] || t('features.InvestmentScreening.BasicInfo.label.other')}</Text>
                        <HStack spacing={2} wrap="wrap" mt={2} p={2} borderColor="gray.200" borderWidth="1px" borderStyle="solid" borderRadius="5">
                          {items.map((item) => (
                            <Button
                              key={item.companyPlantId}
                              colorScheme={
                                tempSelectedCompanyPlantPath[levelIndex]?.companyPlantId === item.companyPlantId
                                  ? "blue" : "gray"
                              }
                              onClick={() => {
                                const newPath = tempSelectedCompanyPlantPath.slice(0, levelIndex);
                                newPath.push(item);

                                const selected = tempSelectedCompanyPlants.some(p => p.companyPlantId === item.companyPlantId);
                                if (selected) {
                                  // 既に選択中の場合は追加しない
                                  if (tempSelectedCompanyPlant?.companyPlantId !== item.companyPlantId) {
                                    toast({
                                      title: TITLE,
                                      description: t('features.InvestmentScreening.BasicInfo.message.alreadySelected'),
                                      status: 'warning',
                                      duration: 3000,
                                      isClosable: true,
                                    });
                                  }
                                  return;
                                }

                                if (tempSelectedCompanyPlants.length >= MAX_COMPANY_PLANT_COUNT && focusedTagIndex === null) {
                                  toast({
                                    title: TITLE,
                                    description: t('features.InvestmentScreening.BasicInfo.message.maxSelectOver', { count: MAX_COMPANY_PLANT_COUNT }),
                                    status: 'warning',
                                    duration: 3000,
                                    isClosable: true,
                                  });
                                  return;
                                }

                                setTempSelectedCompanyPlantPath(newPath);
                                setTempSelectedCompanyPlant(item);
                                // 工場は末端ノード：選択リストへ追加または更新
                                if (focusedTagIndex !== null) {
                                  // フォーカス中 Tag のエントリーを上書き更新
                                  setTempSelectedCompanyPlants(prev => prev.map((cp, i) => i === focusedTagIndex ? item : cp));
                                  setFocusedTagIndex(null);
                                } else {
                                  // 未フォーカス：末尾に追加
                                  setTempSelectedCompanyPlants(prev => [...prev, item]);
                                }
                              }}
                            >
                              {getBilingualDisplayName(item, 'companyPlantName', 'companyPlantNameEn')}
                            </Button>
                          ))}
                        </HStack>
                      </Box>
                    );
                  });
                })()}
              </Box>
              <Box>
                {/* 選択中の会社/工場 */}
                <HStack align="flex-start" mb={2}>
                  <Text whiteSpace="nowrap" fontSize="md" fontWeight="bold">{t('features.InvestmentScreening.BasicInfo.modal.currentlySelected')}</Text>
                  <HelpIcon size="20" width="550px">
                    <Text>
                      {t('features.InvestmentScreening.BasicInfo.help.companyPlantMultiSelect')}
                    </Text>
                  </HelpIcon>
                </HStack>
                <VStack overflowY="auto" p={2} w="450px" maxH="65vh" align="flex-start">
                  {tempSelectedCompanyPlants.map((cp, idx) => {
                    // Tag ラベル: "会社：XXX / 工場：YYY" または "会社：XXX"
                    const isFocused = focusedTagIndex === idx;
                    let tagLabel = "";
                    if (cp.parentCompanyPlantId) {
                      const parentCompany = (companyPlants ?? []).find(c => c.companyPlantId === cp.parentCompanyPlantId);
                      const companyName = getBilingualDisplayName(parentCompany, 'companyPlantName', 'companyPlantNameEn') ?? "";
                      tagLabel = `${t('features.InvestmentScreening.BasicInfo.label.companyPrefix')}${companyName} / ${t('features.InvestmentScreening.BasicInfo.label.plantPrefix')}${getBilingualDisplayName(cp, 'companyPlantName', 'companyPlantNameEn') ?? ""}`;
                    } else {
                      tagLabel = `${t('features.InvestmentScreening.BasicInfo.label.companyPrefix')}${getBilingualDisplayName(cp, 'companyPlantName', 'companyPlantNameEn') ?? ""}`;
                    }
                    return (
                      <Tag
                        key={cp.companyPlantId ?? idx}
                        borderRadius="lg"
                        variant={isFocused ? 'solid' : 'outline'}
                        colorScheme="green"
                        cursor="pointer"
                        onClick={() => {
                          // Tag クリック：フォーカス切替。同じ Tag を再クリックでフォーカス解除
                          const newFocusIdx = isFocused ? null : idx;
                          setFocusedTagIndex(newFocusIdx);
                          // フォーカス中の会社/工場を ModalBody の選択状態に反映
                          if (newFocusIdx !== null) {
                            const buildPath = (target: CompanyPlantType): CompanyPlantType[] => {
                              const path: CompanyPlantType[] = [];
                              let cur: CompanyPlantType | undefined = target;
                              while (cur) {
                                path.unshift(cur);
                                if (!cur.parentCompanyPlantId) break;
                                cur = (companyPlants ?? []).find(c => c.companyPlantId === cur?.parentCompanyPlantId);
                              }
                              return path;
                            };
                            const path = buildPath(cp);
                            setTempSelectedCompanyPlantPath(path);
                            setTempSelectedCompanyPlant(cp);

                            // フォーカス会社が属するグループの Accordion を展開
                            const topItem = path[0];
                            if (topItem) {
                              setAccordionIndex(calcAccordionIndex(topItem, companyPlants ?? []));
                            }
                          } else {
                            // 選択中の会社/工場Tagの選択解除
                            setTempSelectedCompanyPlantPath([]);
                            setTempSelectedCompanyPlant(null);
                          }
                        }}
                      >
                        <TagLabel>{tagLabel}</TagLabel>
                        <TagCloseButton onClick={(e) => {
                          e.stopPropagation();
                          // × ボタン：リストから除外。フォーカス中だった場合は解除
                          setTempSelectedCompanyPlants(prev => prev.filter((_, i) => i !== idx));
                          setFocusedTagIndex(prev => {
                            if (prev === null) return null;
                            if (prev === idx) return null;
                            if (prev > idx) return prev - 1;
                            return prev;
                          });
                        }} />
                      </Tag>
                    );
                  })}
                </VStack>
              </Box>
            </HStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleSelectCompanyPlant}
              isDisabled={tempSelectedCompanyPlants.length === 0}
              ml={1} mr={1}
            >
              {t('common.button.confirmAction')}
            </Button>
            <Button colorScheme="gray" ml={1} mr={1} onClick={onCompanyPlantModalClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >

      {/* ローカル通貨/税率/金利の地域選択モーダル（複数選択時に通貨または税率が混在する場合に表示） */}
      <Modal isOpen={isCurrencyTaxModalOpen} onClose={() => setIsCurrencyTaxModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader mr={5}>{t('features.InvestmentScreening.BasicInfo.modal.selectCurrencyTax')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {(() => {
              const uniqueCurrencyIds = [...new Set(tempSelectedCompanyPlants.map(cp => cp.currencyId ?? "").filter(Boolean))];
              const uniqueTaxRates = [...new Set(tempSelectedCompanyPlants.map(cp => cp.taxRate ?? 0))];
              const uniqueActiveAreaCodes = [...new Set(tempSelectedCompanyPlants.map(cp => cp.areaCode ?? "").filter(Boolean))];
              const areaCodedNames = areaData.filter(area => uniqueActiveAreaCodes.includes(area.areaCode));

              return (
                <VStack align="stretch" spacing={4}>
                  <FormControl>
                    <FormLabel>{t('features.InvestmentScreening.BasicInfo.label.activeCurrency')}</FormLabel>
                    <Select
                      value={selectedActiveCurrencyId}
                      onChange={e => setSelectedActiveCurrencyId(e.target.value)}
                    >
                      {uniqueCurrencyIds.map(currencyId => {
                        const currency = (currencyData ?? []).find(c => c.currencyId === currencyId)
                          ?? (currencyDataById ?? []).find(c => c.currencyId === currencyId);
                        const label = currency ? `${currency.currencyName}` : currencyId;
                        return <option key={currencyId} value={currencyId}>{label}</option>;
                      })}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('features.InvestmentScreening.BasicInfo.label.activeTaxRate')}</FormLabel>
                    <Select
                      value={String(selectedActiveTaxRate ?? 0)}
                      onChange={e => setSelectedActiveTaxRate(Number(e.target.value))}
                    >
                      {uniqueTaxRates.map(rate => (
                        <option key={rate} value={String(rate)}>{rate}%</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('features.InvestmentScreening.BasicInfo.label.activeAreaCode')}</FormLabel>
                    <Select
                      value={selectedActiveAreaCode}
                      onChange={e => setSelectedActiveAreaCode(e.target.value)}
                    >
                      {areaCodedNames.map(area => (
                        <option key={area.areaCode!} value={area.areaCode!}>{getMultilingualDisplayName(area.areaName)}</option>
                      ))}
                    </Select>
                  </FormControl>
                </VStack>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              ml={1} mr={1}
              onClick={() => {
                setIsCurrencyTaxModalOpen(false);
                applyCompanyPlantSelection(selectedActiveCurrencyId, selectedActiveTaxRate, selectedActiveAreaCode);
              }}
            >
              {t('common.button.confirmAction')}
            </Button>
            <Button colorScheme="gray" ml={1} mr={1} onClick={() => setIsCurrencyTaxModalOpen(false)}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 事業部選択モーダル */}
      <Modal isOpen={isDivisionModalOpen} onClose={onDivisionModalClose} size="4xl" >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>{t('features.InvestmentScreening.BasicInfo.modal.selectDivision')}</ModalHeader>
          <ModalBody overflowY="auto">
            {/* 全階層を統一的に動的生成 */}
            {(() => {
              const levelTitles = [t('features.InvestmentScreening.BasicInfo.label.divisionLevelHeadquarters'), t('features.InvestmentScreening.BasicInfo.label.divisionLevelDepartment'), t('features.InvestmentScreening.BasicInfo.label.divisionLevelDivision'), t('features.InvestmentScreening.BasicInfo.label.divisionLevelBusinessSubtotal'), t('features.InvestmentScreening.BasicInfo.label.divisionLevelBusiness'), t('features.InvestmentScreening.BasicInfo.label.divisionLevelProduct')];
              const rootId = "organization00000";

              // 表示する階層を計算
              // Level 0: ルート直下（事業本部）
              // Level 1以降: 選択されたパスの各要素の子
              const levels: { parentId: string; levelIndex: number }[] = [];

              // Level 0（事業本部）は常に表示
              levels.push({ parentId: rootId, levelIndex: 0 });

              // 選択されたパスに基づいて追加のレベルを表示
              tempSelectedDivisionPath.forEach((pathItem, index) => {
                levels.push({ parentId: pathItem.organizationId, levelIndex: index + 1 });
              });

              return levels.map(({ parentId, levelIndex }) => {
                const items = (divisions ?? []).filter((org) => org.parentOrganizationId === parentId);
                if (items.length === 0) return null;

                return (
                  <Box mt={levelIndex === 0 ? 0 : 4} key={`level-${levelIndex}`}>
                    <Text fontWeight="bold">{levelTitles[levelIndex] || t('features.InvestmentScreening.BasicInfo.label.other')}</Text>
                    <HStack spacing={2} wrap="wrap" mt={2} p={2} borderColor="gray.200" borderWidth="1px" borderStyle="solid" borderRadius="5">
                      {items.map((item) => (
                        <Button
                          key={item.organizationId}
                          colorScheme={
                            tempSelectedDivisionPath[levelIndex]?.organizationId === item.organizationId
                              ? "blue" : "gray"
                          }
                          onClick={() => {
                            // パスをこのレベルで切って、新しい選択を追加
                            const newPath = tempSelectedDivisionPath.slice(0, levelIndex);
                            newPath.push(item);
                            setTempSelectedDivisionPath(newPath);
                            setTempSelectedDivision(item);
                          }}
                        >
                          {getBilingualDisplayName(item, 'organizationName', 'organizationNameEn')}
                        </Button>
                      ))}
                    </HStack>
                  </Box>
                );
              });
            })()}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleSelectDivision}
              isDisabled={!tempSelectedDivision}
              ml={1} mr={1}
            >
              {t('common.button.confirmAction')}
            </Button>
            <Button colorScheme="gray" ml={1} mr={1} onClick={onDivisionModalClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >

      {/* 顧客業界選択モーダル */}
      <Modal isOpen={isCustomerIndustryModalOpen} onClose={onCustomerIndustryModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>{t('features.InvestmentScreening.BasicInfo.modal.selectCustomerIndustry')}</ModalHeader>
          <ModalBody overflowY="auto">
            {(() => {
              const levelTitles = [t('features.InvestmentScreening.BasicInfo.label.largeCategoryLevel'), t('features.InvestmentScreening.BasicInfo.label.mediumCategoryLevel'), t('features.InvestmentScreening.BasicInfo.label.smallCategoryLevel'), t('features.InvestmentScreening.BasicInfo.label.detailCategoryLevel')];

              // 表示する階層を計算
              const levels: { parentId: string | null; levelIndex: number }[] = [];

              // Level 0（大分類）は常に表示
              levels.push({ parentId: null, levelIndex: 0 });

              // 選択されたパスに基づいて追加のレベルを表示
              tempSelectedIndustryPath.forEach((pathItem, index) => {
                levels.push({ parentId: pathItem.productCategoryId, levelIndex: index + 1 });
              });

              return levels.map(({ parentId, levelIndex }) => {
                if (levelIndex >= 4) return null; // 最大4階層

                const allItems = productCategories.filter(c => {
                  if (levelIndex === 0) {
                    return c.hierarchy === 1;
                  }
                  return c.upperLevelCategoryId === parentId;
                });

                if (allItems.length === 0) return null;

                // フィルタ適用
                const filterValue = industryFilterValues[levelIndex] || "";
                const items = filterValue
                  ? allItems.filter(c => c.productCategoryName.toLowerCase().includes(filterValue.toLowerCase()))
                  : allItems;

                return (
                  <Box mt={levelIndex === 0 ? 0 : 4} key={`industry-level-${levelIndex}`}>
                    <Flex align="center" mb={1}>
                      <Text fontWeight="bold" mr={3}>{levelTitles[levelIndex]}</Text>
                      <Input
                        size="sm"
                        placeholder={t('features.InvestmentScreening.BasicInfo.label.filterPlaceholder')}
                        value={industryFilterValues[levelIndex] || ""}
                        onChange={(e) => {
                          const newFilterValue = e.target.value;
                          const newValues = [...industryFilterValues];
                          newValues[levelIndex] = newFilterValue;
                          // フィルタにより選択済み項目が除外される場合、そのレベル以降の選択をクリア
                          const currentSelected = tempSelectedIndustryPath[levelIndex];
                          if (currentSelected && newFilterValue) {
                            const stillVisible = currentSelected.productCategoryName.toLowerCase().includes(newFilterValue.toLowerCase());
                            if (!stillVisible) {
                              setTempSelectedIndustryPath(tempSelectedIndustryPath.slice(0, levelIndex));
                              for (let i = levelIndex + 1; i < 4; i++) {
                                newValues[i] = "";
                              }
                            }
                          }
                          setIndustryFilterValues(newValues);
                        }}
                        width="200px"
                      />
                    </Flex>
                    <HStack spacing={2} wrap="wrap" mt={1} p={2} borderColor="gray.200" borderWidth="1px" borderStyle="solid" borderRadius="5" maxH="200px" overflowY="auto">
                      {items.length > 0 ? items.map((item) => (
                        <Button
                          key={item.productCategoryId}
                          colorScheme={
                            tempSelectedIndustryPath[levelIndex]?.productCategoryId === item.productCategoryId
                              ? "blue" : "gray"
                          }
                          size="sm"
                          onClick={() => {
                            // パスをこのレベルで切って、新しい選択を追加
                            const newPath = tempSelectedIndustryPath.slice(0, levelIndex);
                            newPath.push(item);
                            setTempSelectedIndustryPath(newPath);
                            // 下位階層のフィルタをクリア
                            const newFilterValues = [...industryFilterValues];
                            for (let i = levelIndex + 1; i < 4; i++) {
                              newFilterValues[i] = "";
                            }
                            setIndustryFilterValues(newFilterValues);
                          }}
                        >
                          {item.productCategoryName}
                        </Button>
                      )) : (
                        <Text color="gray.500" fontSize="sm">{t('features.InvestmentScreening.BasicInfo.label.noMatch')}</Text>
                      )}
                    </HStack>
                  </Box>
                );
              });
            })()}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleSelectCustomerIndustry}
              isDisabled={tempSelectedIndustryPath.length === 0}
              ml={1} mr={1}
            >
              {t('common.button.confirmAction')}
            </Button>
            <Button colorScheme="gray" ml={1} mr={1} onClick={onCustomerIndustryModalClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 最終確認モーダル */}
      < Modal isOpen={isFinalConfirmationModalOpen} onClose={onFinalConfirmationModalClose} size="xl" >
        <ModalOverlay />
        <ModalContent maxH="780px">
          <ModalHeader>{t('features.InvestmentScreening.BasicInfo.modal.finalConfirmTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack align="stretch" spacing={4}>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.projectName')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.projectName ?? ""}>{projectBasicInfo.projectName || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.customerName')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.customerName ?? ""}>{projectBasicInfo.customerName || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.customerFullName')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.customerFullName ?? ""}>{projectBasicInfo.customerFullName || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.customerProductName')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.customerProductName ?? ""}>{projectBasicInfo.customerProductName || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.opportunityName')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.opportunityName ?? ""}>{projectBasicInfo.opportunityName || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.endUserName')}：</Text>
                <Text style={finalConfirmValueStyle} title={endUserList.filter((v) => v.trim() !== "").join(", ")}>{endUserList.filter((v) => v.trim() !== "").join(", ") || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.carModel')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.carModel ?? ""}>{projectBasicInfo.carModel || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.customerIndustry')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.customerIndustryHierarchy || productCategories.find(c => c.productCategoryId === projectBasicInfo.customerIndustry)?.productCategoryName || ""}>{projectBasicInfo.customerIndustryHierarchy || productCategories.find(c => c.productCategoryId === projectBasicInfo.customerIndustry)?.productCategoryName || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.mmProductCategory')}：</Text>
                <Text style={finalConfirmValueStyle} title={selectedMMIProductCategory?.MMProductCategoryName}>{selectedMMIProductCategory?.MMProductCategoryName || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.mmProductName')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.mmProductNameFreeWrite ?? ""}>{projectBasicInfo.mmProductNameFreeWrite || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="flex-start">
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.mmiPartNo')}：</Text>
                <VStack maxW="338px">
                  {projectPartNo?.filter(p => (p.partNo || null) !== null).map((partNoInfo, idx) => (
                    <Text key={idx} textAlign="left" w="100%" minH="24px" display="block" style={finalConfirmValueStyle} title={resolveDisplayPartNo(partNoInfo)}>{resolveDisplayPartNo(partNoInfo) || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
                  ))}
                  {savedPartNoSnapshot.some(saved => !projectPartNo.some(p => p.partNo?.trim() === saved)) && (
                    <Box borderWidth="1px" borderRadius="md" p={2} bg="orange.50" borderColor="orange.200">
                      <Text color="red.500" textAlign="left" fontSize="sm" display="block" whiteSpace="wrap">
                        {t('features.InvestmentScreening.BasicInfo.message.mmiPartNoChangedWarning')}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Flex>
              {(() => {
                // スナップショットから会社名・工場名を取得
                if (projectBasicInfo?.companyPlantSnapshot) {
                  try {
                    const snapshotList = parseCompanyPlantSnapshot(projectBasicInfo.companyPlantSnapshot);
                    const companyPlantList = snapshotList?.items.map(item => {
                      const companyDisplayName = getBilingualDisplayName(item, 'companyName', 'companyNameEn');
                      return companyDisplayName + (item.plantName ? " / " + getBilingualDisplayName(item, 'plantName', 'plantNameEn') : "");
                    }) || [];

                    return (
                      <Flex align="center">
                        <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.company')} / {t('features.InvestmentScreening.BasicInfo.field.plant')}：</Text>

                        <VStack align="flex-start">
                          {companyPlantList.map((value, idx) => (
                            <Text key={idx} style={finalConfirmValueStyle} title={value}>{value || null || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
                          ))}
                        </VStack>
                      </Flex>
                    );
                  } catch (e) {
                    console.error("Failed to parse companyPlantSnapshot:", e);
                  }
                }
              })()}
              {/* 基準金利表示 */}
              <Flex align="center">
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.baseInterestRate')}：</Text>
                <VStack align="start" spacing={0}>
                  {fetchedInterestRateInfo ? (
                    <>
                      <Text style={finalConfirmValueStyle}>
                        {fetchedInterestRateInfo.rate !== null
                          ? `${fetchedInterestRateInfo.rate}% (${fetchedInterestRateInfo.isLatestUsed && fetchedInterestRateInfo.latestFiscalPeriod
                            ? fetchedInterestRateInfo.latestFiscalPeriod.split('-')[0]
                            : fetchedInterestRateInfo.fiscalPeriod.split('-')[0]}${t('features.InvestmentScreening.BasicInfo.label.fiscalYear')})`
                          : "未設定"}
                      </Text>
                      {fetchedInterestRateInfo.isLatestUsed && fetchedInterestRateInfo.rate !== null && (
                        <Text fontSize="xs" color="orange.500">
                          {t('features.InvestmentScreening.BasicInfo.label.interestRateLatestNote', { year: fetchedInterestRateInfo.fiscalPeriod.split('-')[0] })}
                        </Text>
                      )}
                      {fetchedInterestRateInfo.rate === null && (
                        <Text fontSize="xs" color="red.500">
                          {t('features.InvestmentScreening.BasicInfo.label.interestRateNotFoundNote')}
                        </Text>
                      )}
                    </>
                  ) : (
                    <Text style={finalConfirmValueStyle} color="gray.500">
                      {t('features.InvestmentScreening.BasicInfo.label.interestRateConditionInsufficient')}
                    </Text>
                  )}
                </VStack>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.division')}：</Text>
                <Text style={finalConfirmValueStyle} title={getBilingualDisplayName(selectedDivision, 'organizationName', 'organizationNameEn')}>{getBilingualDisplayName(selectedDivision, 'organizationName', 'organizationNameEn') || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.specificationAndPerformance')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.specificationAndPerformance ?? ""}>{projectBasicInfo.specificationAndPerformance || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.applicationNumber')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.applicationNumber ?? ""}>{projectBasicInfo.applicationNumber || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.equipmentPlanNumber')}：</Text>
                <Text style={finalConfirmValueStyle} title={equipmentPlanNumberList.filter((v) => v.trim() !== "").join(", ")}>{equipmentPlanNumberList.filter((v) => v.trim() !== "").join(", ") || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.plannedBudget')}：</Text>
                <Text style={finalConfirmValueStyle} title={getPlannedBudgetTypeNameByCode(projectBasicInfo.plannedBudget ?? "")}>{getPlannedBudgetTypeNameByCode(projectBasicInfo.plannedBudget ?? "") || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.applicationMonth')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.applicationMonth ?? ""}>{projectBasicInfo.applicationMonth || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.projectStartDate')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.projectStartDate ?? ""}>{projectBasicInfo.projectStartDate || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.projectEndDate')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.projectEndDate ?? ""}>{projectBasicInfo.projectEndDate || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.eolDate')}：</Text>
                <Text style={finalConfirmValueStyle} title={projectBasicInfo.eolDate ?? ""}>{projectBasicInfo.eolDate || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>

              {projectBasicInfo.purpose === PurposeType.IncreasedProduction.code && projectBasicInfo.pastProjectId &&
                <Flex align="center" >
                  <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.increasedProductionStartDate')}：</Text>
                  <Text style={finalConfirmValueStyle} title={projectBasicInfo.increasedProductionStartDate ?? ""}>{projectBasicInfo.increasedProductionStartDate || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
                </Flex>
              }

              {projectBasicInfo.purpose === PurposeType.UpdateAndOther.code && projectBasicInfo.pastProjectId &&
                <Flex align="center" >
                  <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.updateAndOtherStartDate')}：</Text>
                  <Text style={finalConfirmValueStyle} title={projectBasicInfo.updateAndOtherStartDate ?? ""}>{projectBasicInfo.updateAndOtherStartDate || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
                </Flex>
              }

              {projectBasicInfo.purpose === PurposeType.Rationalization.code && projectBasicInfo.pastProjectId &&
                <Flex align="center" >
                  <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.rationalizationStartDate')}：</Text>
                  <Text style={finalConfirmValueStyle} title={projectBasicInfo.rationalizationStartDate ?? ""}>{projectBasicInfo.rationalizationStartDate || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
                </Flex>
              }

              {projectBasicInfo.purpose === PurposeType.NewProduct.code && projectBasicInfo.pastProjectId &&
                <Flex align="center" >
                  <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.newProductStartDate')}：</Text>
                  <Text style={finalConfirmValueStyle} title={projectBasicInfo.newProductStartDate ?? ""}>{projectBasicInfo.newProductStartDate || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
                </Flex>
              }

              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.equipmentCategory')}：</Text>
                <Text style={finalConfirmValueStyle} title={getEquipmentCategoryNamesByCodes(projectBasicInfo.equipmentCategory ?? "")}>{getEquipmentCategoryNamesByCodes(projectBasicInfo.equipmentCategory ?? "") || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.equipmentUsageType')}：</Text>
                <Text style={finalConfirmValueStyle}>{
                  projectBasicInfo.equipmentUsageType === EquipmentUsageType.Dedicated.code ? getEquipmentUsageTypeNameByCode(EquipmentUsageType.Dedicated.code) :
                    projectBasicInfo.equipmentUsageType === EquipmentUsageType.GeneralPurpose.code ? getEquipmentUsageTypeNameByCode(EquipmentUsageType.GeneralPurpose.code) :
                      "未設定"
                }</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.purpose')}：</Text>
                <Text style={finalConfirmValueStyle} title={getPurposeTypeNameByCode(projectBasicInfo.purpose ?? "")}>{getPurposeTypeNameByCode(projectBasicInfo.purpose ?? "") || t('features.InvestmentScreening.BasicInfo.label.notSet')}</Text>
              </Flex>
              <Flex align="center" >
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.rateType')}：</Text>
                <Text style={finalConfirmValueStyle} title={rateType === 'actual' ? t('features.InvestmentScreening.BasicInfo.label.actualRateLabel') : t('features.InvestmentScreening.BasicInfo.label.planRateLabel')}>{rateType === 'actual' ? t('features.InvestmentScreening.BasicInfo.label.actualRateLabel') : t('features.InvestmentScreening.BasicInfo.label.planRateLabel')}</Text>
              </Flex>
              {(!hiddenPastProject && projectBasicInfo.pastProjectId) && (
                <Flex align="center" >
                  <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.pastProjectName')}：</Text>
                  <Text style={finalConfirmValueStyle} title={projectBasicInfo.pastProjectName ?? ""}>
                    {projectBasicInfo.pastProjectName || t('features.InvestmentScreening.BasicInfo.label.notSet')}
                    {projectBasicInfo.pastProjectName && projectBasicInfo.pastReviewDate
                      ? t('features.InvestmentScreening.BasicInfo.modal.pastReviewDateSuffix', { reviewDate: projectBasicInfo.pastReviewDate })
                      : ""}
                  </Text>
                </Flex>
              )}
              <Flex align="flex-start" >
                <Text style={finalConfirmLabelStyle} flexShrink={0}>{t('features.InvestmentScreening.BasicInfo.field.purposeOfInvestment')}：</Text>
                <Text
                  style={{
                    ...finalConfirmValueStyle,
                    whiteSpace: "pre-wrap",
                    overflow: "visible",
                    textOverflow: "unset",
                    wordBreak: "break-word",
                  }}
                  title={projectBasicInfo.purposeOfInvestment ?? ""}
                >
                  {projectBasicInfo.purposeOfInvestment || t('features.InvestmentScreening.BasicInfo.label.notSet')}
                </Text>
              </Flex>
              <Flex align="center">
                {/* 設備入れ替わり警告（BE の _copyEquipment の再コピー判定と同条件。DB 保存済み値と比較する） */}
                {(() => {
                  // 未保存PJは対象外（振り返りモードでも BE が振り返り版の設備を入れ替えるため警告対象）
                  if (!projectBasicInfo.updateDate || !savedEquipmentSource) {
                    return null;
                  }
                  const isNewProductNow = projectBasicInfo.purpose === PurposeType.NewProduct.code;
                  const isNewProductSaved = savedEquipmentSource.purpose === PurposeType.NewProduct.code;
                  const pastProjectIdNow = projectBasicInfo.pastProjectId || null;
                  const pastReviewDateNow = projectBasicInfo.pastReviewDate ?? null;
                  // 目的が DB・現在のどちらも新製品以外なら対象外
                  if (!isNewProductNow && !isNewProductSaved) {
                    return null;
                  }
                  // 過去PJ紐づけが DB・現在のどちらもなければ対象外
                  if (!pastProjectIdNow && !savedEquipmentSource.pastProjectId) {
                    return null;
                  }
                  const purposeChanged = projectBasicInfo.purpose !== savedEquipmentSource.purpose;
                  const pastProjectChanged = pastProjectIdNow !== (savedEquipmentSource.pastProjectId || null);
                  const reviewDateChanged = pastReviewDateNow !== savedEquipmentSource.pastReviewDate;
                  if (!purposeChanged && !pastProjectChanged && !reviewDateChanged) {
                    return null;
                  }

                  // 原因の判定（上から順に最初に当てはまるものを表示）
                  let messageKey: string;
                  if (!isNewProductNow) {
                    // 目的が「新製品」以外に変更された
                    messageKey = 'deleteByPurposeChange';
                  } else if (!pastProjectIdNow) {
                    // 過去プロジェクトの紐づけが解除された
                    messageKey = 'deleteByUnlink';
                  } else if (!isNewProductSaved) {
                    // 目的が「新製品」に変更された
                    messageKey = 'replaceByPurposeChange';
                  } else if (pastProjectChanged) {
                    // 過去プロジェクトが変更された
                    messageKey = 'replaceByPastProjectChange';
                  } else {
                    // 過去プロジェクトの振り返り日が変更された
                    messageKey = 'replaceByReviewDateChange';
                  }
                  // 振り返り日なし（申請のみ）の過去PJの場合は「（振り返り日）」を含まない文言にする
                  if (messageKey.startsWith('replace') && !pastReviewDateNow) {
                    messageKey += 'NoReviewDate';
                  }
                  return (
                    <Box borderWidth="1px" borderRadius="md" p={2} bg="orange.50" borderColor="orange.200">
                      <Text color="red.500" fontWeight="bold" fontSize="sm" whiteSpace="pre-wrap">
                        {t(`features.InvestmentScreening.BasicInfo.modal.equipmentWarning.${messageKey}`)}
                      </Text>
                    </Box>
                  );
                })()}
              </Flex>
              <Flex align="center" >
                {/* ログインユーザーを表示 */}
                <Text style={finalConfirmLabelStyle}>{t('features.InvestmentScreening.BasicInfo.field.registrant')}： </Text>
                <Text style={finalConfirmValueStyle} title={loginUserEmployeeName}>{loginUserEmployeeName}</Text>
              </Flex>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" ml={1} mr={1} onClick={handleFinalConfirmAndSave}>
              {t('common.button.save')}
            </Button>
            <Button colorScheme="gray" ml={1} mr={1} onClick={onFinalConfirmationModalClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 為替レート種別選択モーダル */}
      <Modal isOpen={isRateTypeModalOpen} onClose={onRateTypeModalClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('features.InvestmentScreening.BasicInfo.modal.selectRateType')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>{t('features.InvestmentScreening.BasicInfo.modal.selectRateTypeDescription')}</Text>
            <RadioGroup value={selectedRateType ?? ''} onChange={(value: 'actual' | 'plan') => setSelectedRateType(value)}>
              <VStack align="start" spacing={4}>
                {/* 実勢レート選択肢 */}
                <Box w="100%" p={3} borderWidth="1px" borderRadius="md" borderColor={selectedRateType === 'actual' ? 'blue.500' : 'gray.200'} bg={selectedRateType === 'actual' ? 'blue.50' : 'white'}>
                  <Flex align="center" justify="space-between">
                    <Radio value="actual" size="lg">
                      <Box>
                        <Text fontWeight="bold">{t('features.InvestmentScreening.BasicInfo.option.actualRate')}</Text>
                        <Text fontSize="sm" color="gray.600">{t('features.InvestmentScreening.BasicInfo.option.actualRateDescription')}</Text>
                      </Box>
                    </Radio>
                    <CurrencyList
                      currencyData={previewActualRates}
                      headerText={previewActualRates.length > 0 && previewActualRates[0].rateMonth
                        ? t('common.dateFormat.yearMonth', { year: previewActualRates[0].rateMonth.substring(0, 4), month: previewActualRates[0].rateMonth.substring(5, 7) })
                        : ''}
                      placement="right"
                      isLoading={isLoadingPreview}
                    />
                  </Flex>
                </Box>

                {/* 計画レート選択肢 */}
                <Box w="100%" p={3} borderWidth="1px" borderRadius="md" borderColor={selectedRateType === 'plan' ? 'blue.500' : 'gray.200'} bg={selectedRateType === 'plan' ? 'blue.50' : 'white'}>
                  <Flex align="center" justify="space-between">
                    <Radio value="plan" size="lg">
                      <Box>
                        <Text fontWeight="bold">{t('features.InvestmentScreening.BasicInfo.option.planRate')}</Text>
                        <Text fontSize="sm" color="gray.600">{t('features.InvestmentScreening.BasicInfo.option.planRateDescription')}</Text>
                      </Box>
                    </Radio>
                    <CurrencyList
                      currencyData={previewPlanRates}
                      headerText={previewPlanRates.length > 0 && previewPlanRates[0].rateYear
                        ? `${previewPlanRates[0].rateYear}年度`
                        : ''}
                      placement="right"
                      isLoading={isLoadingPreview}
                    />
                  </Flex>
                </Box>
              </VStack>
            </RadioGroup>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleRateTypeConfirm} isLoading={isLoadingPreview}>
              {projectId ? t('features.InvestmentScreening.BasicInfo.action.get') : t('features.InvestmentScreening.BasicInfo.action.select')}
            </Button>
            <Button variant="ghost" onClick={onRateTypeModalClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* メール送信確認モーダル */}
      <Modal isOpen={isMailConfirmOpen} onClose={onMailConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('features.InvestmentScreening.BasicInfo.modal.mailConfirmTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>{t('features.InvestmentScreening.BasicInfo.modal.mailConfirmDescription')}</Text>
            <Box bg="gray.50" p={3} borderRadius="md">
              <Text fontWeight="bold" mb={2}>
                {mailConfirmTarget?.divisionName}
              </Text>
              <VStack align="start" spacing={1}>
                {mailConfirmTarget?.employeeNames.map((name, idx) => (
                  <Text key={idx} fontSize="sm">・{name}</Text>
                ))}
              </VStack>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              isLoading={sendingMailDivisionId === mailConfirmTarget?.divisionId}
              onClick={async () => {
                if (!mailConfirmTarget) return;

                const { divisionId, employeeIds } = mailConfirmTarget;

                // 送信中フラグを設定
                setSendingMailDivisionId(divisionId);

                try {
                  // APIを呼び出し
                  const result = await WebAPI.request({
                    query: `
                      mutation SendMailByDivision($input: SendMailByDivisionInput!) {
                        sendMailByDivision(input: $input) {
                          status
                          sentAt
                          message
                          failedAddresses
                          sentCount
                        }
                      }
                    `,
                    variables: {
                      input: {
                        projectId: projectBasicInfo.projectId,
                        divisionId: divisionId,
                        employeeIds: employeeIds,
                        sentByEmployeeId: loginUserEmployeeId,
                        ...(reviewDate && { reviewDate }),
                      }
                    }
                  });

                  const response = result?.data?.sendMailByDivision;
                  console.log("response", response)
                  if (response?.status === "success") {
                    // 送信状態を更新
                    setMailSentStatus(prev => [
                      ...prev.filter(s => s.divisionId !== divisionId),
                      {
                        divisionId,
                        sentAt: response.sentAt,
                        sentByEmployeeId: loginUserEmployeeId,
                        sentByEmployeeName: loginUserEmployeeName,
                      }
                    ]);

                    toast({
                      title: t('features.InvestmentScreening.BasicInfo.modal.mailSentTitle'),
                      description: t('features.InvestmentScreening.BasicInfo.message.mailSentToConfirmors'),
                      status: 'success',
                      duration: 5000,
                      isClosable: true,
                    });

                    // モーダルを閉じる
                    onMailConfirmClose();
                  } else {
                    const sentPart = response?.sentCount > 0 ? `${t('features.InvestmentScreening.BasicInfo.modal.sendComplete')}: ${response.sentCount} / ` : '';
                    const failedDesc = response?.failedAddresses?.length
                      ? `${sentPart}${t('features.InvestmentScreening.BasicInfo.modal.sendFailed')}: ${response.failedAddresses.join(', ')}`
                      : (response?.message ? t(response.message) : t('features.InvestmentScreening.BasicInfo.modal.mailSentFailed'));
                    toast({
                      title: t('features.InvestmentScreening.BasicInfo.modal.mailSentFailedTitle'),
                      description: failedDesc,
                      status: 'error',
                      duration: 5000,
                      isClosable: true,
                    });
                  }
                } catch (error) {
                  console.error("メール送信エラー:", error);
                  toast({
                    title: TITLE,
                    description: t('features.InvestmentScreening.BasicInfo.message.mailSendError'),
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                  });
                } finally {
                  // 送信中フラグを解除
                  setSendingMailDivisionId(null);
                }
              }}
            >
              {t('features.InvestmentScreening.BasicInfo.action.send')}
            </Button>
            <Button variant="ghost" onClick={onMailConfirmClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 入力担当者設定 */}
      <AssignTabModal
        isOpen={isOpenAssignTabModal}
        onClose={onCloseAssignTabModal}
        onCofirm={cofirmAssignTabModal}
        purpose={projectBasicInfo.purpose}
        initSelectedEmployeeIds={selectedConfirmorIds}
        initConfirmEmployees={dispConfirmor ?? savedConfirmor}
        disabledEmployeeIds={disabledEmployeeIds}
        isReviewMode={!!reviewDate}
      />

      {/* クロスチェック担当者設定 */}
      <AssignByCategoryModal
        isOpen={isOpenCrossCheckAssignModal}
        onClose={onCloseCrossCheckAssignModal}
        title={t('features.InvestmentScreening.BasicInfo.modal.crossCheckAssignSetting')}
        categories={
          ["manufacturing", "engineering", "it"]
            .filter(dept => (projectBasicInfo.crossCheckDepartments ?? "").split(",").includes(dept))
            .map(dept => getCrossCheckCategories()[dept]).filter((c): c is CategoryConfig => c !== undefined)
        }
        onConfirm={(assignees) => {
          setCrossCheckAssignees(assignees);
          changeProjectBasicInfo("crossCheckAssignees", JSON.stringify(assignees));
          setCrossCheckReconfirmed(true);
        }}
        initAssignees={crossCheckAssignees}
      />

      {/* 資材担当役員担当者設定 */}
      <AssignByCategoryModal
        isOpen={isOpenMaterialOfficerAssignModal}
        onClose={onCloseMaterialOfficerAssignModal}
        title={t('features.InvestmentScreening.BasicInfo.modal.materialOfficerSetting')}
        categories={getMaterialOfficerCategories()}
        onConfirm={(assignees) => {
          setMaterialOfficerAssignees(assignees);
          changeProjectBasicInfo("materialOfficerAssignees", JSON.stringify(assignees));
          setMaterialOfficerReconfirmed(true);
        }}
        initAssignees={materialOfficerAssignees}
      />

      {/* 承認者設定 */}
      <AssignByCategoryModal
        isOpen={isOpenApproverAssignModal}
        onClose={onCloseApproverAssignModal}
        title={t('features.InvestmentScreening.BasicInfo.modal.approverSetting')}
        categories={getApproverCategories()}
        onConfirm={(assignees) => {
          setApproverAssignees(assignees);
          changeProjectBasicInfo("approverAssignees", JSON.stringify(assignees));
          setApproverReconfirmed(true);
        }}
        initAssignees={approverAssignees}
      />

      {/* 画面閲覧制限 */}
      <ViewRestrictionModal
        isOpen={isOpenViewRestrictionModal}
        onClose={onCloseViewRestrictionModal}
        value={projectBasicInfo.screenViewRestriction}
        purpose={projectBasicInfo.purpose ?? ""}
        isReadOnly={hasfetchException || !pagePermissions?.eabledRegistrant}
        onConfirm={(json) => {
          changeProjectBasicInfo("screenViewRestriction", json);
          onCloseViewRestrictionModal();
        }}
      />

      {/* SpreadSheet参照用モーダル */}
      <Modal isOpen={isSpreadSheetModalOpen} onClose={onSpreadSheetModalClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh" height="90vh" overflow="hidden">
          <ModalHeader>
            {t(spreadSheetManualNameKey)}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            {spreadSheetManualUrl ? (
              <iframe
                src={spreadSheetManualUrl}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title={t(spreadSheetManualNameKey)}
                onLoad={() => WebAPI.setLoading(false)}
              />
            ) : (
              <Flex justify="center" align="center" h="100%">
                {!WebAPI.loading && (
                  <Text color="gray.500" fontSize="lg">
                    {t('features.InvestmentScreening.BasicInfo.message.spreadSheetManualError', { manualName: t(spreadSheetManualNameKey) })}
                  </Text>
                )}
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="gray" onClick={onSpreadSheetModalClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
});

export default BasicInfo;
